/**
 * Frontend API client — calls the AIOS CP gateway REST API.
 * In dev mode, Vite proxies /api/* to the configured gateway target.
 */

import {
  parsePlanMetadata as parseSharedPlanMetadata,
  type ParsedPlanMetadata,
  type PlanBillingMode,
  type PlanChangePolicyMetadata,
  type PlanCommercialMetadata,
  type PlanCtaKind,
  type PlanCtaMetadata,
  type PlanVisibilityMetadata,
} from "@aioscp/types";

const BASE = "";
const DEV_GATEWAY_URL = import.meta.env.VITE_AIOSCP_DEV_GATEWAY_URL ?? "http://127.0.0.1:8080";
const SESSION_USER_KEY = "aioscp_user";
const SUPPORT_SESSION_KEY = "aioscp_support_session";
const SUPPORT_SESSION_EVENT = "aioscp-support-session-changed";
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;
let inMemoryToken: string | null = null;

export class ApiRequestError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly correlationId?: string;

  constructor(message: string, options: { status: number; body: unknown; correlationId?: string }) {
    super(message);
    this.name = "ApiRequestError";
    this.status = options.status;
    this.body = options.body;
    this.correlationId = options.correlationId;
  }
}

function legacyTokenStorageEnabled(): boolean {
  return import.meta.env.VITE_AIOSCP_ENABLE_LEGACY_TOKEN_STORAGE === "true";
}

function normalizeRequestError(error: unknown): Error {
  const message = error instanceof Error ? error.message : String(error);
  if (error instanceof DOMException && error.name === "AbortError") {
    return new Error("The AIOS CP gateway request timed out. Please try again.");
  }
  const isNetworkFailure = error instanceof TypeError || /Failed to fetch|fetch failed|NetworkError/i.test(message);

  if (!isNetworkFailure) {
    return error instanceof Error ? error : new Error(message);
  }

  if (import.meta.env.DEV) {
    return new Error(
      `Cannot reach the AIOS CP gateway at ${DEV_GATEWAY_URL}. Start it with "corepack pnpm run dev:backend" or run the full stack with "corepack pnpm run dev:full".`,
    );
  }

  return new Error("Cannot reach the AIOS CP gateway. Check that the API is running and reachable.");
}

function requestTimeoutMs(): number {
  const configured = Number(import.meta.env.VITE_AIOSCP_REQUEST_TIMEOUT_MS);
  return Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_REQUEST_TIMEOUT_MS;
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), requestTimeoutMs());
  try {
    return await fetch(input, {
      ...init,
      signal: init.signal ?? controller.signal,
    });
  } finally {
    globalThis.clearTimeout(timeout);
  }
}

async function readResponseBody(res: Response): Promise<unknown> {
  if (res.status === 204) {
    return {};
  }

  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return res.json();
  }

  return res.text();
}

function getResponseErrorMessage(body: unknown, status: number): string {
  if (typeof body === "object" && body !== null && "error" in body) {
    const error = (body as { error?: unknown }).error;
    if (typeof error === "string" && error.length > 0) {
      return error;
    }
  }

  if (typeof body === "string") {
    const trimmedBody = body.trim();
    if (trimmedBody.length > 0 && !trimmedBody.startsWith("<")) {
      return trimmedBody;
    }
  }

  return `HTTP ${status}`;
}

function getResponseCorrelationId(res: Response): string | undefined {
  return res.headers.get("x-correlation-id")
    ?? res.headers.get("x-request-id")
    ?? res.headers.get("traceparent")
    ?? undefined;
}

function buildApiRequestError(res: Response, body: unknown): ApiRequestError {
  return new ApiRequestError(getResponseErrorMessage(body, res.status), {
    status: res.status,
    body,
    correlationId: getResponseCorrelationId(res),
  });
}

function parseFileName(contentDisposition: string | null, fallback: string): string {
  if (!contentDisposition) return fallback;
  const match = contentDisposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] ?? fallback;
}

function buildQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) {
      continue;
    }
    search.set(key, String(value));
  }

  const query = search.toString();
  return query.length > 0 ? `?${query}` : "";
}

/** Get stored auth token */
export function getToken(): string | null {
  if (inMemoryToken) return inMemoryToken;
  if (!legacyTokenStorageEnabled()) return null;
  try { return localStorage.getItem("aioscp_token"); } catch { return null; }
}

export interface AuthenticatedUser {
  userId: string;
  email: string;
  name: string;
  tenantId: string;
  role: string;
  createdAt: string;
}

function readStorageItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorageItem(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* SSR safe */
  }
}

function parseSessionUser(value: unknown): AuthenticatedUser | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<AuthenticatedUser>;
  if (
    typeof candidate.userId !== "string" ||
    typeof candidate.email !== "string" ||
    typeof candidate.name !== "string" ||
    typeof candidate.tenantId !== "string" ||
    typeof candidate.role !== "string" ||
    typeof candidate.createdAt !== "string"
  ) {
    return null;
  }

  return {
    userId: candidate.userId,
    email: candidate.email,
    name: candidate.name,
    tenantId: candidate.tenantId,
    role: candidate.role,
    createdAt: candidate.createdAt,
  };
}

export function getSessionUser(): AuthenticatedUser | null {
  try {
    const raw = readStorageItem(SESSION_USER_KEY);
    if (!raw) return null;
    const user = parseSessionUser(JSON.parse(raw));
    if (!user) {
      writeStorageItem(SESSION_USER_KEY, null);
      return null;
    }
    return user;
  } catch {
    writeStorageItem(SESSION_USER_KEY, null);
    return null;
  }
}

export function setSessionUser(user: AuthenticatedUser | null): void {
  const sanitized = user ? parseSessionUser(user) : null;
  writeStorageItem(SESSION_USER_KEY, sanitized ? JSON.stringify(sanitized) : null);
}

function dispatchSupportSessionChanged(): void {
  try {
    window.dispatchEvent(new Event(SUPPORT_SESSION_EVENT));
  } catch {
    /* SSR safe */
  }
}

export interface SupportSessionCacheEntry {
  readonly sessionId: string;
  readonly tenantId: string;
  readonly status: "active";
  readonly expiresAt: string;
}

function parseSupportSessionCache(value: unknown): SupportSessionCacheEntry | null {
  if (!value || typeof value !== "object") return null;
  const candidate = value as Partial<PlatformSupportSessionEntry>;
  const expiresAtMs = typeof candidate.expiresAt === "string" ? Date.parse(candidate.expiresAt) : Number.NaN;
  if (
    typeof candidate.sessionId !== "string" ||
    typeof candidate.tenantId !== "string" ||
    candidate.status !== "active" ||
    typeof candidate.expiresAt !== "string" ||
    !Number.isFinite(expiresAtMs) ||
    expiresAtMs <= Date.now()
  ) {
    return null;
  }

  return {
    sessionId: candidate.sessionId,
    tenantId: candidate.tenantId,
    status: "active",
    expiresAt: candidate.expiresAt,
  };
}

export function getSupportSession(): SupportSessionCacheEntry | null {
  try {
    const raw = readStorageItem(SUPPORT_SESSION_KEY);
    if (!raw) return null;
    const parsed = parseSupportSessionCache(JSON.parse(raw));
    if (!parsed) {
      writeStorageItem(SUPPORT_SESSION_KEY, null);
      return null;
    }
    return parsed;
  } catch {
    writeStorageItem(SUPPORT_SESSION_KEY, null);
    return null;
  }
}

export function setSupportSession(session: PlatformSupportSessionEntry | null): void {
  const cacheEntry = session ? parseSupportSessionCache(session) : null;
  writeStorageItem(SUPPORT_SESSION_KEY, cacheEntry ? JSON.stringify(cacheEntry) : null);
  dispatchSupportSessionChanged();
}

export function onSupportSessionChanged(listener: () => void): () => void {
  const handler = () => listener();
  try {
    window.addEventListener(SUPPORT_SESSION_EVENT, handler);
    return () => window.removeEventListener(SUPPORT_SESSION_EVENT, handler);
  } catch {
    return () => {};
  }
}

/** Store the bearer token in memory; persistent storage is legacy opt-in only. */
export function setToken(token: string | null): void {
  inMemoryToken = token;
  try {
    if (token && legacyTokenStorageEnabled()) localStorage.setItem("aioscp_token", token);
    else localStorage.removeItem("aioscp_token");
  } catch { /* SSR safe */ }
}

/**
 * Read the CSRF token from the aioscp_csrf cookie set by the server on login.
 * Returns null when running SSR or before the first login.
 */
function getCsrfToken(): string | null {
  try {
    const match = document.cookie.match(/(?:^|;\s*)aioscp_csrf=([^;]+)/);
    return match ? decodeURIComponent(match[1]!) : null;
  } catch {
    return null;
  }
}

function isStateChangingMethod(method: string): boolean {
  return method !== "GET" && method !== "HEAD" && method !== "OPTIONS";
}

function buildCredentialHeaders(options: {
  readonly method: string;
  readonly path: string;
  readonly includeJsonContentType?: boolean;
}): Record<string, string> {
  const headers: Record<string, string> = {};

  if (options.includeJsonContentType) {
    headers["Content-Type"] = "application/json";
  }

  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const supportSession = getSupportSession();
  if (supportSession && !options.path.startsWith("/api/v1/platform/")) {
    headers["X-AIOSCP-Support-Session"] = supportSession.sessionId;
  }

  if (isStateChangingMethod(options.method)) {
    const csrf = getCsrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  return headers;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers = buildCredentialHeaders({ method, path, includeJsonContentType: true });

  let res: Response;
  try {
    res = await fetchWithTimeout(`${BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      // Send the httpOnly session cookie on same-origin requests
      credentials: "include",
    });
  } catch (error) {
    throw normalizeRequestError(error);
  }

  const data = await readResponseBody(res);
  if (!res.ok) throw buildApiRequestError(res, data);
  return data as T;
}

/** Connect to SSE event stream */
export function connectEvents(onEvent: (type: string, data: unknown) => void): () => void {
  const es = new EventSource(getEventStreamPath(), { withCredentials: true });
  es.addEventListener("health", (e) => { try { onEvent("health", JSON.parse(e.data)); } catch {} });
  es.addEventListener("agents", (e) => { try { onEvent("agents", JSON.parse(e.data)); } catch {} });
  es.addEventListener("agent", (e) => { try { onEvent("agent", JSON.parse(e.data)); } catch {} });
  es.addEventListener("task", (e) => { try { onEvent("task", JSON.parse(e.data)); } catch {} });
  es.addEventListener("audit", (e) => { try { onEvent("audit", JSON.parse(e.data)); } catch {} });
  es.addEventListener("approval", (e) => { try { onEvent("approval", JSON.parse(e.data)); } catch {} });
  es.addEventListener("connected", () => onEvent("connected", {}));
  es.onerror = () => onEvent("error", {});
  return () => es.close();
}

export function getEventStreamPath(): string {
  return `${BASE}/api/v1/events`;
}

export interface HealthData {
  status: string;
  services: Record<string, string>;
  activeAgents: number;
  queuedTasks: number;
  uptimeSeconds: number;
}

export interface AgentSummary {
  id: { id: string; tenantId: string; teamId: string };
  name: string;
  state: string;
  currentTaskId?: string;
  uptimeSeconds: number;
  lastHeartbeat: string;
}

export interface AgentManifestEntry {
  id: {
    id: string;
    spiffeId?: string;
    tenantId: string;
    teamId: string;
    publicKey?: string;
  };
  name: string;
  version: string;
  graphData: string;
  role: string;
  resources: {
    memoryMiB: number;
    cpuMillis: number;
    timeoutSeconds: number;
    maxConcurrentTools?: number;
    networkPolicy: string;
    allowedDomains?: readonly string[];
  };
  metadata: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface TaskHandle {
  taskId: string;
  threadId: string;
  agentId: string;
  status: string;
}

export interface TaskProgressEntry {
  taskId: string;
  currentNodeId: string;
  currentNodeName: string;
  completedNodes: number;
  totalNodes: number;
  progressPercent: number;
  partialOutput?: string;
}

export interface TaskNodeResultEntry {
  nodeId: string;
  nodeName: string;
  status: string;
  output?: unknown;
  startedAt: string;
  completedAt: string;
  cached?: boolean;
  retryCount?: number;
  resilience?: {
    primaryTargetKind: string;
    primaryTargetId: string;
    executedTargetKind: string;
    executedTargetId: string;
    fallbackIndex?: number;
    circuitState?: "closed" | "open" | "half-open";
    attempts: Array<{
      targetKind: string;
      targetId: string;
      status: "success" | "failed" | "skipped";
      fallbackIndex?: number;
      circuitState?: "closed" | "open" | "half-open";
      errorCode?: string;
      errorMessage?: string;
    }>;
  };
  error?: TaskErrorEntry;
  tokenUsage?: {
    inputTokens: number;
    outputTokens: number;
    costMicrocents: number;
  };
}

export interface RetrievalNodeResultItemEntry {
  score: number;
  source: "semantic" | "keyword" | "hybrid";
  chunk: {
    chunkId: string;
    documentId: string;
    collectionId: string;
    sourceId?: string;
    content?: string;
    metadata: Readonly<Record<string, unknown>>;
    createdAt: string;
  };
  rerank?: RetrievalRerankTraceEntry;
}

export interface RetrievalCitationEntry {
  citationId: string;
  score: number;
  source: "semantic" | "keyword" | "hybrid";
  collectionId: string;
  documentId: string;
  chunkId: string;
  sourceId?: string;
  excerpt?: string;
  metadata: Readonly<Record<string, unknown>>;
  createdAt: string;
}

export interface RetrievalNodeOutputEntry {
  kind: "retrieval";
  toolId: "retrieval.search";
  request: {
    query: string;
    collectionId?: string;
    collectionIds?: readonly string[];
    mode: "semantic" | "keyword" | "hybrid";
    topK: number;
    minScore?: number;
    includeContent: boolean;
    filters?: Readonly<Record<string, unknown>>;
    rerank?: RetrievalRerankConfigEntry;
  };
  resultCount: number;
  results: readonly RetrievalNodeResultItemEntry[];
  citations: readonly RetrievalCitationEntry[];
}

export interface TaskExecutionEventEntry {
  eventType: string;
  nodeId: string;
  nodeName: string;
  nodeType: "tool" | "retrieval" | "sub-agent" | "policy" | "task";
  toolId: string;
  timestamp: string;
  attempt: number;
  partialOutput?: string;
  output?: unknown;
  error?: TaskErrorEntry;
  backoffMs?: number;
}

export interface TaskMetricsEntry {
  totalDurationMs: number;
  nodesExecuted: number;
  totalTokens: {
    inputTokens: number;
    outputTokens: number;
    costMicrocents: number;
  };
  retryCount: number;
}

export interface TaskResultEntry {
  taskId: string;
  status: string;
  output?: Record<string, unknown>;
  error?: TaskErrorEntry;
  nodeResults: TaskNodeResultEntry[];
  executionEvents?: TaskExecutionEventEntry[];
  metrics: TaskMetricsEntry;
}

export interface TaskErrorEntry {
  code: string;
  message: string;
  retryable: boolean;
}

export interface TaskGraphNodeEntry {
  id: string;
  name: string;
  type: string;
}

export interface TaskGraphEdgeEntry {
  fromNodeId: string;
  toNodeId: string;
  condition?: string;
}

export interface TaskGraphEntry {
  graphId: string;
  entryNodeId: string;
  nodes: TaskGraphNodeEntry[];
  edges: TaskGraphEdgeEntry[];
}

export interface TaskListEntry {
  taskId: string;
  threadId: string;
  agentId: string;
  status: string;
  submittedAt: string;
  completedAt?: string;
  input: Record<string, unknown>;
  progress?: TaskProgressEntry;
  attemptCount: number;
  maxAttempts: number;
  result?: TaskResultEntry;
}

export interface TaskDetailEntry extends TaskListEntry {
  graph: TaskGraphEntry | null;
}

export type ThreadStatusEntry = "active" | "waiting" | "completed" | "failed" | "cancelled";

export interface ThreadListFilters {
  agentId?: string;
  status?: ThreadStatusEntry;
  cursor?: string;
  limit?: number;
}

export interface ThreadSummaryEntry {
  threadId: string;
  agentId: string;
  status: ThreadStatusEntry;
  title?: string;
  createdAt: string;
  updatedAt: string;
  taskCount: number;
  latestTaskId: string;
  latestTaskStatus: string;
  checkpointCount: number;
  pendingApprovalCount: number;
  sourceThreadId?: string;
  latestOutput?: Record<string, unknown>;
  latestError?: {
    code: string;
    message: string;
    retryable: boolean;
  };
}

export interface ThreadCheckpointEntry {
  checkpointId: string;
  threadId: string;
  taskId: string;
  traceId?: string;
  status: "active" | "waiting" | "completed" | "failed" | "cancelled";
  createdAt: string;
  updatedAt: string;
  nodeId?: string;
  nodeName?: string;
  summary?: string;
  canResume: boolean;
  canReplay: boolean;
  canFork: boolean;
  canCancel: boolean;
  output?: Record<string, unknown>;
  error?: {
    code: string;
    message: string;
    retryable: boolean;
  };
}

export interface ThreadDetailEntry extends ThreadSummaryEntry {
  tasks: TaskListEntry[];
  checkpoints: ThreadCheckpointEntry[];
  memorySummary?: string;
  latestDebuggerSnapshot?: DebuggerSnapshotEntry;
  effectiveBreakpoints?: DebuggerBreakpointEntry[];
  pendingApprovalIds?: string[];
  metadata?: Record<string, unknown>;
}

export interface ThreadListPage {
  threads: ThreadSummaryEntry[];
  totalCount: number;
  nextCursor?: string;
}

export interface ThreadResumeInputEntry {
  checkpointId?: string;
  inputPatch?: Record<string, unknown>;
  message?: string;
}

export interface ThreadFailedNodeResumeInputEntry extends ThreadResumeInputEntry {
  failedNodeId?: string;
}

export interface ThreadReplayInputEntry {
  checkpointId?: string;
  fromNodeId?: string;
  inputPatch?: Record<string, unknown>;
  preserveToolOutputs?: boolean;
}

export interface ThreadForkInputEntry {
  checkpointId?: string;
  title?: string;
  inputPatch?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface ThreadCancelInputEntry {
  checkpointId?: string;
  reason?: string;
}

export interface ThreadCheckpointExportInputEntry {
  checkpointId?: string;
  includeTaskHistory?: boolean;
  includeMemory?: boolean;
  includeDebuggerState?: boolean;
}

export interface ThreadCheckpointExportEntry {
  format: "aioscp.thread.checkpoint.v2";
  exportedAt: string;
  threadId: string;
  agentId: string;
  checkpoint: ThreadCheckpointEntry & { runtimeSnapshot?: Record<string, unknown> };
  sourceTask: {
    taskId: string;
    agentId: string;
    input: Record<string, unknown>;
    timeoutSeconds?: number;
    status: string;
    submittedAt: string;
    completedAt?: string;
  };
  memorySummary?: string;
  sessionMemoryEntries?: unknown[];
  latestDebuggerSnapshot?: DebuggerSnapshotEntry;
  effectiveBreakpoints?: DebuggerBreakpointEntry[];
  taskHistory?: TaskListEntry[];
  metadata?: Record<string, unknown>;
}

export interface ThreadCheckpointExportResultEntry {
  checkpointExport: ThreadCheckpointExportEntry;
}

export interface ThreadCheckpointImportInputEntry {
  checkpointExport: ThreadCheckpointExportEntry;
  threadId?: string;
  inputPatch?: Record<string, unknown>;
  message?: string;
  title?: string;
  metadata?: Record<string, unknown>;
}

export interface ThreadMutationResultEntry {
  thread: ThreadSummaryEntry;
  task: TaskHandle;
}

export interface ThreadCancelResultEntry {
  thread: ThreadSummaryEntry;
  checkpoint: ThreadCheckpointEntry;
  taskId: string;
  wasRunning: boolean;
  cancelledApprovalIds?: string[];
}

export type ApprovalStatusEntry = "pending" | "approved" | "rejected" | "expired" | "cancelled";

export interface ApprovalRequestedByEntry {
  type: "system" | "policy" | "user";
  id?: string;
  displayName?: string;
}

export interface ApprovalActionTargetEntry {
  kind: "tool" | "filesystem" | "process" | "policy" | "connector" | "other";
  action: string;
  resource: string;
}

export interface ApprovalDecisionRecordEntry {
  decision: "approve" | "reject";
  decidedAt: string;
  decidedBy: {
    userId?: string;
    name?: string;
    role?: string;
  };
  comment?: string;
}

export type ApprovalChainModeEntry = "all" | "any" | "quorum";

export interface ApprovalChainStepEntry {
  stepId: string;
  label?: string;
  requiredRole?: string;
}

export interface ApprovalChainDecisionRecordEntry {
  stepId: string;
  stepLabel?: string;
  requiredRole?: string;
  decision: "approve" | "reject";
  decidedAt: string;
  decidedBy: {
    userId?: string;
    name?: string;
    role?: string;
  };
  comment?: string;
}

export interface ApprovalChainStateEntry {
  templateId?: string;
  mode: ApprovalChainModeEntry;
  quorum: number;
  requiredDecisionCount: number;
  approvedCount: number;
  rejectedCount: number;
  remainingCount: number;
  steps: ApprovalChainStepEntry[];
  decisions: ApprovalChainDecisionRecordEntry[];
}

export type ApprovalEditableFieldTypeEntry = "string" | "number" | "boolean" | "json";

export interface ApprovalEditableFieldEntry {
  path: string;
  label: string;
  type: ApprovalEditableFieldTypeEntry;
  required?: boolean;
}

export type ApprovalSlaStatusEntry = "no-sla" | "on-track" | "due-soon" | "overdue" | "resolved";

export interface ApprovalSlaSummaryEntry {
  status: ApprovalSlaStatusEntry;
  dueAt?: string;
  expiresAt?: string;
  minutesUntilDue?: number;
  overdueMinutes?: number;
  escalation: {
    status: "none" | "pending" | "escalated";
    level?: number;
    routedTo?: string;
    nextEscalatesAt?: string;
  };
}

export interface ApprovalSummaryEntry {
  approvalId: string;
  threadId?: string;
  taskId?: string;
  checkpointId?: string;
  agentId?: string;
  status: ApprovalStatusEntry;
  title: string;
  reason: string;
  requestedAt: string;
  requestedBy: ApprovalRequestedByEntry;
  target: ApprovalActionTargetEntry;
  requiredRole?: string;
  expiresAt?: string;
  dueAt?: string;
  input?: Record<string, unknown>;
  policyContext?: {
    matchedRuleId?: string;
    traceId?: string;
  };
  decision?: ApprovalDecisionRecordEntry;
  approvalChain?: ApprovalChainStateEntry;
  editableFields?: ApprovalEditableFieldEntry[];
  appliedEdits?: Record<string, unknown>;
  sla?: ApprovalSlaSummaryEntry;
}

export interface ApprovalListFilters {
  status?: ApprovalStatusEntry;
  threadId?: string;
  agentId?: string;
  cursor?: string;
  limit?: number;
}

export interface ApprovalListPage {
  approvals: ApprovalSummaryEntry[];
  nextCursor?: string;
}

export interface ApprovalRespondInputEntry {
  decision: "approve" | "reject";
  comment?: string;
  metadata?: Record<string, unknown>;
  edits?: Record<string, unknown>;
}

export interface ApprovalRespondResultEntry {
  approval: ApprovalSummaryEntry;
  thread?: ThreadSummaryEntry;
  task?: TaskHandle;
}

export interface ApprovalBulkRespondInputEntry {
  approvalIds: string[];
  decision: "approve" | "reject";
  comment?: string;
  metadata?: Record<string, unknown>;
}

export interface ApprovalBulkRespondErrorEntry {
  code: string;
  message: string;
  retryable?: boolean;
  grpcStatus?: number;
}

export type ApprovalBulkRespondItemEntry = {
  approvalId: string;
  status: "fulfilled";
  approval: ApprovalSummaryEntry;
  thread?: ThreadSummaryEntry;
  task?: TaskHandle;
} | {
  approvalId: string;
  status: "rejected";
  error: ApprovalBulkRespondErrorEntry;
};

export interface ApprovalBulkRespondResultEntry {
  decision: "approve" | "reject";
  total: number;
  succeeded: number;
  failed: number;
  results: ApprovalBulkRespondItemEntry[];
}

export interface ApprovalAnalyticsEntry {
  generatedAt: string;
  total: number;
  byStatus: Record<ApprovalStatusEntry, number>;
  averageResolutionMinutes: number;
  rejectionRate: number;
  sla: {
    overdue: number;
    dueSoon: number;
    escalated: number;
    longestWaitMinutes: number;
  };
  bottleneck?: {
    key: string;
    label: string;
    pendingCount: number;
    overdueCount: number;
  };
  savings: {
    estimatedManualReviewMinutes: number;
    estimatedAutomationSavingsMinutes: number;
    estimatedApprovalSavingsUsd: number;
  };
}

export interface ApprovalAnalyticsSnapshotInputEntry {
  reason?: string;
  exportLabel?: string;
}

export interface ApprovalAnalyticsSnapshotEntry {
  snapshotId: string;
  tenantId: string;
  generatedAt: string;
  createdBy?: string;
  reason?: string;
  exportLabel: string;
  analytics: ApprovalAnalyticsEntry;
}

export type ApprovalAnalyticsExportTypeEntry = "renewal" | "qbr" | "manual";

export interface ApprovalAnalyticsEvidenceExportEntry {
  contract: "approval-command-center.renewal-pack.v1";
  exportedAt: string;
  tenantId: string;
  exportType: ApprovalAnalyticsExportTypeEntry;
  label: string;
  analytics: ApprovalAnalyticsEntry;
  snapshots: readonly ApprovalAnalyticsSnapshotEntry[];
  evidence: {
    dataSources: readonly string[];
    snapshotCount: number;
    generatedAt: string;
  };
}

export interface ApprovalNotificationSettingsEntry {
  enabled: boolean;
  endpointUrl?: string;
  hasSigningSecret: boolean;
  signatureHeader: string;
  updatedAt?: string;
  slack?: ApprovalSlackNotificationSettingsEntry;
  teams?: ApprovalTeamsNotificationSettingsEntry;
  email?: ApprovalEmailNotificationSettingsEntry;
  pagerDuty?: ApprovalPagerDutyNotificationSettingsEntry;
}

export interface ApprovalSlackNotificationSettingsEntry {
  enabled: boolean;
  hasWebhookUrl: boolean;
  channel?: string;
  displayName?: string;
  updatedAt?: string;
}

export interface ApprovalTeamsNotificationSettingsEntry {
  enabled: boolean;
  hasWebhookUrl: boolean;
  channel?: string;
  displayName?: string;
  updatedAt?: string;
}

export type ApprovalEmailNotificationProviderEntry = "log" | "resend" | "sendgrid";

export interface ApprovalEmailNotificationSettingsEntry {
  enabled: boolean;
  recipients: readonly string[];
  provider: ApprovalEmailNotificationProviderEntry;
  fromEmail?: string;
  hasApiKey: boolean;
  updatedAt?: string;
}

export type ApprovalPagerDutySeverityEntry = "info" | "warning" | "error" | "critical";

export interface ApprovalPagerDutyNotificationSettingsEntry {
  enabled: boolean;
  hasRoutingKey: boolean;
  severity: ApprovalPagerDutySeverityEntry;
  source?: string;
  component?: string;
  dedupKeyPrefix?: string;
  updatedAt?: string;
}

export interface ApprovalNotificationUpdateInputEntry {
  enabled?: boolean;
  endpointUrl?: string;
  signingSecret?: string | null;
  slack?: ApprovalSlackNotificationUpdateInputEntry;
  teams?: ApprovalTeamsNotificationUpdateInputEntry;
  email?: ApprovalEmailNotificationUpdateInputEntry;
  pagerDuty?: ApprovalPagerDutyNotificationUpdateInputEntry;
}

export interface ApprovalSlackNotificationUpdateInputEntry {
  enabled?: boolean;
  webhookUrl?: string | null;
  channel?: string | null;
  displayName?: string | null;
}

export interface ApprovalTeamsNotificationUpdateInputEntry {
  enabled?: boolean;
  webhookUrl?: string | null;
  channel?: string | null;
  displayName?: string | null;
}

export interface ApprovalEmailNotificationUpdateInputEntry {
  enabled?: boolean;
  recipients?: readonly string[];
  provider?: ApprovalEmailNotificationProviderEntry;
  fromEmail?: string | null;
  apiKey?: string | null;
}

export interface ApprovalPagerDutyNotificationUpdateInputEntry {
  enabled?: boolean;
  routingKey?: string | null;
  severity?: ApprovalPagerDutySeverityEntry;
  source?: string | null;
  component?: string | null;
  dedupKeyPrefix?: string | null;
}

export interface ApprovalChainTemplateStepEntry {
  stepId: string;
  label?: string;
  requiredRole?: string;
  description?: string;
}

export interface ApprovalChainTemplateEntry {
  tenantId: string;
  templateId: string;
  displayName: string;
  description?: string;
  mode: ApprovalChainModeEntry;
  quorum?: number;
  steps: readonly ApprovalChainTemplateStepEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface ApprovalChainTemplateConfigEntry {
  tenantId: string;
  templates: readonly ApprovalChainTemplateEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface ApprovalChainTemplateUpsertInputEntry {
  templateId?: string;
  displayName: string;
  description?: string | null;
  mode?: ApprovalChainModeEntry;
  quorum?: number;
  steps: readonly ApprovalChainTemplateStepEntry[];
}

export interface ConnectorCatalogActionEntry {
  actionId: string;
  name: string;
  description: string;
  httpMethod: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  inputSchema: Readonly<Record<string, unknown>>;
  outputSchema: Readonly<Record<string, unknown>>;
}

export interface ConnectorCatalogTriggerEventEntry {
  eventType: string;
  name: string;
  description: string;
  schemaId: string;
  schemaVersion: string;
  payloadSchema: Readonly<Record<string, unknown>>;
}

export interface ConnectorSetupGuideStepEntry {
  stepId: string;
  title: string;
  description: string;
}

export interface ConnectorSetupGuideEntry {
  authSummary: string;
  credentialFields: readonly {
    field: string;
    label: string;
    required: boolean;
    source: "bearer" | "basic" | "query" | "oauth2";
  }[];
  oauthScopes: readonly string[];
  setupSteps: readonly ConnectorSetupGuideStepEntry[];
  testActionIds: readonly string[];
  troubleshooting: readonly string[];
}

export interface ConnectorCatalogEntry {
  connectorId: string;
  name: string;
  provider: string;
  description: string;
  category: string;
  authMethod: "oauth2" | "api_key" | "basic" | "bearer" | "none";
  baseUrl: string;
  actions: readonly ConnectorCatalogActionEntry[];
  triggerEvents?: readonly ConnectorCatalogTriggerEventEntry[];
  icon?: string;
  docsUrl?: string;
  /** Dispatch kind. Defaults to "http" when omitted. */
  kind?: "http" | "process" | "mcp" | "byo";
  setupGuide?: ConnectorSetupGuideEntry;
}

export type ConnectorDispatchAdapterEntry = "http" | "process" | "mcp";
export type ConnectorPluginStatusEntry = "pending" | "approved" | "suspended" | "revoked";

export interface ConnectorPluginListFiltersEntry {
  status?: ConnectorPluginStatusEntry;
  adapter?: ConnectorDispatchAdapterEntry;
}

export interface ConnectorPluginEntry {
  tenantId: string;
  pluginId: string;
  version: string;
  displayName: string;
  description: string;
  adapter: ConnectorDispatchAdapterEntry;
  status: ConnectorPluginStatusEntry;
  manifestHash: string;
  definition: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  revokedAt?: string;
  revokedBy?: string;
  reason?: string;
  notes?: string;
}

export interface ConnectorPluginRegisterInputEntry {
  definition: Readonly<Record<string, unknown>>;
  version?: string;
  notes?: string;
}

export interface ConnectorPluginTransitionInputEntry {
  reason?: string;
}

export interface ConnectorPluginOpenApiImportInputEntry {
  document: Readonly<Record<string, unknown>>;
  connectorId?: string;
  name?: string;
  version?: string;
  category?: string;
  authMethod?: "oauth2" | "api_key" | "basic" | "bearer" | "none";
  baseUrl?: string;
  notes?: string;
}

export interface ConnectorPluginImportResultEntry {
  plugin: ConnectorPluginEntry;
  generatedActionCount: number;
  warnings: readonly string[];
  source: { format: "openapi"; title?: string; version?: string };
}

export interface ConnectorPluginCertificationCheckEntry {
  checkId: string;
  title: string;
  status: "passed" | "warning" | "failed";
  severity: "info" | "warning" | "blocker";
  details: string;
  remediation?: string;
}

export interface ConnectorPluginCertificationReportEntry {
  tenantId: string;
  pluginId: string;
  version: string;
  status: "certified" | "failed";
  certifiedAt: string;
  certifiedBy?: string;
  manifestHash: string;
  adapter: ConnectorDispatchAdapterEntry;
  actionCount: number;
  triggerEventCount: number;
  checks: readonly ConnectorPluginCertificationCheckEntry[];
}

export interface ConnectorPluginEvidencePackEntry {
  tenantId: string;
  pluginId: string;
  version: string;
  generatedAt: string;
  manifestHash: string;
  plugin: ConnectorPluginEntry;
  certification: ConnectorPluginCertificationReportEntry;
  liveProviderEvidence: {
    evidenceRoot: string;
    requiredArtifacts: readonly string[];
    recommendedCommands: readonly string[];
    checklist: readonly string[];
  };
}

export interface ConnectorConnectionEntry {
  connectionId: string;
  connectorId: string;
  displayName?: string;
  status: "pending_auth" | "active" | "disabled" | "error";
  createdAt: string;
  updatedAt: string;
  lastSyncAt?: string;
  lastSyncStatus?: "queued" | "running" | "completed" | "failed";
  lastSyncCompletedAt?: string;
  lastSyncRecordsProcessed?: number;
  lastSyncError?: string;
  syncFreshUntil?: string;
  lastActionAt?: string;
  lastActionId?: string;
  lastActionStatus?: "completed" | "failed";
  lastActionStatusCode?: number;
  lastActionLatencyMs?: number;
  lastActionError?: string;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface ConnectorCatalogFilters {
  query?: string;
  category?: string;
}

export interface CreateConnectorConnectionInputEntry {
  connectorId: string;
  displayName?: string;
  credentials?: Readonly<Record<string, string>>;
  redirectUri?: string;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface CreateConnectorConnectionResultEntry {
  connection: ConnectorConnectionEntry;
  authorizationUrl: string | null;
}

export interface ConnectorSyncResultEntry {
  connectionId: string;
  status: "queued" | "running" | "completed" | "failed";
  startedAt: string;
  completedAt?: string;
  recordsProcessed?: number;
  freshnessUntil?: string;
  error?: string;
}

export interface ConnectorActionResultEntry {
  success: boolean;
  data?: unknown;
  error?: string;
  statusCode: number;
  latencyMs: number;
  rateLimitRemaining?: number;
}

export type ConnectorReadinessStatusEntry = "ready" | "warning" | "blocked";

export interface ConnectorCredentialRequirementEntry {
  field: string;
  label: string;
  required: boolean;
  source: "bearer" | "basic" | "query" | "oauth2";
}

export interface ConnectorReadinessCheckEntry {
  checkId: string;
  status: ConnectorReadinessStatusEntry;
  title: string;
  description: string;
  remediation?: string;
  fields?: readonly string[];
}

export interface ConnectorConnectionReadinessEntry {
  connection: ConnectorConnectionEntry;
  connector: {
    connectorId: string;
    name: string;
    authMethod: ConnectorCatalogEntry["authMethod"];
    actionCount: number;
    triggerEventCount: number;
    syncSupported: boolean;
  };
  status: ConnectorReadinessStatusEntry;
  ready: boolean;
  credentialRequirements: readonly ConnectorCredentialRequirementEntry[];
  checks: readonly ConnectorReadinessCheckEntry[];
}

export type ConnectorPolicyActionEntry =
  | "connectors.create"
  | "connectors.authorize"
  | "connectors.sync"
  | "connectors.execute"
  | "connectors.delete";

export type ConnectorPolicyEffectEntry = "allow" | "deny";

export interface ConnectorPolicyRuleEntry {
  tenantId: string;
  ruleId: string;
  policyRuleId: string;
  role: string;
  effect: ConnectorPolicyEffectEntry;
  actions: readonly ConnectorPolicyActionEntry[];
  resources: readonly string[];
  description?: string;
}

export interface ConnectorPolicyConfigEntry {
  tenantId: string;
  rules: readonly ConnectorPolicyRuleEntry[];
}

export interface ConnectorPolicyRuleInputEntry {
  ruleId?: string;
  role?: string;
  effect: ConnectorPolicyEffectEntry;
  actions: readonly ConnectorPolicyActionEntry[];
  resources: readonly string[];
  description?: string;
}

export interface ConnectorPolicyExplanationEntry {
  action: ConnectorPolicyActionEntry;
  resource: string;
  decision: "allow" | "deny" | "requires_approval";
  reason: string;
  traceId: string;
  connectorActionId?: string;
  matchedRuleId?: string;
}

export interface ConnectorPolicyExplanationSummaryEntry {
  role: string;
  checks: readonly ConnectorPolicyExplanationEntry[];
}

export interface ConnectorConnectionReadinessResultEntry {
  readiness: ConnectorConnectionReadinessEntry;
  policy: ConnectorPolicyExplanationSummaryEntry;
}

export type SimulationScenarioTypeEntry =
  | "historical-replay"
  | "synthetic-user"
  | "policy-edge"
  | "sensitive-data"
  | "mocked-connector"
  | "failed-api"
  | "retrieval-quality"
  | "database-query"
  | "hosted-mcp"
  | "runtime-execution"
  | "sub-agent-handoff";
export type SimulationExpectationEntry = "complete" | "approval" | "safe-fail";
export type SimulationRunStatusEntry = "planned" | "running" | "waiting" | "completed" | "failed" | "cancelled";
export type SimulationRunSourceEntry = "synthetic" | "historical" | "preflight";

export interface SimulationConnectorMockEntry {
  connectionId?: string;
  connectorId?: string;
  actionId?: string;
  statusCode: number;
  latencyMs: number;
  success: boolean;
  data?: unknown;
  error?: string;
  rateLimitRemaining?: number;
}

export interface SimulationFailureInjectionEntry {
  target: "connector" | "http" | "tool";
  connectionId?: string;
  connectorId?: string;
  actionId?: string;
  toolId?: string;
  method?: string;
  urlPattern?: string;
  statusCode: number;
  latencyMs: number;
  errorCode: string;
  errorMessage: string;
}

export interface SimulationProfileEntry {
  profileId: string;
  tenantId: string;
  name: string;
  description?: string;
  connectorMocks: readonly SimulationConnectorMockEntry[];
  failureInjections: readonly SimulationFailureInjectionEntry[];
  metadata: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface SimulationProfileInputEntry {
  profileId?: string;
  name: string;
  description?: string;
  connectorMocks?: readonly SimulationConnectorMockEntry[];
  failureInjections?: readonly SimulationFailureInjectionEntry[];
  metadata?: Readonly<Record<string, unknown>>;
}

export interface SimulationScenarioEntry {
  scenarioId: string;
  scenarioType: SimulationScenarioTypeEntry;
  title: string;
  description?: string;
  expectation: SimulationExpectationEntry;
  input: Readonly<Record<string, unknown>>;
  simulationProfileId?: string;
  metadata: Readonly<Record<string, unknown>>;
}

export interface SimulationSuiteEntry {
  suiteId: string;
  tenantId: string;
  name: string;
  description?: string;
  agentId: string;
  scenarios: readonly SimulationScenarioEntry[];
  metadata: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface SimulationSuiteInputEntry {
  suiteId?: string;
  name: string;
  description?: string;
  agentId: string;
  scenarios: readonly SimulationScenarioEntry[];
  metadata?: Readonly<Record<string, unknown>>;
}

export interface SimulationRunEntry {
  runId: string;
  tenantId: string;
  suiteId?: string;
  scenarioId?: string;
  simulationProfileId?: string;
  agentId: string;
  status: SimulationRunStatusEntry;
  source: SimulationRunSourceEntry;
  taskId?: string;
  threadId?: string;
  input: Readonly<Record<string, unknown>>;
  preflight: Readonly<Record<string, unknown>>;
  outcome: Readonly<Record<string, unknown>>;
  metadata: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  createdBy?: string;
}

export interface SimulationRunInputEntry {
  runId?: string;
  suiteId?: string;
  scenarioId?: string;
  simulationProfileId?: string;
  agentId: string;
  status?: SimulationRunStatusEntry;
  source: SimulationRunSourceEntry;
  taskId?: string;
  threadId?: string;
  input?: Readonly<Record<string, unknown>>;
  preflight?: Readonly<Record<string, unknown>>;
  outcome?: Readonly<Record<string, unknown>>;
  metadata?: Readonly<Record<string, unknown>>;
  completedAt?: string;
}

export interface SimulationRunUpdateEntry {
  status?: SimulationRunStatusEntry;
  taskId?: string;
  threadId?: string;
  preflight?: Readonly<Record<string, unknown>>;
  outcome?: Readonly<Record<string, unknown>>;
  metadata?: Readonly<Record<string, unknown>>;
  completedAt?: string;
}

export type AgentTrustDossierStatusEntry = "ready" | "attention" | "blocked";
export type AgentTrustEvidenceStatusEntry = "available" | "missing" | "attention" | "blocked" | "unavailable";
export type AgentTrustEvidenceTypeEntry = "agent" | "graph" | "governance" | "audit" | "evaluation" | "simulation" | "release" | "budget" | "permissions";

export interface AgentTrustEvidenceLinkEntry {
  evidenceType: AgentTrustEvidenceTypeEntry;
  id: string;
  label: string;
  status: AgentTrustEvidenceStatusEntry;
  route?: string;
  sourceRoute?: string;
  timestamp?: string;
  summary?: string;
}

export interface AgentTrustGraphSummaryEntry {
  graphId?: string;
  entryNodeId?: string;
  nodeCount: number;
  edgeCount: number;
  toolCount: number;
  policyCheckCount: number;
  approvalGateCount: number;
  retrievalNodeCount: number;
  toolIds: readonly string[];
  connectorIds: readonly string[];
  retrievalCollectionIds: readonly string[];
  approvalChainTemplateIds: readonly string[];
  policyRuleIds: readonly string[];
  parseError?: string;
}

export interface AgentTrustEvaluationSummaryEntry {
  runCount: number;
  passCount: number;
  reviewCount: number;
  failCount: number;
  latestRun?: EvaluationRunEntry;
  latestVerdict?: EvaluationVerdictEntry;
  latestProductionEvidence?: {
    scheduleId: string;
    datasetId: string;
    experimentId?: string;
    runId: string;
    verdict?: EvaluationVerdictEntry;
    alertCount: number;
    criticalAlertCount: number;
    evidenceUrl?: string;
    generatedAt?: string;
    decisionPack: Readonly<Record<string, unknown>>;
  };
}

export interface AgentTrustSimulationSummaryEntry {
  runCount: number;
  completedCount: number;
  failedCount: number;
  waitingCount: number;
  latestRun?: SimulationRunEntry;
}

export interface AgentTrustAuditSummaryEntry {
  exportEnabled: boolean;
  recentEntryCount: number;
  denyEntryCount: number;
  entries: readonly AuditLogEntry[];
  unavailableReason?: string;
}

export interface AgentTrustBudgetPostureEntry {
  cpuMillis: number;
  memoryMiB: number;
  timeoutSeconds: number;
  maxConcurrentTools: number;
  networkPolicy: string;
  allowedDomains: readonly string[];
}

export type AgentTrustPermissionSourceKindEntry = "connector" | "retrieval-collection" | "retrieval-source" | "approval-chain-template" | "policy-rule";

export interface AgentTrustPermissionSourceEntry {
  kind: AgentTrustPermissionSourceKindEntry;
  id: string;
  label: string;
  status: AgentTrustEvidenceStatusEntry;
  sourceRoute?: string;
  policyResource?: string;
  allowedRoles?: readonly string[];
  summary: string;
}

export interface AgentTrustPermissionSummaryEntry {
  connectorCount: number;
  activeConnectorCount: number;
  retrievalCollectionCount: number;
  retrievalSourceCount: number;
  approvalChainTemplateCount: number;
  policyRuleCount: number;
  policyResourceCount: number;
  metadataRoleCount: number;
  missingReferenceCount: number;
  sources: readonly AgentTrustPermissionSourceEntry[];
}

export interface AgentTrustDossierEntry {
  tenantId: string;
  agentId: string;
  generatedAt: string;
  status: AgentTrustDossierStatusEntry;
  summary: {
    agentName: string;
    state: string;
    owner?: string;
    version?: string;
    lastChangeAt: string;
    evidenceCount: number;
    attentionCount: number;
  };
  agent: AgentSummary;
  manifest?: {
    version: string;
    role: string;
    createdAt: string;
    updatedAt: string;
    metadata: Readonly<Record<string, string>>;
  };
  graph: AgentTrustGraphSummaryEntry;
  governance: GovernancePostureSnapshotEntry;
  evaluations: AgentTrustEvaluationSummaryEntry;
  simulations: AgentTrustSimulationSummaryEntry;
  audit: AgentTrustAuditSummaryEntry;
  budget: AgentTrustBudgetPostureEntry | null;
  release: {
    status: AgentTrustEvidenceStatusEntry;
    summary: string;
  };
  permissions: AgentTrustPermissionSummaryEntry;
  evidenceLinks: readonly AgentTrustEvidenceLinkEntry[];
  missingEvidence: readonly string[];
}

export interface AgentTrustDossierExportEntry {
  format: "json";
  exportedAt: string;
  dossier: AgentTrustDossierEntry;
}

export type AgentReleaseEnvironmentEntry = "dev" | "staging" | "production";
export type AgentReleaseStatusEntry = "active" | "superseded" | "rolled-back" | "blocked";
export type AgentReleaseGateTypeEntry =
  | "evaluation"
  | "scheduled-evaluation"
  | "simulation"
  | "approval"
  | "deployment-window"
  | "lint"
  | "manual";
export type AgentReleaseGateStatusEntry = "pass" | "review" | "fail" | "missing" | "blocked";

export interface AgentReleaseGateEntry {
  gateId: string;
  type: AgentReleaseGateTypeEntry;
  label: string;
  status: AgentReleaseGateStatusEntry;
  blocking: boolean;
  sourceId?: string;
  summary?: string;
  checkedAt: string;
}

export interface AgentReleaseDeploymentWindowEntry {
  startsAt?: string;
  endsAt?: string;
  timezone?: string;
}

export interface AgentReleaseDiffSourceLinkEntry {
  field: string;
  sourceType: "graph" | "prompt" | "tool" | "policy" | "deployment" | "gate" | "release";
  sourceId: string;
  route: string;
}

export interface AgentReleaseDiffArtifactEntry {
  field: string;
  artifactType: "graph" | "prompt" | "tool" | "policy" | "dlp" | "deployment" | "gate";
  before?: unknown;
  after?: unknown;
  summary: string;
  sourceRoute?: string;
}

export interface AgentReleaseDiffEntry {
  fromReleaseId?: string;
  changed: boolean;
  fields: readonly string[];
  graphChanged: boolean;
  promptChanged: boolean;
  toolChanged: boolean;
  policyChanged: boolean;
  deploymentChanged: boolean;
  summary: readonly string[];
  sourceLinks: readonly AgentReleaseDiffSourceLinkEntry[];
  artifacts?: readonly AgentReleaseDiffArtifactEntry[];
}

export interface AgentReleaseGateSummaryEntry {
  total: number;
  passed: number;
  review: number;
  failed: number;
  missing: number;
  blocked: number;
  ready: boolean;
}

export interface AgentReleaseEntry {
  releaseId: string;
  tenantId: string;
  agentId: string;
  agentName?: string;
  version: string;
  environment: AgentReleaseEnvironmentEntry;
  status: AgentReleaseStatusEntry;
  graphData?: string;
  requiredReviewers: readonly string[];
  approvedBy: readonly string[];
  deploymentWindow?: AgentReleaseDeploymentWindowEntry;
  gates: readonly AgentReleaseGateEntry[];
  gateSummary: AgentReleaseGateSummaryEntry;
  diff: AgentReleaseDiffEntry;
  notes?: string;
  sourceReleaseId?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  promotedAt?: string;
  rolledBackAt?: string;
  rolledBackFromReleaseId?: string;
}

export interface AgentReleaseFiltersEntry {
  agentId?: string;
  environment?: AgentReleaseEnvironmentEntry;
  status?: AgentReleaseStatusEntry;
  limit?: number;
}

export interface AgentReleaseCreateInputEntry {
  agentId: string;
  agentName?: string;
  version: string;
  environment?: AgentReleaseEnvironmentEntry;
  graphData?: string;
  requiredReviewers?: readonly string[];
  approvedBy?: readonly string[];
  deploymentWindow?: AgentReleaseDeploymentWindowEntry;
  gates?: readonly Omit<AgentReleaseGateEntry, "checkedAt">[];
  notes?: string;
}

export interface AgentReleasePromoteInputEntry {
  targetEnvironment?: AgentReleaseEnvironmentEntry;
  approvedBy?: readonly string[];
  notes?: string;
}

export interface AgentReleaseRollbackInputEntry {
  reason?: string;
}

export type LocalRunnerOperatingSystemEntry = "windows" | "linux" | "macos" | "container" | "unknown";
export type LocalRunnerStatusEntry = "pending" | "online" | "offline" | "retired";
export type LocalRunnerAssignmentTargetTypeEntry = "agent-release" | "builder-workflow-version" | "deployed-agent";
export type LocalRunnerAssignmentStatusEntry = "pending" | "deploying" | "running" | "draining" | "failed" | "retired";
export type LocalRunnerTaskDispatchStatusEntry = "queued" | "claimed" | "running" | "completed" | "failed" | "cancelled";

export interface LocalRunnerEntry {
  tenantId: string;
  runnerId: string;
  displayName: string;
  status: LocalRunnerStatusEntry;
  operatingSystem: LocalRunnerOperatingSystemEntry;
  architecture: string;
  hostname?: string;
  version?: string;
  labels: readonly string[];
  poolIds: readonly string[];
  capabilities: readonly string[];
  maxConcurrentTasks: number;
  currentTaskCount: number;
  tokenLast4: string;
  certificateSha256?: string;
  registeredBy?: string;
  registeredAt?: string;
  lastSeenAt?: string;
  retiredAt?: string;
  metadata: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
}

export interface LocalRunnerListFiltersEntry {
  status?: LocalRunnerStatusEntry;
  limit?: number;
}

export interface LocalRunnerRegisterInputEntry {
  displayName: string;
  operatingSystem: LocalRunnerOperatingSystemEntry;
  architecture?: string;
  hostname?: string;
  version?: string;
  labels?: readonly string[];
  poolIds?: readonly string[];
  capabilities?: readonly string[];
  maxConcurrentTasks?: number;
  certificateSha256?: string;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface LocalRunnerLifecycleInputEntry {
  reason: string;
}

export interface LocalRunnerTokenRotationInputEntry {
  reason: string;
}

export interface LocalRunnerCertificateRotationInputEntry {
  certificateSha256: string;
  reason: string;
  allowPreviousForSeconds?: number;
}

export interface LocalRunnerAssignmentEntry {
  tenantId: string;
  assignmentId: string;
  runnerId: string;
  targetType: LocalRunnerAssignmentTargetTypeEntry;
  targetId: string;
  targetVersionId?: string;
  displayName: string;
  status: LocalRunnerAssignmentStatusEntry;
  desiredConcurrency: number;
  targetSnapshot: Readonly<Record<string, unknown>>;
  metadata: Readonly<Record<string, unknown>>;
  assignedBy?: string;
  assignedAt: string;
  lastPulledAt?: string;
  drainedAt?: string;
  retiredAt?: string;
  failureReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LocalRunnerAssignmentListFiltersEntry {
  status?: LocalRunnerAssignmentStatusEntry;
  targetType?: LocalRunnerAssignmentTargetTypeEntry;
  limit?: number;
}

export interface LocalRunnerPoolAssignmentResultEntry {
  runnerPoolId: string;
  assignments: readonly LocalRunnerAssignmentEntry[];
  skippedRunners: readonly { runnerId: string; reason: string }[];
}

export interface LocalRunnerAssignmentCreateInputEntry {
  targetType: LocalRunnerAssignmentTargetTypeEntry;
  targetId: string;
  targetVersionId?: string;
  displayName?: string;
  desiredConcurrency?: number;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface LocalRunnerTaskProgressEntry {
  taskId: string;
  currentNodeId: string;
  currentNodeName: string;
  completedNodes: number;
  totalNodes: number;
  progressPercent: number;
  partialOutput?: string;
}

export interface LocalRunnerTaskEntry {
  tenantId: string;
  dispatchId: string;
  runnerId: string;
  assignmentId: string;
  taskId: string;
  status: LocalRunnerTaskDispatchStatusEntry;
  input: Readonly<Record<string, unknown>>;
  progress?: LocalRunnerTaskProgressEntry;
  result?: Readonly<Record<string, unknown>>;
  attemptCount: number;
  maxAttempts: number;
  claimId?: string;
  claimedAt?: string;
  claimExpiresAt?: string;
  completedAt?: string;
  failureReason?: string;
  metadata: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
  targetType: LocalRunnerAssignmentTargetTypeEntry;
  targetId: string;
  targetVersionId?: string;
  targetSnapshot: Readonly<Record<string, unknown>>;
}

export interface LocalRunnerTaskSubmitInputEntry {
  input?: Readonly<Record<string, unknown>>;
  traceId?: string;
  threadId?: string;
  timeoutSeconds?: number;
  maxAttempts?: number;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface AgentReleaseDeployInputEntry {
  targetAgentId?: string;
  force?: boolean;
  reason?: string;
}

export type ReleaseEvidenceChecklistStatusEntry = "open" | "ready" | "blocked" | "approved";
export type ReleaseEvidenceChecklistItemStatusEntry = "pending" | "provided" | "waived" | "blocked";
export type ReleaseEvidenceChecklistItemTypeEntry =
  | "evaluation"
  | "simulation"
  | "approval"
  | "deployment-window"
  | "trust-dossier"
  | "attestation"
  | "manual";

export interface ReleaseEvidenceChecklistItemEntry {
  itemId: string;
  itemType: ReleaseEvidenceChecklistItemTypeEntry;
  label: string;
  status: ReleaseEvidenceChecklistItemStatusEntry;
  required: boolean;
  sourceId?: string;
  evidenceUrl?: string;
  notes?: string;
  updatedAt: string;
  updatedBy?: string;
}

export interface ReleaseEvidenceChecklistAuditEntry {
  auditId: string;
  action: "created" | "reopened" | "item-updated" | "approved";
  actor?: string;
  at: string;
  summary: string;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface ReleaseEvidenceChecklistEntry {
  checklistId: string;
  tenantId: string;
  releaseId: string;
  agentId: string;
  status: ReleaseEvidenceChecklistStatusEntry;
  owner?: string;
  dueAt?: string;
  items: readonly ReleaseEvidenceChecklistItemEntry[];
  audit: readonly ReleaseEvidenceChecklistAuditEntry[];
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  approvedBy?: string;
  approvedAt?: string;
}

export interface ReleaseEvidenceChecklistFiltersEntry {
  agentId?: string;
  status?: ReleaseEvidenceChecklistStatusEntry;
  limit?: number;
}

export interface ReleaseEvidenceChecklistCreateInputEntry {
  owner?: string;
  dueAt?: string;
}

export interface ReleaseEvidenceChecklistItemInputEntry {
  status: ReleaseEvidenceChecklistItemStatusEntry;
  evidenceUrl?: string;
  notes?: string;
}

export interface ReleaseEvidenceChecklistApproveInputEntry {
  notes?: string;
}

export type WorkspaceReviewTargetTypeEntry = "agent-release" | "industry-pack" | "marketplace-pack";
export type WorkspaceReviewStatusEntry = "open" | "changes-requested" | "approved" | "rejected" | "cancelled";

export interface WorkspaceReviewAuditEntry {
  auditId: string;
  action: "created" | "assigned" | "status-changed" | "commented" | "decided";
  actor?: string;
  at: string;
  summary: string;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface WorkspaceReviewEntry {
  reviewId: string;
  tenantId: string;
  targetType: WorkspaceReviewTargetTypeEntry;
  targetId: string;
  targetLabel: string;
  status: WorkspaceReviewStatusEntry;
  assignee?: string;
  requestedBy?: string;
  requestedAt: string;
  updatedAt: string;
  dueAt?: string;
  summary?: string;
  decisionBy?: string;
  decisionAt?: string;
  audit: readonly WorkspaceReviewAuditEntry[];
  metadata: Readonly<Record<string, unknown>>;
}

export interface WorkspaceReviewFiltersEntry {
  targetType?: WorkspaceReviewTargetTypeEntry;
  targetId?: string;
  status?: WorkspaceReviewStatusEntry;
  assignee?: string;
  limit?: number;
}

export interface WorkspaceReviewCreateInputEntry {
  targetType: WorkspaceReviewTargetTypeEntry;
  targetId: string;
  targetLabel?: string;
  assignee?: string;
  dueAt?: string;
  summary?: string;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface WorkspaceReviewUpdateInputEntry {
  assignee?: string;
  dueAt?: string;
  status?: Extract<WorkspaceReviewStatusEntry, "open" | "changes-requested">;
  summary?: string;
}

export interface WorkspaceReviewDecisionInputEntry {
  decision: "approve" | "reject" | "request-changes" | "cancel";
  summary?: string;
}

export interface RolloutSplitEntry {
  releaseId: string;
  weight: number;
  isBaseline?: boolean;
}

export interface RolloutAutoRollbackEntry {
  enabled: boolean;
  errorRateThreshold: number;
  windowSize: number;
  minSamples: number;
}

export interface RolloutPlanEntry {
  agentId: string;
  environment: AgentReleaseEnvironmentEntry;
  splits: readonly RolloutSplitEntry[];
  autoRollback: RolloutAutoRollbackEntry;
  updatedAt: string;
}

export interface RolloutSetInputEntry {
  environment?: AgentReleaseEnvironmentEntry;
  splits: readonly RolloutSplitEntry[];
  autoRollback?: Partial<RolloutAutoRollbackEntry>;
}

export interface SimulationRunFiltersEntry {
  agentId?: string;
  suiteId?: string;
  status?: SimulationRunStatusEntry | readonly SimulationRunStatusEntry[];
  limit?: number;
}

export interface SimulationPolicyCheckInputEntry {
  action: string;
  resource: string;
  context?: Readonly<Record<string, unknown>>;
}

export interface SimulationPreflightInputEntry {
  agentId: string;
  scenarioType?: SimulationScenarioTypeEntry;
  simulationProfileId?: string;
  input?: Readonly<Record<string, unknown>>;
  policyChecks?: readonly SimulationPolicyCheckInputEntry[];
  dlpTexts?: readonly string[];
}

export interface SimulationPreflightResultEntry {
  agentId: string;
  scenarioType?: SimulationScenarioTypeEntry;
  simulationProfileId?: string;
  policy: {
    checks: readonly {
      action: string;
      resource: string;
      allowed: boolean;
      requiresApproval: boolean;
      matchedRuleId?: string;
      reason?: string;
    }[];
    denyCount: number;
    approvalCount: number;
  };
  dlp: {
    mode: TenantDlpMode;
    blocked: boolean;
    findingCount: number;
    categories: readonly string[];
  };
  profile: {
    connectorMockCount: number;
    failureInjectionCount: number;
  };
  estimates: {
    expectedApprovalVolume: number;
    riskScore: number;
  };
}

export interface SimulationFaultResolutionInputEntry {
  simulationProfileId: string;
  target?: "tool" | "http";
  toolId: string;
  input?: Readonly<Record<string, unknown>>;
}

export interface SimulationFaultResolutionEntry {
  target: "tool" | "http";
  toolId: string;
  statusCode: number;
  latencyMs: number;
  errorCode: string;
  errorMessage: string;
  retryable: boolean;
  output?: unknown;
}

export interface IndustryPackDescriptorEntry {
  id: string;
  name: string;
  category: string;
  buyer: string;
  outcome: string;
  requiredConnectors: readonly string[];
  policyChecks: readonly string[];
  approvers: readonly string[];
  dlpCategories: readonly string[];
  evaluationSuite: string;
  releaseGates: readonly string[];
  trustDossierExamples: readonly string[];
  roiAssumptions: IndustryPackRoiAssumptionsEntry;
  dashboards: readonly string[];
  sloTarget: string;
  howToPath: string;
  catalogAnchor: string;
}

export interface IndustryPackRoiAssumptionsEntry {
  manualMinutesPerTask: number;
  loadedHourlyCostCents: number;
  approvalCostCents: number;
  failureCostCents: number;
  successCriteria: string;
}

export interface IndustryPackListEntry extends IndustryPackDescriptorEntry {
  installed: boolean;
  lastInstalledAt?: string;
}

export type PackInstallSectionStatusEntry = "applied" | "pending" | "skipped";

export interface PackInstallReceiptSectionEntry {
  status: PackInstallSectionStatusEntry;
  summary: string;
  items: readonly string[];
}

export interface PackInstallReceiptEntry {
  id: string;
  packId: string;
  packName: string;
  tenantId: string;
  installedAt: string;
  installedBy?: string;
  dlp: PackInstallReceiptSectionEntry;
  approvals: PackInstallReceiptSectionEntry;
  connectors: PackInstallReceiptSectionEntry;
  evaluations: PackInstallReceiptSectionEntry;
  releaseGates: PackInstallReceiptSectionEntry;
  trustDossier: PackInstallReceiptSectionEntry;
  roiAssumptions: PackInstallReceiptSectionEntry;
  dashboards: PackInstallReceiptSectionEntry;
  releaseGateTemplates: readonly string[];
  roiAssumptionOverrides: IndustryPackRoiAssumptionsEntry;
  howToPath: string;
  catalogAnchor: string;
}

export interface PackConnectorBindingInputEntry {
  requirementId?: string;
  connectorId?: string;
  connectionId?: string;
}

export interface PackConnectorPreflightInputEntry {
  grantedScopes?: ReadonlyArray<Record<string, unknown>>;
  connectionBindings?:
    | Readonly<Record<string, string | PackConnectorBindingInputEntry>>
    | ReadonlyArray<PackConnectorBindingInputEntry>;
  packKind?: "industry" | "marketplace";
  version?: string;
}

export interface PackConnectorPreflightCheckEntry {
  checkId: string;
  status: ConnectorReadinessStatusEntry;
  title: string;
  description: string;
  remediation?: string;
}

export interface PackConnectorPreflightBindingEntry {
  requirementId: string;
  label: string;
  requestedConnectorId: string;
  requestedRights: readonly string[];
  matchedConnectorId?: string;
  connection?: ConnectorConnectionEntry;
  availableConnections: readonly ConnectorConnectionEntry[];
  readiness?: ConnectorConnectionReadinessEntry;
  policy?: ConnectorPolicyExplanationSummaryEntry;
  status: ConnectorReadinessStatusEntry;
  ready: boolean;
  checks: readonly PackConnectorPreflightCheckEntry[];
}

export interface PackConnectorPreflightResultEntry {
  packId: string;
  status: ConnectorReadinessStatusEntry;
  ready: boolean;
  checkedAt: string;
  source: "request" | "install" | "manifest" | "descriptor";
  connectorCount: number;
  bindings: readonly PackConnectorPreflightBindingEntry[];
}

export type PackPublisherTrustTierEntry = "aioscp-first-party" | "verified" | "unverified";
export type PackPublisherStatusEntry = "pending" | "verified" | "suspended";
export type TenantPackInstallApprovalThresholdEntry = "none" | "any-egress" | "any-connector-write" | "all";

export interface PackPublisherEntry {
  publisherId: string;
  displayName: string;
  contactEmail: string;
  trustTier: PackPublisherTrustTierEntry;
  status: PackPublisherStatusEntry;
  publicKeyBase64: string;
  createdAt: string;
  verifiedAt?: string;
  verifiedBy?: string;
  notes?: string;
}

export interface RegisterPackPublisherInputEntry {
  publisherId: string;
  displayName: string;
  contactEmail: string;
  publicKeyBase64: string;
  notes?: string;
}

export type MarketplaceReviewStatusEntry = "pending" | "community_review" | "approved" | "changes_requested" | "rejected";
export type MarketplaceCommercialModelEntry = "free" | "paid" | "byo-contract";

export interface MarketplaceCommercialTermsEntry {
  model: MarketplaceCommercialModelEntry;
  currency: string;
  priceCents?: number;
  setupFeeCents?: number;
  usageMetricKey?: string;
  billingPlanId?: string;
  revenueShareBps: number;
  trialDays?: number;
}

export interface MarketplaceReviewStateEntry {
  status: MarketplaceReviewStatusEntry;
  reviewedAt?: string;
  reviewedBy?: string;
  notes?: string;
  evidenceUrls: readonly string[];
}

export interface MarketplaceReviewInputEntry {
  status: MarketplaceReviewStatusEntry;
  notes?: string;
  evidenceUrls?: readonly string[];
}

export interface MarketplacePackEntry {
  packId: string;
  version: string;
  publisherId: string;
  signed?: SignedPackManifestEntry | undefined;
  manifest?: Record<string, unknown> | undefined;
  signature?: Record<string, unknown> | string | undefined;
  status: "active" | "yanked";
  publishedAt: string;
  revoked?: boolean | undefined;
  review?: MarketplaceReviewStateEntry | undefined;
  commercial?: MarketplaceCommercialTermsEntry | undefined;
}

export interface TenantPackInstallPolicyEntry {
  tenantId: string;
  allowedTrustTiers: readonly PackPublisherTrustTierEntry[];
  allowedPublishers?: readonly string[];
  deniedPublishers?: readonly string[];
  requireApprovalAbove: TenantPackInstallApprovalThresholdEntry;
  updatedAt: string;
  updatedBy?: string;
}

export interface TenantPackInstallPolicyInputEntry {
  allowedTrustTiers: readonly PackPublisherTrustTierEntry[];
  allowedPublishers?: readonly string[];
  deniedPublishers?: readonly string[];
  requireApprovalAbove: TenantPackInstallApprovalThresholdEntry;
}

export interface PackRevocationEntryEntry {
  packId: string;
  version: string;
  reason: string;
  revokedAt: string;
  revokedBy?: string;
}

export interface PackScopeAuthorizeDecisionEntry {
  decision: "allow" | "deny";
  reason?: string;
  missing?: readonly Record<string, unknown>[];
}

export interface SignedPackManifestEntry {
  manifest: Record<string, unknown>;
  signature: string;
}

export interface PackManifestLookupEntry {
  signed: SignedPackManifestEntry;
  revoked: boolean;
  revocation: PackRevocationEntryEntry | null;
}

export interface RevocationListEntry {
  schemaVersion: 1;
  issuer: string;
  issuedAt: string;
  entries: readonly PackRevocationEntryEntry[];
}

export interface MarketplaceRegistrySnapshotEntry {
  schemaVersion: 1;
  issuer: string;
  generatedAt: string;
  sourceUrl?: string;
  packs: readonly MarketplacePackEntry[];
  revocations: RevocationListEntry;
}

export type MarketplaceCommercialSettlementStatusEntry = "not_billable" | "ready" | "metadata_only" | "missing_billing_plan" | "external_contract";

export interface MarketplaceCommercialOpsPackEntry {
  packId: string;
  version: string;
  publisherId: string;
  status: "active" | "yanked";
  revoked: boolean;
  reviewStatus: MarketplaceReviewStatusEntry;
  commercial: MarketplaceCommercialTermsEntry;
  settlementStatus: MarketplaceCommercialSettlementStatusEntry;
  requiredActions: readonly string[];
}

export interface MarketplaceCommercialOpsSnapshotEntry {
  generatedAt: string;
  settlementProvider?: string;
  payoutsEnabled: boolean;
  taxReportingEnabled: boolean;
  paidPacks: number;
  contractPacks: number;
  readyPacks: number;
  entries: readonly MarketplaceCommercialOpsPackEntry[];
  requiredActions: readonly string[];
}

export type McpTransportEntry = "stdio" | "http" | "sse";
export type McpServiceStatusEntry = "active" | "disabled" | "error";
export type McpManagedServiceStatusEntry = "active" | "disabled";
export type McpInvocationHistoryStatusEntry = "queued" | "running" | "waiting_approval" | "completed" | "failed" | "rejected" | "denied" | "cancelled";
export type TenantMcpPolicyExecutionModeEntry = "inherit" | "allow" | "require_approval" | "deny";
export type TenantMcpPolicyRiskClassEntry = "read" | "mutate" | "export" | "stdio" | "unknown";
export type TenantMcpSecretBindingPrivilegeClassEntry = "standard" | "privileged" | "sensitive";

export interface McpSecretBindingEntry {
  name: string;
  secretKey: string;
}

export interface McpInvocationPolicyEntry {
  decision: "allow" | "require_approval" | "deny";
  riskClass: TenantMcpPolicyRiskClassEntry;
  reason: string;
  traceId: string;
  matchedRuleId?: string;
}

export interface TenantMcpServicePolicyEntry {
  serviceId: string;
  executionMode: TenantMcpPolicyExecutionModeEntry;
  defaultRiskClass?: TenantMcpPolicyRiskClassEntry;
  notes?: string;
}

export interface TenantMcpOutboundApiRequestConstraintsEntry {
  allowedHosts: readonly string[];
  allowedMethods?: readonly string[];
  allowedUrlPrefixes?: readonly string[];
  allowedHeaders?: readonly string[];
  maxRequestBodyBytes?: number;
  maxResponseBodyBytes?: number;
}

export interface TenantMcpOutboundDatabaseQueryConstraintsEntry {
  allowedSchemas?: readonly string[];
  allowedTables?: readonly string[];
  allowedQueryPrefixes?: readonly string[];
  allowedColumns?: readonly string[];
  allowedParameterKeys?: readonly string[];
  maxRows?: number;
  maxExecutionMs?: number;
}

export interface TenantMcpOutboundToolConstraintsEntry {
  apiRequest?: TenantMcpOutboundApiRequestConstraintsEntry;
  databaseQuery?: TenantMcpOutboundDatabaseQueryConstraintsEntry;
}

export interface TenantMcpToolPolicyEntry {
  serviceId: string;
  toolName: string;
  executionMode: TenantMcpPolicyExecutionModeEntry;
  riskClass?: TenantMcpPolicyRiskClassEntry;
  outboundConstraints?: TenantMcpOutboundToolConstraintsEntry;
  notes?: string;
}

export interface TenantMcpSecretBindingPolicyEntry {
  serviceId: string;
  bindingName: string;
  privilegeClass: TenantMcpSecretBindingPrivilegeClassEntry;
  executionMode: TenantMcpPolicyExecutionModeEntry;
  riskClass?: TenantMcpPolicyRiskClassEntry;
  notes?: string;
}

export interface TenantMcpPolicyConfigEntry {
  tenantId: string;
  servicePolicies: readonly TenantMcpServicePolicyEntry[];
  toolPolicies: readonly TenantMcpToolPolicyEntry[];
  bindingPolicies: readonly TenantMcpSecretBindingPolicyEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface McpServiceSummaryEntry {
  serviceId: string;
  displayName: string;
  transport: McpTransportEntry;
  status: McpServiceStatusEntry;
  endpoint?: string;
  command?: string;
  args?: readonly string[];
  secretBindings?: readonly McpSecretBindingEntry[];
  toolCount?: number;
  createdAt: string;
  updatedAt: string;
  lastConnectedAt?: string;
  lastError?: string;
}

export interface ConnectMcpServiceInputEntry {
  serviceId?: string;
  displayName: string;
  transport: McpTransportEntry;
  endpoint?: string;
  command?: string;
  args?: readonly string[];
  secretBindings?: readonly McpSecretBindingEntry[];
  metadata?: Readonly<Record<string, unknown>>;
}

export interface McpToolInvocationInputEntry {
  arguments?: Readonly<Record<string, unknown>>;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface McpToolInvocationResultEntry {
  serviceId: string;
  toolName: string;
  output: unknown;
  policy: McpInvocationPolicyEntry;
}

export interface McpToolInvocationApprovalRequiredEntry {
  status: "waiting_approval";
  approval: ApprovalSummaryEntry;
  policy: McpInvocationPolicyEntry;
  thread?: ThreadSummaryEntry;
  task?: {
    threadId: string;
    taskId: string;
    agentId: string;
    status: "queued" | "running" | "waiting";
  };
  service: {
    serviceId: string;
    displayName: string;
  };
  toolName: string;
}

// --- Session memory ---------------------------------------------------------

export type SessionMemoryScopeEntry = "thread" | "agent" | "tenant";

export interface SessionMemoryEntryEntry {
  tenantId: string;
  scope: SessionMemoryScopeEntry;
  scopeId: string;
  key: string;
  value: unknown;
  metadata?: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
}

export interface SessionMemorySnapshotEntry {
  tenantId: string;
  threadId: string;
  entries: readonly SessionMemoryEntryEntry[];
  hydratedAt: string;
}

export interface SessionMemoryListFilters {
  scope?: SessionMemoryScopeEntry;
  scopeId?: string;
  limit?: number;
}

export interface SessionMemoryUpsertInputEntry {
  scope: SessionMemoryScopeEntry;
  scopeId: string;
  key: string;
  value: unknown;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface SessionMemoryThreadUpsertInputEntry {
  key: string;
  value: unknown;
  metadata?: Readonly<Record<string, unknown>>;
}

// --- Debugger ---------------------------------------------------------------

export type DebuggerSnapshotKindEntry = "manual" | "breakpoint" | "checkpoint" | "error";

export interface DebuggerSnapshotEntry {
  snapshotId: string;
  tenantId: string;
  threadId?: string;
  taskId?: string;
  agentId?: string;
  nodeId?: string;
  kind: DebuggerSnapshotKindEntry;
  label?: string;
  state: unknown;
  capturedAt: string;
  capturedBy?: { userId: string; name?: string; role?: string };
}

export interface DebuggerBreakpointEntry {
  breakpointId: string;
  tenantId: string;
  agentId: string;
  threadId?: string;
  nodeId: string;
  condition?: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: { userId: string; name?: string; role?: string };
}

export interface DebuggerSnapshotListFilters {
  threadId?: string;
  taskId?: string;
  agentId?: string;
  kinds?: readonly DebuggerSnapshotKindEntry[];
  limit?: number;
}

export interface DebuggerCaptureSnapshotInputEntry {
  threadId?: string;
  taskId?: string;
  agentId?: string;
  nodeId?: string;
  kind: DebuggerSnapshotKindEntry;
  label?: string;
  state: unknown;
}

export interface DebuggerBreakpointListFilters {
  agentId?: string;
  threadId?: string;
  enabled?: boolean;
  limit?: number;
}

export interface DebuggerBreakpointUpsertInputEntry {
  agentId: string;
  threadId?: string;
  nodeId: string;
  condition?: string;
  enabled: boolean;
}

// --- Observability ----------------------------------------------------------

export type ObservabilityExecutionEventTypeEntry =
  | "task"
  | "node"
  | "tool"
  | "model"
  | "retrieval"
  | "approval"
  | "policy"
  | "agent";

export type ObservabilityExecutionEventStatusEntry =
  | "started"
  | "progress"
  | "completed"
  | "failed"
  | "cancelled"
  | "waiting";

export interface ObservabilityTraceListFilters {
  query?: string;
  spanId?: string;
  taskId?: string;
  agentId?: string;
  userId?: string;
  sessionId?: string;
  operationName?: string;
  attributeKey?: string;
  attributeValue?: string;
  status?: "ok" | "error";
  since?: number;
  until?: number;
  minDurationMs?: number;
  limit?: number;
}

export type ObservabilitySavedTraceFilterFieldsEntry = Omit<ObservabilityTraceListFilters, never>;

export interface ObservabilitySavedTraceFilterEntry {
  filterId: string;
  name: string;
  filters: ObservabilitySavedTraceFilterFieldsEntry;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface ObservabilitySavedTraceFilterInputEntry {
  filterId?: string;
  name: string;
  filters: ObservabilitySavedTraceFilterFieldsEntry;
}

export interface ObservabilityTraceGovernancePolicyEntry {
  retentionDays: number;
  samplingRate: number;
  redactPayloads: boolean;
  incidentHandoffProvider: string;
  incidentHandoffUrlTemplate?: string;
  updatedAt: string;
  updatedBy?: string;
}

export interface ObservabilityTraceGovernancePolicyInputEntry {
  retentionDays?: number;
  samplingRate?: number;
  redactPayloads?: boolean;
  incidentHandoffProvider?: string;
  incidentHandoffUrlTemplate?: string;
}

export interface ObservabilityTraceDiffSpanChangeEntry {
  operationName: string;
  leftCount: number;
  rightCount: number;
  durationDeltaMs: number;
  costDeltaMicrocents: number;
  statusChanged: boolean;
}

export interface ObservabilityTraceDiffEntry {
  leftTraceId: string;
  rightTraceId: string;
  summary: {
    durationDeltaMs: number | null;
    spanCountDelta: number;
    errorCountDelta: number;
    tokenDelta: number;
    costDeltaMicrocents: number;
    addedOperations: readonly string[];
    removedOperations: readonly string[];
    changedOperations: readonly ObservabilityTraceDiffSpanChangeEntry[];
  };
  left: ObservabilityTraceEnvelopeEntry["summary"];
  right: ObservabilityTraceEnvelopeEntry["summary"];
}

export interface ObservabilityIncidentHandoffInputEntry {
  spanId?: string;
  severity?: "low" | "medium" | "high" | "critical";
  provider?: string;
  title?: string;
  externalUrl?: string;
  assignee?: string;
  tags?: readonly string[];
  notes?: string;
}

export interface ObservabilityIncidentHandoffEntry {
  incidentId: string;
  traceId: string;
  spanId?: string;
  provider: string;
  severity: "low" | "medium" | "high" | "critical";
  title: string;
  incidentUrl?: string;
  traceRoute: string;
  summary: string;
  redactedPreview: Readonly<Record<string, unknown>>;
  createdAt: string;
  createdBy?: string;
  assignee?: string;
  tags: readonly string[];
}

export interface ObservabilityExecutionEventFilters {
  type?: ObservabilityExecutionEventTypeEntry;
  status?: ObservabilityExecutionEventStatusEntry;
  after?: number;
  limit?: number;
}

export interface ObservabilityExecutionEventEntry {
  eventId: string;
  taskId: string;
  traceId?: string;
  spanId?: string;
  type: ObservabilityExecutionEventTypeEntry;
  status: ObservabilityExecutionEventStatusEntry;
  timestampMs: number;
  nodeId?: string;
  nodeName?: string;
  message?: string;
  payload?: Readonly<Record<string, unknown>>;
}

export interface ObservabilityTraceAnnotationEntry {
  annotationId: string;
  kind: "eval_failure";
  createdAt: string;
  createdAtMs: number;
  message: string;
  runId: string;
  datasetId?: string;
  caseId: string;
  candidateId: string;
  verdict?: "fail" | "review";
  score?: number;
  issues: readonly string[];
  createdBy?: string;
}

export interface ObservabilityTraceAnnotationInputEntry {
  runId: string;
  datasetId?: string;
  caseId: string;
  candidateId: string;
  verdict?: "fail" | "review";
  score?: number;
  message: string;
  issues?: readonly string[];
}

export interface ObservabilityTraceAnnotationResultEntry {
  annotation: ObservabilityTraceAnnotationEntry;
  span: ObservabilityTraceSpanEntry;
}

export interface ObservabilityTraceSpanEntry {
  traceId: string;
  spanId: string;
  parentSpanId?: string;
  taskId?: string;
  operationName: string;
  serviceName: string;
  agentId?: string;
  tenantId: string;
  startTimeMs: number;
  endTimeMs: number | null;
  durationMs: number | null;
  status: "ok" | "error" | "in_progress";
  attributes: Readonly<Record<string, string | number | boolean>>;
  events: readonly ObservabilityExecutionEventEntry[];
  annotations?: readonly ObservabilityTraceAnnotationEntry[];
  tokenUsage?: {
    inputTokens: number;
    outputTokens: number;
    costMicrocents: number;
  };
}

export interface ObservabilityTraceEnvelopeEntry {
  traceId: string;
  taskId?: string;
  spans: readonly ObservabilityTraceSpanEntry[];
  summary: {
    status: "ok" | "error" | "in_progress";
    startedAtMs: number;
    endedAtMs: number | null;
    durationMs: number | null;
    spanCount: number;
    errorCount: number;
  };
}

// --- Outbound MCP -----------------------------------------------------------

export interface McpOutboundToolDescriptorEntry {
  name: string;
  description: string;
  inputSchema: Readonly<Record<string, unknown>>;
}

export interface McpOutboundResourceDescriptorEntry {
  uri: string;
  name: string;
  description: string;
  mimeType: string;
  tools: readonly string[];
}

export interface McpOutboundPromptArgumentDescriptorEntry {
  name: string;
  description: string;
  required: boolean;
}

export interface McpOutboundPromptDescriptorEntry {
  name: string;
  description: string;
  arguments: readonly McpOutboundPromptArgumentDescriptorEntry[];
  tools: readonly string[];
}

export interface McpOutboundCapabilityCardDescriptorEntry {
  id: string;
  title: string;
  description: string;
  tools: readonly string[];
  resources: readonly string[];
  prompts: readonly string[];
}

export interface McpOutboundCapabilitiesEntry {
  serverName: string;
  version: string;
  tools: readonly McpOutboundToolDescriptorEntry[];
  resources: readonly McpOutboundResourceDescriptorEntry[];
  prompts: readonly McpOutboundPromptDescriptorEntry[];
  capabilityCards: readonly McpOutboundCapabilityCardDescriptorEntry[];
}

export interface McpOutboundInvocationResultEntry {
  toolName: string;
  content: unknown;
  isError?: boolean;
}

export type McpToolInvocationResponseEntry =
  | McpToolInvocationResultEntry
  | McpToolInvocationApprovalRequiredEntry;

export interface McpInvocationHistoryEntry {
  historyId: string;
  service: {
    serviceId: string;
    displayName: string;
    transport: McpTransportEntry;
  };
  toolName: string;
  actorId: string;
  status: McpInvocationHistoryStatusEntry;
  decision: "allow" | "require_approval" | "deny";
  riskClass?: TenantMcpPolicyRiskClassEntry;
  reason: string;
  matchedRuleId?: string;
  traceId?: string;
  requestedAt: string;
  updatedAt: string;
  completedAt?: string;
  threadId?: string;
  taskId?: string;
  approvalId?: string;
  approvalStatus?: "pending" | "approved" | "rejected" | "expired" | "cancelled";
  output?: unknown;
  error?: TaskErrorEntry;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface McpInvocationHistoryFilters {
  serviceId?: string;
  status?: McpInvocationHistoryStatusEntry;
  limit?: number;
}

export interface McpInvocationHistorySummaryBucketEntry<Key extends string = string> {
  key: Key;
  count: number;
}

export interface McpInvocationHistoryServiceSummaryEntry {
  service: {
    serviceId: string;
    displayName: string;
    transport: McpTransportEntry;
  };
  count: number;
  completedCount: number;
  waitingApprovalCount: number;
  failedCount: number;
  deniedCount: number;
  lastInvokedAt: string;
  topTools: { toolName: string; count: number }[];
}

export interface McpInvocationHistorySummaryEntry {
  filters: {
    serviceId?: string;
    status?: McpInvocationHistoryStatusEntry;
    limit: number;
  };
  totalCount: number;
  completedCount: number;
  waitingApprovalCount: number;
  failedCount: number;
  deniedCount: number;
  approvalRequiredCount: number;
  byStatus: McpInvocationHistorySummaryBucketEntry<McpInvocationHistoryStatusEntry>[];
  byDecision: McpInvocationHistorySummaryBucketEntry<"allow" | "require_approval" | "deny">[];
  byRiskClass: McpInvocationHistorySummaryBucketEntry<TenantMcpPolicyRiskClassEntry | "unspecified">[];
  services: McpInvocationHistoryServiceSummaryEntry[];
  latestInvocationAt?: string;
}

export type SandboxedExecutionLanguageEntry = "javascript" | "typescript";
export type SandboxedExecutionRuntimeEntry = "deno" | "node_vm" | "python_subprocess";
export type SandboxedExecutionIsolationEntry = "strict" | "best_effort" | "none";
export type SandboxedExecutionStatusEntry = "completed" | "failed" | "timed_out";

export interface SandboxedExecutionInputEntry {
  language: SandboxedExecutionLanguageEntry;
  code: string;
  timeoutMs?: number;
}

export interface SandboxedExecutionResultEntry {
  executionId: string;
  language: SandboxedExecutionLanguageEntry;
  runtime: SandboxedExecutionRuntimeEntry;
  isolation: SandboxedExecutionIsolationEntry;
  status: SandboxedExecutionStatusEntry;
  stdout: string;
  stderr: string;
  exitCode: number;
  startedAt: string;
  completedAt: string;
  durationMs: number;
}

export interface PrivilegedCommandExecutionInputEntry {
  command: string;
  workingDirectory?: string;
  timeoutMs?: number;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface PrivilegedPowerShellExecutionInputEntry {
  script: string;
  workingDirectory?: string;
  timeoutMs?: number;
  metadata?: Readonly<Record<string, unknown>>;
}

export type PrivilegedFilesystemOperationEntry = "read" | "write" | "delete" | "list" | "create_directory";
export type PrivilegedFilesystemEncodingEntry = "utf8" | "base64";

export interface PrivilegedFilesystemExecutionInputEntry {
  operation: PrivilegedFilesystemOperationEntry;
  path: string;
  encoding?: PrivilegedFilesystemEncodingEntry;
  content?: string;
  overwrite?: boolean;
  recursive?: boolean;
  createParents?: boolean;
  timeoutMs?: number;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface PrivilegedExecutionQueuedEntry {
  status: "queued";
  thread: {
    threadId: string;
  };
  task: {
    threadId: string;
    taskId: string;
    agentId: string;
    status: "queued" | "running";
  };
}

export interface PrivilegedExecutionApprovalRequiredEntry {
  status: "waiting_approval";
  approval: ApprovalSummaryEntry;
  thread?: ThreadSummaryEntry;
  task?: {
    threadId: string;
    taskId: string;
    agentId: string;
    status: "queued" | "running" | "waiting";
  };
}

export type PrivilegedExecutionSubmissionEntry =
  | PrivilegedExecutionQueuedEntry
  | PrivilegedExecutionApprovalRequiredEntry;

export interface TaskStreamEventEntry {
  taskId: string;
  tenantId: string;
  agentId: string;
  status: string;
  attemptCount: number;
  maxAttempts: number;
  submittedAt: string;
  completedAt?: string;
  progress?: TaskProgressEntry;
  error?: {
    code: string;
    message: string;
    retryable: boolean;
  };
}

export interface UploadedFileEntry {
  fileId: string;
  originalName: string;
  sizeBytes: number;
  uploadedAt?: string;
}

export interface BuilderTemplateEntry {
  id: string;
  name: string;
  description: string;
  category: string;
  nodes: unknown[];
  edges: unknown[];
}

export interface BuilderWorkflowEntry {
  id: string;
  tenantId: string;
  name: string;
  nodes: unknown[];
  edges: unknown[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface BuilderGovernanceApplicationResultEntry {
  recommendationId: string;
  type: BuilderAiGovernanceRecommendationTypeEntry;
  status: "applied" | "recorded" | "skipped";
  targetType: "evaluation-dataset" | "dlp-policy" | "release-gate-evidence" | "deployment-settings" | "approval-config";
  targetId?: string;
  summary: string;
}

export interface BuilderWorkflowEvaluationHandoffEntry {
  source: "builder-governance";
  workflowId: string;
  draftId?: string;
  evaluationDatasetIds: readonly string[];
  latestEvaluationRunIds: readonly string[];
  updatedAt: string;
  promotionGateStatus?: EvaluationVerdictEntry;
  promotionGateSummary?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  reviewReason?: string;
}

export interface BuilderWorkflowGovernanceEvidenceEntry {
  draftId?: string;
  title?: string;
  recommendations: BuilderAiGovernanceRecommendationEntry[];
  evalCases?: BuilderAiEvalCaseSuggestionEntry[];
  deploymentSettings?: BuilderAiDeploymentSettingsSuggestionEntry;
  applyAcceptedRecommendations?: boolean;
  appliedAt?: string;
  applicationResults?: BuilderGovernanceApplicationResultEntry[];
  evaluationHandoff?: BuilderWorkflowEvaluationHandoffEntry;
}

export interface BuilderWorkflowVersionEntry {
  id: string;
  workflowId: string;
  tenantId: string;
  name: string;
  nodes: unknown[];
  edges: unknown[];
  note?: string;
  governanceEvidence?: BuilderWorkflowGovernanceEvidenceEntry;
  createdAt: string;
  createdBy?: string;
}

export interface BuilderCommentEntry {
  id: string;
  tenantId: string;
  workflowId: string;
  nodeId?: string;
  text: string;
  author: string;
  createdAt: string;
}

export interface SaveBuilderWorkflowInputEntry {
  id?: string;
  name: string;
  nodes: unknown[];
  edges: unknown[];
}

export interface AppendBuilderVersionInputEntry {
  name?: string;
  note?: string;
  nodes: unknown[];
  edges: unknown[];
  governanceEvidence?: BuilderWorkflowGovernanceEvidenceEntry;
}

export interface AddBuilderCommentInputEntry {
  text: string;
  nodeId?: string;
}

export type BuilderAssetKind = "prompt" | "schema" | "config" | "credential-ref";

export interface BuilderAssetEntry {
  id: string;
  tenantId: string;
  kind: BuilderAssetKind;
  name: string;
  description?: string;
  content: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface BuilderSubflowIoField {
  name: string;
  type: string;
}

export interface BuilderSubflowEntry {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  nodes: unknown[];
  edges: unknown[];
  inputs: BuilderSubflowIoField[];
  outputs: BuilderSubflowIoField[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface UpsertBuilderAssetInputEntry {
  id?: string;
  kind: BuilderAssetKind;
  name: string;
  description?: string;
  content?: Record<string, unknown>;
}

export interface UpsertBuilderSubflowInputEntry {
  id?: string;
  name: string;
  description?: string;
  nodes: unknown[];
  edges: unknown[];
  inputs?: BuilderSubflowIoField[];
  outputs?: BuilderSubflowIoField[];
}

export interface BuilderAiSuggestionEntry {
  kind: string;
  reason: string;
}

export interface BuilderAiFixEntry {
  target: string;
  issue: string;
  recommendation: string;
}

export interface BuilderAiDraftNodeEntry {
  nodeId: string;
  kind: string;
  label: string;
  config: Record<string, unknown>;
}

export interface BuilderAiDraftEdgeEntry {
  edgeId: string;
  sourceNodeId: string;
  targetNodeId: string;
  label: string;
}

export type BuilderAiGovernanceRecommendationTypeEntry = "approval" | "dlp-policy" | "evaluation" | "deployment" | "release-gate";
export type BuilderAiGovernanceReviewStateEntry = "proposed" | "accepted" | "rejected";

export interface BuilderAiGovernanceRecommendationEntry {
  recommendationId: string;
  type: BuilderAiGovernanceRecommendationTypeEntry;
  title: string;
  rationale: string;
  appliesToNodeIds: string[];
  suggestedConfig: Record<string, unknown>;
  reviewState: BuilderAiGovernanceReviewStateEntry;
}

export interface BuilderAiEvalCaseSuggestionEntry {
  caseId: string;
  name: string;
  query: string;
  expectedAnswer: string;
  tags: string[];
}

export interface BuilderAiDeploymentSettingsSuggestionEntry {
  environment: "dev" | "staging" | "production";
  modelProvider: string;
  timeoutSeconds: number;
  maxConcurrentTools: number;
  requireReleaseGate: boolean;
  reviewerRole: string;
}

export interface BuilderAiGovernedGraphDraftEntry {
  draftId: string;
  title: string;
  summary: string;
  nodes: BuilderAiDraftNodeEntry[];
  edges: BuilderAiDraftEdgeEntry[];
  governanceRecommendations: BuilderAiGovernanceRecommendationEntry[];
  evalCases: BuilderAiEvalCaseSuggestionEntry[];
  deploymentSettings: BuilderAiDeploymentSettingsSuggestionEntry;
}

export interface BuilderAiGenerateEntry {
  kinds: string[];
  rationale: string;
  draft?: BuilderAiGovernedGraphDraftEntry;
}

export interface PlanFeatureEntry {
  featureKey: string;
  kind: "flag" | "quota";
  enabled: boolean;
  limit?: number | null;
  unit?: string;
  description?: string;
}

export type PlanBillingModeEntry = PlanBillingMode;
export type PlanCtaKindEntry = PlanCtaKind;
export type PlanCtaMetadataEntry = PlanCtaMetadata;
export type PlanChangePolicyMetadataEntry = PlanChangePolicyMetadata;
export type PlanVisibilityMetadataEntry = PlanVisibilityMetadata;
export type PlanCommercialMetadataEntry = PlanCommercialMetadata;
export type ParsedPlanMetadataEntry = ParsedPlanMetadata;

export interface PlanEntry {
  planId: string;
  tier: string;
  displayName: string;
  description: string;
  features: PlanFeatureEntry[];
  metadata?: Readonly<Record<string, unknown>>;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export type PlatformPlanPreviewActionEntry = "update" | "archive" | "publish";

export interface PlatformPlanPreviewInputEntry {
  action: PlatformPlanPreviewActionEntry;
  tier?: string;
  displayName?: string;
  description?: string;
  features?: PlanFeatureEntry[];
  metadata?: Readonly<Record<string, unknown>>;
  active?: boolean;
  sampleLimit?: number;
}

export interface PlatformPlanImpactSnapshotEntry {
  planId: string;
  tier: string;
  displayName: string;
  active: boolean;
  featureCount: number;
  public: boolean;
  billingMode: PlanBillingModeEntry | string;
  priceMonthlyCents: number;
  visibility: Readonly<Record<string, boolean>>;
}

export interface PlatformPlanImpactPreviewEntry {
  dryRun: true;
  action: PlatformPlanPreviewActionEntry;
  planId: string;
  before: PlatformPlanImpactSnapshotEntry;
  after: PlatformPlanImpactSnapshotEntry;
  impact: {
    activeSubscriptionCount: number;
    affectedTenantIds: readonly string[];
    hasMoreAffectedTenants: boolean;
    featureChanges: {
      added: readonly string[];
      removed: readonly string[];
      changed: readonly string[];
    };
    metadataChangedKeys: readonly string[];
    visibilityChanged: boolean;
    billingChanged: boolean;
    risks: readonly string[];
  };
}

export interface PlatformPlanCreateInputEntry {
  planId: string;
  tier: string;
  displayName: string;
  description: string;
  features?: PlanFeatureEntry[];
  metadata?: Readonly<Record<string, unknown>>;
  active?: boolean;
  reason: string;
}

export interface PlatformPlanUpdateInputEntry {
  tier?: string;
  displayName?: string;
  description?: string;
  features?: PlanFeatureEntry[];
  metadata?: Readonly<Record<string, unknown>>;
  active?: boolean;
  reason: string;
}

export interface PlatformPlanArchiveInputEntry {
  reason: string;
}

export interface PlatformPlanCloneInputEntry {
  targetPlanId: string;
  tier?: string;
  displayName?: string;
  description?: string;
  metadata?: Readonly<Record<string, unknown>>;
  active?: boolean;
  reason: string;
}

export function parsePlanMetadata(plan: Pick<PlanEntry, "planId" | "tier" | "metadata" | "active">): ParsedPlanMetadataEntry {
  return parseSharedPlanMetadata(plan);
}

export interface SubscriptionEntry {
  subscriptionId: string;
  tenantId: string;
  planId: string;
  status: string;
  billingProvider?: string;
  externalSubscriptionId?: string;
  trialEndsAt?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  metadata?: Readonly<Record<string, unknown>>;
  createdAt?: string;
  updatedAt?: string;
}

export interface MembershipEntry {
  membershipId: string;
  tenantId: string;
  userId: string;
  email?: string;
  role: string;
  status: string;
}

export interface InviteEntry {
  inviteId: string;
  tenantId: string;
  membershipId: string;
  email: string;
  role: string;
  status: "pending" | "accepted" | "revoked" | "expired";
  invitedByEmail?: string;
  expiresAt: string;
  acceptedAt?: string;
  inviteUrl?: string;
  delivery?: {
    provider: "log" | "resend" | "sendgrid";
    delivered: boolean;
    messageId?: string;
  };
}

export interface UsageSummaryEntry {
  tenantId: string;
  metricKey: string;
  totalQuantity: number;
  unit: string;
  windowStart?: string;
  windowEnd?: string;
}

export interface WorkspaceQuotaEntry {
  featureKey: string;
  label: string;
  currentValue: number;
  limit: number | null;
  remainingValue: number | null;
  unit?: string;
  source: "plan" | "override" | "default";
  warningLevel: "ok" | "warning" | "exceeded";
  utilizationPercent?: number;
}

export interface WorkspaceUsageOverviewEntry {
  tenantId: string;
  windowStart: string;
  summaries: UsageSummaryEntry[];
  quotas: WorkspaceQuotaEntry[];
}

export type RetrievalAccessControlStatusEntry = "enforced" | "warning" | "untracked";
export type RetrievalAccessControlSourceEntry = "collection-metadata" | "document-metadata" | "connector-policy" | "policy-engine" | "none";
export type RetrievalCitationQualityStatusEntry = "ready" | "warning" | "untracked";
export type RetrievalFreshnessAlertStatusEntry = "ok" | "warning" | "breached";

export interface RetrievalStaleAlertSummaryEntry {
  status: RetrievalFreshnessAlertStatusEntry;
  thresholdHours: number;
  overdueByHours?: number;
  staleSourceCount: number;
  message: string;
}

export interface RetrievalAccessControlSummaryEntry {
  status: RetrievalAccessControlStatusEntry;
  source: RetrievalAccessControlSourceEntry;
  warningCount: number;
  lastCheckedAt?: string;
  resourcePattern?: string;
  allowedRoles?: readonly string[];
  lastWarning?: string;
}

export interface RetrievalLineageSummaryEntry {
  sourceCount: number;
  sourceIds: readonly string[];
  connectionIds: readonly string[];
  documentTypes: Readonly<Record<string, number>>;
  staleSourceCount: number;
  lastUpdatedAt?: string;
  lastSourceId?: string;
}

export interface RetrievalCitationQualitySummaryEntry {
  status: RetrievalCitationQualityStatusEntry;
  sourceCoverage: number;
  metadataCoverage: number;
  averageQualityScore: number;
  sourceIdCount: number;
  missingSourceIdCount: number;
  citationReadyCount: number;
  warningCount: number;
  lastWarning?: string;
}

export type RetrievalRerankStrategyEntry = "none" | "balanced" | "freshness" | "citation-quality" | "governance";
export type RetrievalAppliedRerankStrategyEntry = Exclude<RetrievalRerankStrategyEntry, "none">;
export type RetrievalOptimizationVerdictEntry = "healthy" | "needs-tuning" | "failing";

export interface RetrievalRerankConfigEntry {
  strategy?: RetrievalRerankStrategyEntry | undefined;
  topN?: number | undefined;
  provider?: string | undefined;
  model?: string | undefined;
}

export interface RetrievalRerankTraceEntry {
  strategy: RetrievalAppliedRerankStrategyEntry;
  provider: string;
  model?: string | undefined;
  originalRank: number;
  rerankScore: number;
  signals: {
    relevance: number;
    freshness: number;
    citationQuality: number;
    metadataCoverage: number;
    access: number;
  };
  summary: string;
}

export interface RetrievalOptimizationRecommendationEntry {
  id: string;
  severity: "info" | "warning" | "critical";
  message: string;
  suggestedSearch?: {
    mode?: "semantic" | "keyword" | "hybrid";
    topK?: number;
    minScore?: number;
    rerank?: RetrievalRerankConfigEntry;
  };
}

export interface RetrievalOptimizationSummaryEntry {
  verdict: RetrievalOptimizationVerdictEntry;
  recommendations: readonly RetrievalOptimizationRecommendationEntry[];
  suggestedSearch?: {
    mode?: "semantic" | "keyword" | "hybrid";
    topK?: number;
    minScore?: number;
    rerank?: RetrievalRerankConfigEntry;
  };
}

export interface RetrievalSearchRationaleEntry {
  matchedTerms: readonly string[];
  sourceRationale: string;
  citation: {
    status: "ready" | "warning";
    qualityScore: number;
    sourceId?: string;
    title?: string;
    url?: string;
    documentType?: string;
    updatedAt?: string;
  };
  access: {
    status: RetrievalAccessControlStatusEntry;
    source: RetrievalAccessControlSourceEntry;
    resourcePattern?: string;
  };
  lineage: {
    connectionIds: readonly string[];
    sourceUpdatedAt?: string;
  };
}

export interface RetrievalCollectionEntry {
  collectionId: string;
  name: string;
  description?: string;
  dimensions: number;
  documentCount: number;
  createdAt: string;
  ops: {
    freshness: {
      source: "ingestion";
      status: "fresh" | "stale" | "untracked";
      freshUntil?: string;
      lastMaterializedAt?: string;
      staleAlert?: RetrievalStaleAlertSummaryEntry;
    };
    ingestion: {
      status: "idle" | "healthy" | "warning";
      lastIngestAt?: string;
      warningCount: number;
      lastWarning?: string;
      chunkCount: number;
      sourceCount: number;
    };
    observability: {
      searchCount: number;
      lastSearchAt?: string;
      lastSearchLatencyMs?: number;
      lastSearchResultCount?: number;
      lastSearchEstimatedCostMicrocents?: number;
      totalSearchEstimatedCostMicrocents: number;
    };
    accessControl: RetrievalAccessControlSummaryEntry;
    lineage: RetrievalLineageSummaryEntry;
    citationQuality: RetrievalCitationQualitySummaryEntry;
  };
  metadata?: Readonly<Record<string, unknown>>;
}

function buildDefaultRetrievalCollectionOps(): RetrievalCollectionEntry["ops"] {
  return {
    freshness: {
      source: "ingestion",
      status: "untracked",
    },
    ingestion: {
      status: "idle",
      warningCount: 0,
      chunkCount: 0,
      sourceCount: 0,
    },
    observability: {
      searchCount: 0,
      totalSearchEstimatedCostMicrocents: 0,
    },
    accessControl: {
      status: "untracked",
      source: "none",
      warningCount: 0,
    },
    lineage: {
      sourceCount: 0,
      sourceIds: [],
      connectionIds: [],
      documentTypes: {},
      staleSourceCount: 0,
    },
    citationQuality: {
      status: "untracked",
      sourceCoverage: 0,
      metadataCoverage: 0,
      averageQualityScore: 0,
      sourceIdCount: 0,
      missingSourceIdCount: 0,
      citationReadyCount: 0,
      warningCount: 0,
    },
  };
}

export function normalizeRetrievalCollection(collection: RetrievalCollectionEntry): RetrievalCollectionEntry {
  const defaultOps = buildDefaultRetrievalCollectionOps();

  return {
    ...collection,
    ops: {
      freshness: {
        ...defaultOps.freshness,
        ...collection.ops?.freshness,
      },
      ingestion: {
        ...defaultOps.ingestion,
        ...collection.ops?.ingestion,
      },
      observability: {
        ...defaultOps.observability,
        ...collection.ops?.observability,
      },
      accessControl: {
        ...defaultOps.accessControl,
        ...collection.ops?.accessControl,
      },
      lineage: {
        ...defaultOps.lineage,
        ...collection.ops?.lineage,
      },
      citationQuality: {
        ...defaultOps.citationQuality,
        ...collection.ops?.citationQuality,
      },
    },
  };
}

export type RetrievalObservabilitySeverityEntry = "healthy" | "watch" | "critical";

export interface RetrievalObservabilityHotspotEntry {
  collectionId: string;
  name: string;
  severity: RetrievalObservabilitySeverityEntry;
  searchCount: number;
  documentCount: number;
  sourceCount: number;
  chunkCount: number;
  lastSearchAt?: string;
  lastSearchLatencyMs?: number;
  lastSearchResultCount?: number;
  totalSearchEstimatedCostMicrocents: number;
  freshnessStatus: RetrievalCollectionEntry["ops"]["freshness"]["status"];
  ingestionStatus: RetrievalCollectionEntry["ops"]["ingestion"]["status"];
  accessControlStatus: RetrievalAccessControlStatusEntry;
  citationStatus: RetrievalCitationQualityStatusEntry;
  warnings: readonly string[];
  recommendation: string;
}

export interface RetrievalObservabilityRecommendationEntry {
  id: string;
  severity: "info" | "watch" | "critical";
  message: string;
  collectionId?: string;
  action: string;
}

export interface RetrievalObservabilitySummaryEntry {
  generatedAt: string;
  readinessScore: number;
  totals: {
    collectionCount: number;
    documentCount: number;
    sourceCount: number;
    chunkCount: number;
    searchCount: number;
    totalSearchEstimatedCostMicrocents: number;
    activeProfileCount: number;
    activeProductionProfileCount: number;
    activeChunkingProfileCount: number;
  };
  latency: {
    observedCollectionCount: number;
    averageLastSearchLatencyMs?: number;
    maxLastSearchLatencyMs?: number;
    slowCollectionCount: number;
    lastSearchAt?: string;
  };
  quality: {
    freshness: { fresh: number; stale: number; untracked: number; staleSourceCount: number };
    accessControl: { enforced: number; warning: number; untracked: number };
    citation: {
      ready: number;
      warning: number;
      untracked: number;
      averageQualityScore: number;
      metadataCoverage: number;
      citationReadySourceCoverage: number;
    };
  };
  profileLifecycle: {
    dev: number;
    staging: number;
    production: number;
    active: number;
    archived: number;
    promotedLast30Days: number;
  };
  hotspots: readonly RetrievalObservabilityHotspotEntry[];
  recommendations: readonly RetrievalObservabilityRecommendationEntry[];
}

export type RetrievalGovernanceSimulationStatusEntry = "pass" | "review" | "block";

export type RetrievalEvaluationTargetKindEntry = "chunkId" | "documentId" | "sourceId";

export interface RetrievalEvaluationExpectedTargetEntry {
  kind: RetrievalEvaluationTargetKindEntry;
  id: string;
}

export interface RetrievalEvaluateInputEntry {
  query: string;
  collectionId?: string;
  collectionIds?: readonly string[];
  mode?: "semantic" | "keyword" | "hybrid";
  topK?: number;
  minScore?: number;
  filters?: Readonly<Record<string, unknown>>;
  includeContent?: boolean;
  rerank?: RetrievalRerankConfigEntry;
  expected: readonly RetrievalEvaluationExpectedTargetEntry[];
}

export interface RetrievalEvaluationRankedResultEntry {
  rank: number;
  score: number;
  source: "semantic" | "keyword" | "hybrid";
  collectionId: string;
  chunkId: string;
  documentId: string;
  sourceId?: string;
  matchedExpected: readonly RetrievalEvaluationExpectedTargetEntry[];
  rationale?: RetrievalSearchRationaleEntry;
  rerank?: RetrievalRerankTraceEntry;
}

export interface RetrievalEvaluationMatchEntry extends RetrievalEvaluationExpectedTargetEntry {
  rank: number;
  score: number;
  collectionId: string;
  chunkId: string;
  documentId: string;
  sourceId?: string;
}

export interface RetrievalEvaluationResultEntry {
  query: string;
  mode: "semantic" | "keyword" | "hybrid";
  topK: number;
  evaluatedAt: string;
  resultCount: number;
  expectedCount: number;
  matchedExpectedCount: number;
  matchedResultCount: number;
  recall: number;
  precision: number;
  matched: readonly RetrievalEvaluationMatchEntry[];
  missing: readonly RetrievalEvaluationExpectedTargetEntry[];
  results: readonly RetrievalEvaluationRankedResultEntry[];
  optimization: RetrievalOptimizationSummaryEntry;
}

export type RetrievalProfileStageEntry = "dev" | "staging" | "production";
export type RetrievalProfileStatusEntry = "draft" | "active" | "archived";

export type RetrievalProfileConfigEntry = Omit<RetrievalSearchInput, "query">;

export interface RetrievalProfileEntry {
  profileId: string;
  name: string;
  description?: string;
  stage: RetrievalProfileStageEntry;
  status: RetrievalProfileStatusEntry;
  config: RetrievalProfileConfigEntry;
  metrics: Readonly<Record<string, unknown>>;
  metadata: Readonly<Record<string, unknown>>;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RetrievalProfileInputEntry {
  profileId?: string;
  name: string;
  description?: string;
  stage?: RetrievalProfileStageEntry;
  status?: RetrievalProfileStatusEntry;
  config: RetrievalProfileConfigEntry;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface RetrievalProfilePromotionEvidenceEntry {
  query?: string;
  candidateId?: string;
  winnerCandidateId?: string;
  score?: number;
  recall?: number;
  precision?: number;
  expectedCount?: number;
  resultCount?: number;
  evaluatedAt?: string;
  notes?: string;
}

export interface RetrievalProfilePromotionInputEntry {
  targetStage: RetrievalProfileStageEntry;
  status?: RetrievalProfileStatusEntry;
  reason?: string;
  evidence?: RetrievalProfilePromotionEvidenceEntry;
  archiveExistingProduction?: boolean;
}

export interface RetrievalGovernanceSimulationInputEntry {
  profileId?: string;
  config?: RetrievalProfileConfigEntry;
  callerRoles?: readonly string[];
  targetStage?: RetrievalProfileStageEntry;
  status?: RetrievalProfileStatusEntry;
  evidence?: RetrievalProfilePromotionEvidenceEntry;
}

export interface RetrievalGovernanceSimulationCollectionEntry {
  collectionId: string;
  name: string;
  documentCount: number;
  sourceCount: number;
  chunkCount: number;
  freshnessStatus: RetrievalCollectionEntry["ops"]["freshness"]["status"];
  ingestionStatus: RetrievalCollectionEntry["ops"]["ingestion"]["status"];
  accessControlStatus: RetrievalAccessControlStatusEntry;
  citationStatus: RetrievalCitationQualityStatusEntry;
  readinessScore: number;
  warnings: readonly string[];
}

export interface RetrievalGovernanceSimulationPolicyCheckEntry {
  role: string;
  collectionId: string;
  status: RetrievalGovernanceSimulationStatusEntry;
  evaluatedSourceCount: number;
  visibleSourceCount: number;
  deniedSourceCount: number;
  approvalRequiredCount: number;
  matchedRuleIds: readonly string[];
  reasons: readonly string[];
}

export interface RetrievalGovernanceSimulationGateEntry {
  gateId: string;
  status: RetrievalGovernanceSimulationStatusEntry;
  message: string;
  action: string;
  collectionId?: string;
  role?: string;
}

export interface RetrievalGovernancePromotionGateEntry {
  targetStage: RetrievalProfileStageEntry;
  targetStatus: RetrievalProfileStatusEntry;
  eligible: boolean;
  evidenceRequired: boolean;
  evidenceAccepted: boolean;
  overlappingProductionProfileIds: readonly string[];
  reason: string;
}

export interface RetrievalGovernanceSimulationResultEntry {
  simulatedAt: string;
  verdict: RetrievalGovernanceSimulationStatusEntry;
  profileId?: string;
  config: RetrievalProfileConfigEntry;
  targetStage: RetrievalProfileStageEntry;
  targetStatus: RetrievalProfileStatusEntry;
  collections: readonly RetrievalGovernanceSimulationCollectionEntry[];
  policyChecks: readonly RetrievalGovernanceSimulationPolicyCheckEntry[];
  promotionGate: RetrievalGovernancePromotionGateEntry;
  gates: readonly RetrievalGovernanceSimulationGateEntry[];
  recommendations: readonly RetrievalObservabilityRecommendationEntry[];
}

export interface RetrievalExperimentCandidateInputEntry {
  candidateId?: string;
  profileId?: string;
  label?: string;
  config?: RetrievalProfileConfigEntry;
}

export interface RetrievalExperimentInputEntry {
  query: string;
  expected?: readonly RetrievalEvaluationExpectedTargetEntry[];
  candidates: readonly RetrievalExperimentCandidateInputEntry[];
}

export interface RetrievalExperimentCandidateResultEntry {
  candidateId: string;
  label: string;
  profileId?: string;
  config: RetrievalProfileConfigEntry;
  score: number;
  resultCount: number;
  averageScore: number;
  rerankedResultCount: number;
  evaluation?: RetrievalEvaluationResultEntry;
  results?: readonly RetrievalSearchResultEntry[];
  optimization?: RetrievalOptimizationSummaryEntry;
}

export interface RetrievalExperimentResultEntry {
  query: string;
  ranAt: string;
  expectedCount: number;
  winnerCandidateId?: string;
  candidates: readonly RetrievalExperimentCandidateResultEntry[];
  recommendations: readonly RetrievalOptimizationRecommendationEntry[];
}

export interface RetrievalCollectionCreateInput {
  name: string;
  description?: string;
  dimensions: number;
  metadata?: Readonly<Record<string, unknown>>;
}

export type RetrievalChunkingStrategyEntry = "semantic" | "fixed" | "markdown" | "json";

export interface RetrievalChunkingInput {
  strategy?: RetrievalChunkingStrategyEntry;
  maxTokens?: number;
  overlapTokens?: number;
  maxCharacters?: number;
  overlapCharacters?: number;
}

export type RetrievalChunkingProfileStatusEntry = "draft" | "active" | "archived";

export interface RetrievalChunkingProfileEntry {
  profileId: string;
  name: string;
  description?: string;
  status: RetrievalChunkingProfileStatusEntry;
  config: RetrievalChunkingInput;
  metrics: Readonly<Record<string, unknown>>;
  metadata: Readonly<Record<string, unknown>>;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RetrievalChunkingProfileInputEntry {
  profileId?: string;
  name: string;
  description?: string;
  status?: RetrievalChunkingProfileStatusEntry;
  config: RetrievalChunkingInput;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface RetrievalIngestDocumentInput {
  sourceId: string;
  filename?: string;
  documentType?: "text" | "pdf" | "docx" | "csv" | "html" | "markdown" | "json";
  content?: string;
  fileId?: string;
  metadata?: Readonly<Record<string, unknown>>;
  chunkingProfileId?: string;
  chunking?: RetrievalChunkingInput;
}

export interface RetrievalIngestInput {
  collectionId: string;
  chunkingProfileId?: string;
  documents: readonly RetrievalIngestDocumentInput[];
}

export interface RetrievalIngestResultEntry {
  collectionId: string;
  documentId: string;
  sourceId: string;
  status: "queued" | "indexed";
  chunksIndexed?: number;
  chunkingProfileId?: string;
  warnings?: readonly string[];
}

export interface RetrievalSearchInput {
  query: string;
  collectionId?: string;
  collectionIds?: readonly string[];
  mode?: "semantic" | "keyword" | "hybrid";
  topK?: number;
  minScore?: number;
  filters?: Readonly<Record<string, unknown>>;
  includeContent?: boolean;
  rerank?: RetrievalRerankConfigEntry;
}

export interface RetrievalSearchResultEntry {
  score: number;
  source: "semantic" | "keyword" | "hybrid";
  chunk: {
    chunkId: string;
    documentId: string;
    collectionId: string;
    sourceId?: string;
    content?: string;
    metadata: Readonly<Record<string, unknown>>;
    createdAt: string;
  };
  rationale: RetrievalSearchRationaleEntry;
  rerank?: RetrievalRerankTraceEntry;
}

export type EvaluationCandidateTypeEntry = "agent-version" | "model" | "prompt" | "retrieval-config";
export type EvaluationVerdictEntry = "pass" | "review" | "fail";
export type EvaluationLifecycleStatusEntry = "draft" | "active" | "archived" | "production-gate";
export type EvaluationExperimentBudgetStatusEntry = "within" | "exceeded";

export interface EvaluationRetrievalConfigEntry {
  collectionId?: string;
  collectionIds?: readonly string[];
  mode?: "semantic" | "keyword" | "hybrid";
  topK?: number;
  minScore?: number;
  filters?: Readonly<Record<string, unknown>>;
  rerank?: RetrievalRerankConfigEntry;
}

export interface EvaluationRubricCriterionEntry {
  criterionId: string;
  label: string;
  weight: number;
  threshold?: number;
}

export interface EvaluationCaseAssertionsEntry {
  mustContain?: readonly string[];
  mustNotContain?: readonly string[];
}

export interface EvaluationTraceReferenceEntry {
  traceId?: string;
  taskId?: string;
  spanId?: string;
  threadId?: string;
  nodeId?: string;
}

export interface EvaluationTokenUsageEntry {
  inputTokens: number;
  outputTokens: number;
  costMicrocents: number;
}

export interface EvaluationRunComparisonEntry {
  baselineRunId?: string;
  bestScore: number;
  baselineBestScore?: number;
  scoreDelta?: number;
  tokenDelta?: number;
  costDeltaMicrocents?: number;
  traceCoverage: number;
  regressionCaseCount: number;
  issueCount: number;
  blockingIssueCount: number;
}

export interface EvaluationPromotionGateEntry {
  status: EvaluationVerdictEntry;
  sourceRunId: string;
  summary: string;
  reasons: readonly string[];
  blockingIssues: readonly string[];
  championCandidateId?: string;
  baselineRunId?: string;
}

export interface EvaluationCaseInputEntry {
  caseId?: string;
  name: string;
  query: string;
  input?: Readonly<Record<string, unknown>>;
  expectedAnswer?: string;
  expectedTargets?: readonly RetrievalEvaluationExpectedTargetEntry[];
  assertions?: EvaluationCaseAssertionsEntry;
  retrieval?: EvaluationRetrievalConfigEntry;
  baselineScore?: number;
  tags?: readonly string[];
  metadata?: Readonly<Record<string, unknown>>;
}

export interface EvaluationCaseEntry extends EvaluationCaseInputEntry {
  caseId: string;
}

export interface EvaluationDatasetEntry {
  datasetId: string;
  tenantId: string;
  name: string;
  description?: string;
  status?: EvaluationLifecycleStatusEntry;
  cases: readonly EvaluationCaseEntry[];
  rubric: readonly EvaluationRubricCriterionEntry[];
  passThreshold: number;
  reviewThreshold: number;
  metadata?: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
}

export interface EvaluationDatasetInputEntry {
  datasetId?: string;
  name: string;
  description?: string;
  status?: EvaluationLifecycleStatusEntry;
  cases: readonly EvaluationCaseInputEntry[];
  rubric?: readonly EvaluationRubricCriterionEntry[];
  passThreshold?: number;
  reviewThreshold?: number;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface EvaluationCandidateEntry {
  candidateId: string;
  label: string;
  type: EvaluationCandidateTypeEntry;
  agentId?: string;
  agentVersion?: string;
  model?: string;
  promptVersion?: string;
  outputs?: Readonly<Record<string, string>>;
  rubricScores?: Readonly<Record<string, Readonly<Record<string, number>>>>;
  retrieval?: EvaluationRetrievalConfigEntry;
  trace?: EvaluationTraceReferenceEntry;
  tokenUsage?: EvaluationTokenUsageEntry;
  caseTraces?: Readonly<Record<string, EvaluationTraceReferenceEntry>>;
  caseTokenUsage?: Readonly<Record<string, EvaluationTokenUsageEntry>>;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface EvaluationCaseResultEntry {
  caseId: string;
  candidateId: string;
  verdict: EvaluationVerdictEntry;
  score: number;
  output?: string;
  assertionScore?: number;
  expectedAnswerScore?: number;
  rubricScore?: number;
  retrievalScore?: number;
  hallucinationScore?: number;
  citationQualityScore?: number;
  modelGradedScore?: number;
  modelGradedSource?: "external" | "local-heuristic";
  modelGradedRationale?: string;
  modelGradedCalibrationProfile?: string;
  retrieval?: RetrievalEvaluationResultEntry;
  regressionDelta?: number;
  trace?: EvaluationTraceReferenceEntry;
  tokenUsage?: EvaluationTokenUsageEntry;
  issues: readonly string[];
}

export interface EvaluationCandidateResultEntry {
  candidateId: string;
  label: string;
  type: EvaluationCandidateTypeEntry;
  verdict: EvaluationVerdictEntry;
  averageScore: number;
  regressionDelta?: number;
  traceCount?: number;
  tokenUsage?: EvaluationTokenUsageEntry;
  caseResults: readonly EvaluationCaseResultEntry[];
}

export interface EvaluationRunEntry {
  runId: string;
  tenantId: string;
  datasetId: string;
  datasetName: string;
  status: "completed";
  verdict: EvaluationVerdictEntry;
  passThreshold: number;
  reviewThreshold: number;
  candidates: readonly EvaluationCandidateEntry[];
  results: readonly EvaluationCandidateResultEntry[];
  summary: {
    candidateCount: number;
    caseCount: number;
    passCount: number;
    reviewCount: number;
    failCount: number;
    bestCandidateId?: string;
    tracedCaseCount?: number;
    totalTokenUsage?: EvaluationTokenUsageEntry;
    comparison?: EvaluationRunComparisonEntry;
    promotionGate?: EvaluationPromotionGateEntry;
  };
  metadata?: Readonly<Record<string, unknown>>;
  createdAt: string;
  completedAt: string;
}

export interface EvaluationRunInputEntry {
  runId?: string;
  experimentId?: string;
  datasetId: string;
  candidates: readonly EvaluationCandidateEntry[];
  metadata?: Readonly<Record<string, unknown>>;
}

export interface EvaluationExperimentBudgetEntry {
  maxRuns?: number;
  maxTokens?: number;
  maxCostMicrocents?: number;
}

export interface EvaluationExperimentPromotionCriteriaEntry {
  championCandidateId?: string;
  minScore?: number;
  maxScoreRegression?: number;
  maxRegressionCases?: number;
  maxCostIncreaseMicrocents?: number;
  minTraceCoverage?: number;
  requirePromotionGatePass?: boolean;
}

export interface EvaluationExperimentSummaryEntry {
  runCount: number;
  latestRunId?: string;
  latestVerdict?: EvaluationVerdictEntry;
  bestRunId?: string;
  bestScore?: number;
  championCandidateId?: string;
  challengerCandidateIds: readonly string[];
  totalTokenUsage: EvaluationTokenUsageEntry;
  budgetStatus: EvaluationExperimentBudgetStatusEntry;
  budgetReasons: readonly string[];
  promotionStatus: EvaluationVerdictEntry;
  promotionReasons: readonly string[];
  recommendation: string;
}

export interface EvaluationExperimentEntry {
  experimentId: string;
  tenantId: string;
  name: string;
  description?: string;
  status: EvaluationLifecycleStatusEntry;
  datasetIds: readonly string[];
  candidateIds: readonly string[];
  runIds: readonly string[];
  budget: EvaluationExperimentBudgetEntry;
  promotionCriteria: EvaluationExperimentPromotionCriteriaEntry;
  summary: EvaluationExperimentSummaryEntry;
  metadata?: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
}

export interface EvaluationExperimentInputEntry {
  experimentId?: string;
  name: string;
  description?: string;
  status?: EvaluationLifecycleStatusEntry;
  datasetIds?: readonly string[];
  candidateIds?: readonly string[];
  runIds?: readonly string[];
  budget?: EvaluationExperimentBudgetEntry;
  promotionCriteria?: EvaluationExperimentPromotionCriteriaEntry;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface EvaluationExperimentFiltersEntry {
  status?: EvaluationLifecycleStatusEntry;
  datasetId?: string;
  limit?: number;
}

export interface EvaluationExperimentReviewNotifyInputEntry {
  webhookUrl?: string;
  message?: string;
}

export interface EvaluationExperimentReviewNotifyResultEntry {
  delivered: boolean;
  statusCode: number;
  webhookHost: string;
}

export interface EvaluationRunFiltersEntry {
  datasetId?: string;
  limit?: number;
}

export type PromptStatusEntry = "draft" | "active" | "archived";
export type PromptMessageRoleEntry = "system" | "developer" | "user" | "assistant";

export interface PromptMessageEntry {
  role: PromptMessageRoleEntry;
  content: string;
  name?: string;
}

export interface PromptEntry {
  tenantId: string;
  promptId: string;
  slug: string;
  name: string;
  description?: string;
  status: PromptStatusEntry;
  tags: readonly string[];
  metadata: Readonly<Record<string, unknown>>;
  currentVersion: number;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PromptVersionEntry {
  tenantId: string;
  promptId: string;
  version: number;
  messages: readonly PromptMessageEntry[];
  variables: readonly string[];
  includes: readonly string[];
  changeSummary?: string;
  metadata: Readonly<Record<string, unknown>>;
  createdBy?: string;
  createdAt: string;
}

export interface PromptLabelEntry {
  tenantId: string;
  promptId: string;
  label: string;
  version: number;
  protected: boolean;
  metadata: Readonly<Record<string, unknown>>;
  createdBy?: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt: string;
}

export interface PromptRenderResultEntry {
  prompt: PromptEntry;
  version: PromptVersionEntry;
  label?: PromptLabelEntry;
  messages: readonly PromptMessageEntry[];
  unresolvedVariables: readonly string[];
  composition: {
    rootPromptId: string;
    rootVersion: number;
    nodes: readonly { promptId: string; slug: string; name: string; version: number; label?: string }[];
    edges: readonly { fromPromptId: string; fromVersion: number; toPromptId: string; toVersion: number; include: string }[];
  };
  etag: string;
  cache: { key?: string; hit: boolean; ttlSeconds: number };
}

export interface PromptDetailEntry {
  prompt: PromptEntry;
  labels: readonly PromptLabelEntry[];
  latestVersion?: PromptVersionEntry;
}

export interface PromptCreateInputEntry {
  slug?: string;
  name: string;
  description?: string;
  status?: PromptStatusEntry;
  tags?: readonly string[];
  messages: readonly PromptMessageEntry[];
  variables?: readonly string[];
  includes?: readonly string[];
  metadata?: Readonly<Record<string, unknown>>;
  versionMetadata?: Readonly<Record<string, unknown>>;
  changeSummary?: string;
}

export interface PromptPlaygroundRunEntry {
  tenantId: string;
  runId: string;
  promptId: string;
  version: number;
  label?: string;
  status: "completed" | "failed";
  variables: Readonly<Record<string, unknown>>;
  modelSettings: Readonly<Record<string, unknown>>;
  renderedMessages: readonly PromptMessageEntry[];
  unresolvedVariables: readonly string[];
  tokenEstimate: { inputTokens: number; estimatedCostMicrocents: number };
  outputPreview: string;
  error?: string;
  createdAt: string;
}

export interface PromptExperimentEntry {
  tenantId: string;
  experimentId: string;
  name: string;
  description?: string;
  promptId: string;
  datasetId?: string;
  evaluationRunId?: string;
  status: "draft" | "running" | "completed" | "archived";
  candidates: readonly unknown[];
  results: readonly unknown[];
  summary: Readonly<Record<string, unknown>>;
  winnerCandidateId?: string;
  confidence?: number;
  recommendation?: string;
  metadata: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
}

export interface PromptNotificationSettingsEntry {
  tenantId: string;
  enabled: boolean;
  events: readonly string[];
  webhookUrl?: string;
  slackWebhookUrl?: string;
  slackChannel?: string;
  updatedAt: string;
}

export interface PromptNotificationDeliveryEntry {
  tenantId: string;
  deliveryId: string;
  eventType: string;
  targetType: "webhook" | "slack";
  status: "pending" | "delivered" | "failed" | "skipped";
  promptId?: string;
  experimentId?: string;
  responseStatus?: number;
  error?: string;
  attemptCount: number;
  maxAttempts: number;
  nextAttemptAt?: string;
  lastAttemptAt?: string;
  createdAt: string;
  deliveredAt?: string;
}

export interface RoiWorkflowAssumptionsEntry {
  workflowId: string;
  displayName: string;
  estimatedManualMinutesPerTask: number;
  loadedHourlyCostUsd: number;
  failureCostUsd: number;
  approvalCostUsd: number;
  successCriteria: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface RoiWorkflowAssumptionsUpdateEntry {
  displayName?: string;
  estimatedManualMinutesPerTask?: number;
  loadedHourlyCostUsd?: number;
  failureCostUsd?: number;
  approvalCostUsd?: number;
  successCriteria?: string;
}

export interface RoiDataLineageEntry {
  taskMetricSource: "task-executions";
  tokenSpendSource: "task.metrics.totalTokens.costMicrocents";
  approvalSavingsSource: "workflow-assumptions" | "approval-analytics";
  assumptionSource: "default" | "tenant-workflow-override";
  taskIds: readonly string[];
  generatedAt: string;
}

export interface WorkflowRoiEntry {
  workflowId: string;
  displayName: string;
  agentId?: string;
  teamId?: string;
  teamName?: string;
  taskCount: number;
  successfulRuns: number;
  failedRuns: number;
  successRate: number;
  tokenSpendUsd: number;
  costPerSuccessfulRunUsd: number | null;
  estimatedHoursSaved: number;
  estimatedLaborValueUsd: number;
  estimatedApprovalSavingsUsd: number;
  estimatedFailureCostUsd: number;
  estimatedGrossValueUsd: number;
  estimatedNetValueUsd: number;
  roiPercent: number | null;
  assumptions: RoiWorkflowAssumptionsEntry;
  dataLineage: RoiDataLineageEntry;
}

export interface RoiTeamRollupEntry {
  teamId: string;
  displayName: string;
  workflowCount: number;
  totalTasks: number;
  successfulRuns: number;
  failedRuns: number;
  estimatedNetValueUsd: number;
  roiPercent: number | null;
  workflowIds: readonly string[];
}

export interface RoiSummaryEntry {
  tenantId: string;
  generatedAt: string;
  workflowCount: number;
  totalTasks: number;
  successfulRuns: number;
  failedRuns: number;
  successRate: number;
  tokenSpendUsd: number;
  costPerSuccessfulRunUsd: number | null;
  estimatedHoursSaved: number;
  estimatedLaborValueUsd: number;
  estimatedApprovalSavingsUsd: number;
  estimatedFailureCostUsd: number;
  estimatedGrossValueUsd: number;
  estimatedNetValueUsd: number;
  roiPercent: number | null;
  topWorkflows: readonly WorkflowRoiEntry[];
  teamRollups: readonly RoiTeamRollupEntry[];
  dataLineage: {
    taskMetricSource: "task-executions";
    approvalSavingsSource: "workflow-assumptions" | "approval-analytics";
    workflowAssumptionCount: number;
    generatedAt: string;
  };
}

export type RoiEvidenceSnapshotTypeEntry = "scheduled" | "qbr" | "renewal" | "manual";

export interface RoiEvidenceSnapshotSummaryEntry {
  snapshotId: string;
  snapshotType: RoiEvidenceSnapshotTypeEntry;
  generatedAt: string;
  createdBy?: string;
  workflowCount: number;
  teamCount: number;
  totalTasks: number;
  estimatedNetValueUsd: number;
}

export interface RoiEvidenceSnapshotEntry extends RoiEvidenceSnapshotSummaryEntry {
  tenantId: string;
  summary: RoiSummaryEntry;
  workflows: readonly WorkflowRoiEntry[];
  teamRollups: readonly RoiTeamRollupEntry[];
  evidence: {
    dataSources: readonly string[];
    assumptions: readonly RoiWorkflowAssumptionsEntry[];
  };
}

export interface RoiEvidenceExportEntry {
  contract: "business-roi-cockpit.v1";
  generatedAt: string;
  summary: RoiSummaryEntry;
  workflows: readonly WorkflowRoiEntry[];
  evidence: {
    dataSources: readonly string[];
    assumptions: readonly RoiWorkflowAssumptionsEntry[];
    snapshots: readonly RoiEvidenceSnapshotSummaryEntry[];
  };
}

export interface RoiEvidenceSnapshotInputEntry {
  snapshotType?: RoiEvidenceSnapshotTypeEntry;
}

export type EvidenceScheduleKindEntry = "approval-analytics-snapshot" | "business-roi-snapshot" | "evaluation-production-evidence";
export type EvidenceScheduleStatusEntry = "active" | "disabled";
export type EvidenceScheduleRunStatusEntry = "success" | "failed";
export type ProductionEvidenceAlertSeverityEntry = "warning" | "critical";
export type ProductionEvidenceAlertTypeEntry = "promotion-gate" | "score-regression" | "trace-coverage" | "cost-spike" | "persistent-review-fail" | "budget-exceeded" | "release-evidence-export";
export type ProductionEvidenceAlertDeliveryTargetTypeEntry = "webhook";
export type ProductionEvidenceAlertDeliveryStatusEntry = "delivered" | "skipped" | "failed";

export interface ProductionEvidenceAlertThresholdsEntry {
  minTraceCoverage?: number;
  maxScoreRegression?: number;
  maxRegressionCases?: number;
  maxCostIncreaseMicrocents?: number;
  maxCostMicrocents?: number;
  maxConsecutiveReviewOrFail?: number;
  requirePromotionGatePass?: boolean;
}

export interface ProductionEvidenceAlertEntry {
  type: ProductionEvidenceAlertTypeEntry;
  severity: ProductionEvidenceAlertSeverityEntry;
  message: string;
  metric?: string;
  threshold?: number;
  actual?: number;
}

export interface ProductionEvidenceAlertDeliveryTargetEntry {
  targetId: string;
  type: ProductionEvidenceAlertDeliveryTargetTypeEntry;
  label?: string;
  endpointUrl: string;
  minSeverity: ProductionEvidenceAlertSeverityEntry;
  enabled: boolean;
  signingSecretKey?: string;
}

export interface ProductionEvidenceAlertDeliveryTargetInputEntry {
  targetId?: string;
  type?: ProductionEvidenceAlertDeliveryTargetTypeEntry;
  label?: string;
  endpointUrl: string;
  minSeverity?: ProductionEvidenceAlertSeverityEntry;
  enabled?: boolean;
  signingSecretKey?: string;
}

export interface ProductionEvidenceAlertDeliveryRequestSummaryEntry {
  endpointHost: string;
  endpointPath: string;
  payloadSha256: string;
  payloadBytes: number;
  signed: boolean;
}

export interface ProductionEvidenceAlertDeliveryResponseSummaryEntry {
  statusCode?: number;
  bodySha256?: string;
  bodyPreview?: string;
}

export interface ProductionEvidenceAlertDeliveryAttemptEntry {
  attempt: number;
  attemptedAt: string;
  status: Extract<ProductionEvidenceAlertDeliveryStatusEntry, "delivered" | "failed">;
  statusCode?: number;
  error?: string;
  nextRetryAt?: string;
  requestSummary: ProductionEvidenceAlertDeliveryRequestSummaryEntry;
  responseSummary?: ProductionEvidenceAlertDeliveryResponseSummaryEntry;
}

export interface ProductionEvidenceAlertDeliveryResultEntry {
  targetId: string;
  type: ProductionEvidenceAlertDeliveryTargetTypeEntry;
  label?: string;
  status: ProductionEvidenceAlertDeliveryStatusEntry;
  severity: ProductionEvidenceAlertSeverityEntry;
  alertCount: number;
  deliveredAt: string;
  statusCode?: number;
  error?: string;
  attemptCount?: number;
  maxAttempts?: number;
  nextRetryAt?: string;
  idempotencyKey?: string;
  signingKeyId?: string;
  requestSummary?: ProductionEvidenceAlertDeliveryRequestSummaryEntry;
  responseSummary?: ProductionEvidenceAlertDeliveryResponseSummaryEntry;
  attempts?: readonly ProductionEvidenceAlertDeliveryAttemptEntry[];
}

export interface ProductionEvidenceRetrievalProfilePromotionEvidenceEntry {
  query?: string;
  candidateId?: string;
  winnerCandidateId?: string;
  score?: number;
  recall?: number;
  precision?: number;
  expectedCount?: number;
  resultCount?: number;
  evaluatedAt?: string;
  notes?: string;
}

export interface ProductionEvidenceRetrievalProfileEvidenceEntry {
  profileId: string;
  profileName: string;
  stage: RetrievalProfileStageEntry;
  status: RetrievalProfileStatusEntry;
  source: "last-promotion";
  promotionEvidence: ProductionEvidenceRetrievalProfilePromotionEvidenceEntry;
  score?: number;
  recall?: number;
  precision?: number;
  evaluatedAt?: string;
  validatedAt: string;
}

export interface ProductionEvidenceDecisionPackEntry {
  scheduleId: string;
  datasetId: string;
  experimentId?: string;
  agentId?: string;
  releaseId?: string;
  retrievalProfileId?: string;
  retrievalProfileEvidence?: ProductionEvidenceRetrievalProfileEvidenceEntry;
  releaseChecklistId?: string;
  runId: string;
  verdict: EvaluationVerdictEntry;
  promotionStatus?: EvaluationVerdictEntry;
  bestCandidateId?: string;
  scoreDelta?: number;
  traceCoverage?: number;
  costDeltaMicrocents?: number;
  totalCostMicrocents?: number;
  alertCount: number;
  criticalAlertCount: number;
  alerts: readonly ProductionEvidenceAlertEntry[];
  evidenceUrl: string;
  generatedAt: string;
}

export interface EvidenceScheduleEntry {
  tenantId: string;
  scheduleId: string;
  kind: EvidenceScheduleKindEntry;
  displayName: string;
  status: EvidenceScheduleStatusEntry;
  intervalMinutes: number;
  nextRunAt: string;
  lastRunAt?: string;
  lastRunStatus?: EvidenceScheduleRunStatusEntry;
  lastRunError?: string;
  lastRunSnapshotId?: string;
  snapshotType?: RoiEvidenceSnapshotTypeEntry;
  reason?: string;
  exportLabel?: string;
  datasetId?: string;
  experimentId?: string;
  agentId?: string;
  releaseId?: string;
  retrievalProfileId?: string;
  attachReleaseEvidence?: boolean;
  alertThresholds?: ProductionEvidenceAlertThresholdsEntry;
  alertDeliveryTargets?: readonly ProductionEvidenceAlertDeliveryTargetEntry[];
  alertDeliveryTargetCount?: number;
  lastAlertDeliveryResults?: readonly ProductionEvidenceAlertDeliveryResultEntry[];
  alertDeliveryHistory?: readonly ProductionEvidenceAlertDeliveryResultEntry[];
  recentRuns?: readonly EvidenceScheduleRunHistoryEntry[];
  candidateCount?: number;
  lastRunAlertCount?: number;
  lastRunCriticalAlertCount?: number;
  lastRunAlerts?: readonly ProductionEvidenceAlertEntry[];
  lastRunDecisionPack?: ProductionEvidenceDecisionPackEntry;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface EvidenceScheduleRunHistoryEntry {
  runId: string;
  ranAt: string;
  status: EvidenceScheduleRunStatusEntry;
  snapshotId?: string;
  error?: string;
  alertCount?: number;
  criticalAlertCount?: number;
  alerts?: readonly ProductionEvidenceAlertEntry[];
  decisionPack?: ProductionEvidenceDecisionPackEntry;
  alertDeliveryResults?: readonly ProductionEvidenceAlertDeliveryResultEntry[];
}

export interface EvidenceScheduleAuditEventEntry {
  eventId: string;
  scheduleId: string;
  recordedAt: string;
  eventType: string;
  payload: Readonly<Record<string, unknown>>;
}

export interface EvidenceScheduleInputEntry {
  scheduleId?: string;
  kind: EvidenceScheduleKindEntry;
  displayName?: string;
  status?: EvidenceScheduleStatusEntry;
  intervalMinutes?: number;
  nextRunAt?: string;
  snapshotType?: Exclude<RoiEvidenceSnapshotTypeEntry, "manual">;
  reason?: string;
  exportLabel?: string;
  datasetId?: string;
  experimentId?: string;
  agentId?: string;
  releaseId?: string;
  retrievalProfileId?: string;
  candidates?: readonly EvaluationCandidateEntry[];
  alertThresholds?: ProductionEvidenceAlertThresholdsEntry;
  alertDeliveryTargets?: readonly ProductionEvidenceAlertDeliveryTargetInputEntry[];
  attachReleaseEvidence?: boolean;
}

export interface EvidenceScheduleRunEntry {
  schedule: EvidenceScheduleEntry;
  ranAt: string;
  status: EvidenceScheduleRunStatusEntry;
  snapshotId?: string;
  error?: string;
  alerts?: readonly ProductionEvidenceAlertEntry[];
  decisionPack?: ProductionEvidenceDecisionPackEntry;
  alertDeliveryResults?: readonly ProductionEvidenceAlertDeliveryResultEntry[];
}

export interface EvidenceScheduleRunResultEntry {
  attempted: number;
  succeeded: number;
  failed: number;
  runs: readonly EvidenceScheduleRunEntry[];
}

export interface ProductionEvidenceAlertDeliveryRetryResultEntry {
  attempted: number;
  succeeded: number;
  failed: number;
  deliveries: readonly ProductionEvidenceAlertDeliveryResultEntry[];
}

export interface ProductionEvidencePackTraceReferenceEntry {
  candidateId: string;
  caseId: string;
  traceId?: string;
  taskId?: string;
  spanId?: string;
  threadId?: string;
  nodeId?: string;
}

export interface ProductionEvidencePackIntegrityEntry {
  canonicalization: "aioscp-json-v1";
  payloadSha256: string;
  signatureAlgorithm: "hmac-sha256" | "sha256";
  signature: string;
  keyId: string;
  signedAt: string;
}

export interface ProductionEvidenceExportPackEntry {
  contract: "production-evidence-pack.v1";
  packId: string;
  tenantId: string;
  scheduleId: string;
  runId: string;
  exportedAt: string;
  schedule: EvidenceScheduleEntry;
  scheduleRun: EvidenceScheduleRunHistoryEntry;
  decisionPack: ProductionEvidenceDecisionPackEntry;
  evaluationRun?: EvaluationRunEntry;
  experiment?: EvaluationExperimentEntry;
  release?: AgentReleaseEntry;
  trustDossier?: {
    agentId: string;
    route: string;
    exportRoute: string;
  };
  traceAndCostReferences: {
    traceReferences: readonly ProductionEvidencePackTraceReferenceEntry[];
    totalCostMicrocents?: number;
    traceCoverage?: number;
  };
  integrity: ProductionEvidencePackIntegrityEntry;
}

export type PlatformIsolationTierEntry = "shared-saas" | "dedicated-execution" | "dedicated-deployment";

export interface PlatformWorkspaceEntitlementSummaryEntry {
  enabledFeatureCount: number;
  quotaFeatureCount: number;
}

export interface PlatformWorkspaceOnboardingSummaryEntry {
  completed: boolean;
  completedAt?: string;
  updatedAt?: string;
  blockerCount: number;
  blockers: TenantOnboardingBlockerEntry[];
  activationEventCount: number;
  lastActivationEvent?: TenantOnboardingActivationEventEntry;
}

export type PlatformBudgetEnforcementModeEntry = "observe" | "alert" | "enforce";
export type PlatformSpendWarningLevelEntry = "unconfigured" | "ok" | "warning" | "over_budget";
export type PlatformSloStatusEntry = "no_data" | "ok" | "warning" | "breach";
export type PlatformFleetHealthStatusEntry = "healthy" | "attention" | "critical";

export interface PlatformBudgetControlsEntry {
  monthlySpendLimitCents: number | null;
  alertThresholdPercent: number;
  enforcementMode: PlatformBudgetEnforcementModeEntry;
  notes?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface PlatformSloControlsEntry {
  targetP95DurationMs: number;
  targetSuccessRate: number;
  updatedAt?: string;
  updatedBy?: string;
}

export interface PlatformWorkspaceControlsEntry {
  isolationTier: PlatformIsolationTierEntry;
  budget: PlatformBudgetControlsEntry;
  slo: PlatformSloControlsEntry;
}

export interface PlatformWorkspaceControlsUpdateEntry {
  isolationTier?: PlatformIsolationTierEntry;
  budget?: {
    monthlySpendLimitCents?: number | null;
    alertThresholdPercent?: number;
    enforcementMode?: PlatformBudgetEnforcementModeEntry;
    notes?: string | null;
  };
  slo?: {
    targetP95DurationMs?: number;
    targetSuccessRate?: number;
  };
}

export interface PlatformSpendPostureEntry {
  currentMonthlySpendCents: number;
  monthlySpendLimitCents: number | null;
  remainingBudgetCents: number | null;
  utilizationPercent: number | null;
  warningLevel: PlatformSpendWarningLevelEntry;
  enforcementMode: PlatformBudgetEnforcementModeEntry;
  source: "task-metrics" | "usage-events" | "none";
}

export interface PlatformSloPostureEntry {
  status: PlatformSloStatusEntry;
  taskCount: number;
  completedCount: number;
  failedCount: number;
  cancelledCount: number;
  successRate: number | null;
  averageDurationMs: number | null;
  p95DurationMs: number | null;
  targetP95DurationMs: number;
  targetSuccessRate: number;
}

export interface PlatformWorkspaceSummaryEntry {
  tenantId: string;
  slug: string;
  name: string;
  status: string;
  region?: string;
  planId: string;
  planTier: string;
  memberCount: number;
  isolationTier: PlatformIsolationTierEntry;
  usage: WorkspaceUsageOverviewEntry;
  entitlements: PlatformWorkspaceEntitlementSummaryEntry;
  onboarding: PlatformWorkspaceOnboardingSummaryEntry;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformWorkspaceOrganizationEntry {
  tenantId: string;
  slug: string;
  name: string;
  status: string;
  metadata: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformWorkspaceDetailEntry {
  organization: PlatformWorkspaceOrganizationEntry;
  summary: PlatformWorkspaceSummaryEntry;
  subscription: SubscriptionEntry;
  plan: PlanEntry | null;
  entitlements: TenantEntitlementSnapshot;
  onboarding: PlatformWorkspaceOnboardingSummaryEntry;
}

export interface PlatformWorkspaceMemberEntry {
  membershipId: string;
  userId: string;
  email?: string;
  name?: string;
  role: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformWorkspaceSubscriptionResultEntry {
  subscription: SubscriptionEntry;
  plan: PlanEntry | null;
  entitlements: TenantEntitlementSnapshot;
  usage: UsageSummaryEntry[];
}

export interface PlatformWorkspaceSubscriptionUpdateEntry {
  planId?: string;
  status?: "trialing" | "active" | "past_due" | "canceled";
  billingProvider?: string | null;
  externalSubscriptionId?: string | null;
  trialEndsAt?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean;
  metadata?: Readonly<Record<string, unknown>>;
  reason: string;
}

export interface TenantEntitlementOverrideEntry {
  overrideId: string;
  tenantId: string;
  featureKey: string;
  enabled?: boolean;
  limit?: number | null;
  unit?: string;
  reason?: string;
  expiresAt?: string;
  metadata: Readonly<Record<string, unknown>>;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformEntitlementOverrideInputEntry {
  overrideId?: string;
  featureKey: string;
  enabled?: boolean;
  limit?: number | null;
  unit?: string;
  expiresAt?: string;
  metadata?: Readonly<Record<string, unknown>>;
  reason: string;
}

export interface PlatformEntitlementOverrideResultEntry {
  override: TenantEntitlementOverrideEntry;
  entitlements: TenantEntitlementSnapshot;
}

export interface PlatformAgentPostureEntry {
  totalAgentCount: number;
  activeAgentCount: number;
  runningAgentCount: number;
  failedAgentCount: number;
}

export interface PlatformLocalRunnerVersionBucketEntry {
  version: string;
  runnerCount: number;
}

export interface PlatformLocalRunnerPostureEntry {
  totalRunnerCount: number;
  onlineRunnerCount: number;
  offlineRunnerCount: number;
  pendingRunnerCount: number;
  retiredRunnerCount: number;
  outdatedRunnerCount: number;
  assignmentCount: number;
  runningAssignmentCount: number;
  drainingAssignmentCount: number;
  failedAssignmentCount: number;
  queuedTaskCount: number;
  runningTaskCount: number;
  failedTaskCount: number;
  currentTaskCount: number;
  taskCapacity: number;
  saturationPercent: number | null;
  latestHeartbeatAt?: string;
  latestVersion?: string;
  versionBreakdown: PlatformLocalRunnerVersionBucketEntry[];
  attentionItems: string[];
}

export interface PlatformWorkspaceOperationalPostureEntry {
  workspace: PlatformWorkspaceSummaryEntry;
  controls: PlatformWorkspaceControlsEntry;
  agents: PlatformAgentPostureEntry;
  localRunners: PlatformLocalRunnerPostureEntry;
  spend: PlatformSpendPostureEntry;
  slo: PlatformSloPostureEntry;
  health: PlatformFleetHealthStatusEntry;
  attentionItems: string[];
}

export interface PlatformFleetOverviewEntry {
  generatedAt: string;
  filters: { query?: string; status?: string; region?: string; limit?: number };
  summary: {
    workspaceCount: number;
    activeWorkspaceCount: number;
    enterpriseWorkspaceCount: number;
    dedicatedWorkspaceCount: number;
    budgetConfiguredCount: number;
    budgetAttentionCount: number;
    sloAttentionCount: number;
    criticalWorkspaceCount: number;
    totalAgentCount: number;
    activeAgentCount: number;
    runningAgentCount: number;
    failedAgentCount: number;
    totalLocalRunnerCount: number;
    onlineLocalRunnerCount: number;
    offlineLocalRunnerCount: number;
    outdatedLocalRunnerCount: number;
    localRunnerAssignmentCount: number;
    localRunnerQueuedTaskCount: number;
    localRunnerCurrentTaskCount: number;
    localRunnerTaskCapacity: number;
    localRunnerSaturationPercent: number | null;
    estimatedMonthlySpendCents: number;
  };
  isolationBreakdown: Record<PlatformIsolationTierEntry, number>;
  planTierBreakdown: Record<string, number>;
  workspaces: PlatformWorkspaceOperationalPostureEntry[];
}

export type ControlTowerTimeRangeEntry = "1h" | "24h" | "7d" | "30d" | "mtd";
export type ControlTowerSectionEntry =
  | "dashboard"
  | "summary"
  | "risk-posture"
  | "alerts"
  | "audit-activity"
  | "agents"
  | "approvals"
  | "connectors"
  | "failed-missions"
  | "usage"
  | "slo"
  | "platform-services"
  | "audit-delivery";

export interface ControlTowerQueryEntry {
  tenantId?: string;
  workspaceId?: string;
  environment?: AgentReleaseEnvironmentEntry;
  region?: string;
  timeRange?: ControlTowerTimeRangeEntry;
}

export interface ControlTowerFiltersEntry extends ControlTowerQueryEntry {
  resolvedTenantId: string;
  source: "tenant" | "fleet";
  timeRange: ControlTowerTimeRangeEntry;
}

export interface ControlTowerSummaryEntry {
  activeAgents: number;
  runningAgents: number;
  failedAgents: number;
  failedMissions: number;
  openMissions: number;
  pendingApprovals: number;
  approvalBottlenecks: number;
  tenantHealth: string;
  spendMtdCents: number | null;
  p95LatencyMs: number | null;
  dlpIncidents: number;
  connectorFailures: number;
}

export interface ControlTowerRiskPostureEntry {
  score: number;
  level: "low" | "elevated" | "critical";
  drivers: readonly string[];
}

export interface ControlTowerAlertEntry {
  id: string;
  severity: "critical" | "warning" | "info";
  title: string;
  description: string;
  source: string;
  createdAt: string;
  route?: string;
}

export interface ControlTowerPlatformServiceStatusEntry {
  id: string;
  name: string;
  status: string;
}

export interface ControlTowerAuditDeliveryEntry {
  targets: readonly AuditDeliveryTargetEntry[];
  configured: boolean;
  activeCount: number;
}

export interface ControlTowerReportActionEntry {
  id: "audit-report" | "usage-report" | "security-posture-report" | "download-logs" | "trace-explorer";
  label: string;
  status: "available" | "unavailable" | "not_implemented";
  method: "GET";
  endpoint?: string;
  route?: string;
  reason?: string;
}

export interface ControlTowerSignalsEntry {
  tasks: readonly TaskListEntry[];
  approvals: readonly ApprovalSummaryEntry[];
  connectors: readonly ConnectorConnectionEntry[];
  releases: readonly AgentReleaseEntry[];
  evaluationRuns: readonly EvaluationRunEntry[];
  roiSnapshots: readonly RoiEvidenceSnapshotSummaryEntry[];
  dlpCategoryTotals: readonly TenantDlpFindingsAggregateCategoryTotalEntry[];
  usage: WorkspaceUsageOverviewEntry | null;
  billing: BillingOverviewEntry | null;
  auditLogs: readonly AuditLogEntry[];
  governancePosture: GovernancePostureSnapshotEntry | null;
  fleet: PlatformFleetOverviewEntry | null;
  auditDelivery: ControlTowerAuditDeliveryEntry;
  errors: Partial<Record<string, string>>;
}

export interface ControlTowerDashboardEntry {
  generatedAt: string;
  filters: ControlTowerFiltersEntry;
  health: HealthData;
  agents: readonly AgentSummary[];
  operationsPosture: PlatformWorkspaceOperationalPostureEntry | null;
  summary: ControlTowerSummaryEntry;
  riskPosture: ControlTowerRiskPostureEntry;
  alerts: readonly ControlTowerAlertEntry[];
  slo: PlatformSloPostureEntry | null;
  platformServices: readonly ControlTowerPlatformServiceStatusEntry[];
  signals: ControlTowerSignalsEntry;
  reportActions: readonly ControlTowerReportActionEntry[];
}

export interface ControlTowerDashboardResponseEntry {
  generatedAt: string;
  filters: ControlTowerFiltersEntry;
  section: ControlTowerSectionEntry;
  dashboard: ControlTowerDashboardEntry;
}

export type ControlTowerReportEntry = "audit" | "usage" | "security-posture" | "logs";

export interface PlatformSupportActorEntry {
  userId: string;
  role: string;
  tenantId: string;
  email?: string;
  name?: string;
}

export type PlatformSupportSessionStatusEntry = "active" | "ended" | "expired";

export interface PlatformSupportSessionEntry {
  sessionId: string;
  tenantId: string;
  tenantName?: string;
  tenantSlug?: string;
  reason: string;
  ticketId?: string;
  status: PlatformSupportSessionStatusEntry;
  startedAt: string;
  expiresAt: string;
  endedAt?: string;
  endReason?: string;
  startedBy: PlatformSupportActorEntry;
  endedBy?: PlatformSupportActorEntry;
  metadata?: Record<string, unknown>;
}

export interface PlatformSupportAuditEntry {
  entryId: string;
  timestamp: string;
  tenantId: string;
  tenantName?: string;
  tenantSlug?: string;
  sessionId?: string;
  action: "support.session.start" | "support.session.end";
  decision: "allow" | "deny";
  reason: string;
  traceId: string;
  operator: PlatformSupportActorEntry;
}

export interface PlatformUserWorkspaceMembershipEntry {
  tenantId: string;
  workspaceName: string;
  role: string;
  status: string;
}

export interface PlatformUserSummaryEntry {
  userId: string;
  email: string;
  name: string;
  homeTenantId: string;
  role: string;
  createdAt: string;
  workspaces: readonly PlatformUserWorkspaceMembershipEntry[];
}

export interface TenantEntitlementSnapshot {
  tenantId: string;
  planId: string;
  planTier: string;
  features: Record<string, {
    featureKey: string;
    kind: "flag" | "quota";
    enabled: boolean;
    limit?: number | null;
    unit?: string;
    source: "plan" | "override" | "default";
  }>;
}

export interface TenantOverview {
  organization: {
    tenantId: string;
    slug: string;
    name: string;
    status: string;
  };
  subscription: SubscriptionEntry;
  plan: PlanEntry | null;
  entitlements: TenantEntitlementSnapshot;
  memberships: MembershipEntry[];
  usage: UsageSummaryEntry[];
}

export type TenantOnboardingCompletionSourceEntry = "starter_agent_deployed" | "skipped" | "auto_completed" | "local_migration";

export type TenantOnboardingActivationEventTypeEntry =
  | "workspace.created"
  | "model_provider.configured"
  | "starter_template.deployed"
  | "first_run.completed"
  | "connector_or_sandbox.activated"
  | "approval_or_eval.configured"
  | "release.promoted"
  | "onboarding.completed";

export interface TenantOnboardingBlockerEntry {
  blockerId: string;
  severity: "info" | "warning" | "critical";
  message: string;
}

export interface TenantOnboardingActivationEventEntry {
  eventId: string;
  eventType: TenantOnboardingActivationEventTypeEntry;
  source: string;
  occurredAt: string;
  actor?: {
    userId?: string;
    email?: string;
    role?: string;
  };
  metadata: Readonly<Record<string, unknown>>;
}

export interface TenantOnboardingStateEntry {
  tenantId: string;
  completed: boolean;
  completedAt?: string;
  completedBy?: {
    userId?: string;
    email?: string;
    role?: string;
  };
  updatedAt?: string;
  activationEvents: TenantOnboardingActivationEventEntry[];
  blockerCount: number;
  blockers: TenantOnboardingBlockerEntry[];
}

export interface TenantOnboardingCompleteInputEntry {
  source: TenantOnboardingCompletionSourceEntry;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface TenantOnboardingEventInputEntry {
  eventType: Exclude<TenantOnboardingActivationEventTypeEntry, "onboarding.completed">;
  source?: string;
  metadata?: Readonly<Record<string, unknown>>;
}

export interface InviteLookupEntry {
  organization: {
    tenantId: string;
    slug: string;
    name: string;
    status: string;
  };
  invite: InviteEntry;
}

export interface PasswordResetLookupEntry {
  email: string;
  tenantId: string;
  expiresAt: string;
}

export interface TenantBrandingEntry {
  tenantId: string;
  appName: string;
  primaryColor: string;
  logoUrl?: string;
  faviconUrl?: string;
  customDomain?: string;
}

export interface TenantSecretSummaryEntry {
  secretId: string;
  tenantId: string;
  secretKey: string;
  displayName?: string;
  last4?: string;
  createdAt: string;
  updatedAt: string;
}

export type ModelProviderSourceEntry = "tenant" | "external" | "env" | "platform" | "mixed" | "none";
export type ModelProviderConfigurationStateEntry = "configured" | "partial" | "platform" | "unconfigured";

export interface ModelProviderStatusEntry {
  providerId: string;
  displayName: string;
  source: ModelProviderSourceEntry;
  configurationState: ModelProviderConfigurationStateEntry;
  requiredSecretKeys: string[];
  optionalSecretKeys: string[];
  configuredSecretKeys: string[];
  secretLast4: Record<string, string>;
  catalogModelIds: string[];
  eligibleModelIds: string[];
  availableModelIds: string[];
}

export interface AvailableModelEntry {
  modelId: string;
  providerId: string;
  source: ModelProviderSourceEntry;
}

export type TenantDlpMode = "off" | "redact" | "block" | "require_approval";

export interface TenantDlpCustomPatternEntry {
  category: string;
  pattern: string;
  flags?: string;
}

export interface TenantDlpPolicyEntry {
  tenantId: string;
  mode: TenantDlpMode;
  categories: string[];
  customPatterns: TenantDlpCustomPatternEntry[];
  updatedAt: string;
  updatedBy?: string;
  availableCategories: string[];
}

export interface TenantDlpPolicyInput {
  mode: TenantDlpMode;
  categories?: string[];
  customPatterns?: TenantDlpCustomPatternEntry[];
}

export interface TenantDlpFindingEntry {
  id: number;
  tenantId: string;
  mode: TenantDlpMode;
  category: string;
  token: string;
  preview: string;
  occurredAt: string;
}

export interface TenantDlpFindingsAggregateBucketEntry {
  day: string;
  mode: TenantDlpMode;
  count: number;
}

export interface TenantDlpFindingsAggregateCategoryTotalEntry {
  category: string;
  count: number;
}

export interface PublicSSOProviderEntry {
  tenantId: string;
  providerId: string;
  displayName: string;
  type: "oidc" | "saml";
  loginUrl: string;
}

export interface EnterpriseSSOProviderEntry {
  tenantId: string;
  providerId: string;
  displayName: string;
  type: "oidc" | "saml";
  status: "active" | "disabled";
  allowedDomains: string[];
  clientId?: string;
  issuerUrl?: string;
  authorizationEndpoint?: string;
  tokenEndpoint?: string;
  userinfoEndpoint?: string;
  scopes: string[];
  samlIssuer?: string;
  samlEntryPoint?: string;
  samlIdpIssuer?: string;
  samlIdentifierFormat?: string;
  samlPreset?: "generic" | "okta" | "azure";
  hasIdpCertificate?: boolean;
  hasSigningPrivateKey?: boolean;
  hasSigningPublicCert?: boolean;
  samlSignMetadata?: boolean;
  samlSignatureAlgorithm?: "sha1" | "sha256" | "sha512";
  defaultRole?: string;
  callbackUrl: string;
  loginUrl: string;
  hasClientSecret: boolean;
  metadataUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface EnterpriseSCIMProviderEntry {
  tenantId: string;
  providerId: string;
  displayName: string;
  status: "active" | "disabled";
  defaultRole?: string;
  roleMappingMode: "default" | "roles" | "groups";
  roleMappings: Record<string, string>;
  baseUrl: string;
  hasBearerToken: boolean;
  tokenLast4?: string;
  createdAt: string;
  updatedAt: string;
  lastRotatedAt?: string;
}

type AuditDeliveryTargetFormat = "csv" | "jsonl";

interface BaseAuditDeliveryTargetEntry {
  targetId: string;
  tenantId: string;
  displayName: string;
  status: "active" | "disabled";
  endpointUrl: string;
  format: AuditDeliveryTargetFormat;
  createdAt: string;
  updatedAt: string;
  lastDeliveredAt?: string;
  lastDeliveryStatus?: "success" | "failed";
  lastDeliveryError?: string;
  scheduleEnabled: boolean;
  scheduleIntervalMinutes?: number;
  nextDeliveryAt?: string;
}

export interface WebhookAuditDeliveryTargetEntry extends BaseAuditDeliveryTargetEntry {
  type: "webhook";
  hasSigningSecret: boolean;
}

export interface S3AuditDeliveryTargetEntry extends BaseAuditDeliveryTargetEntry {
  type: "s3";
  bucket: string;
  region: string;
  prefix?: string;
  endpoint?: string;
  accessKeyId: string;
  hasSecretAccessKey: boolean;
  hasSessionToken: boolean;
  forcePathStyle: boolean;
}

export interface BigQueryAuditDeliveryTargetEntry extends BaseAuditDeliveryTargetEntry {
  type: "bigquery";
  projectId: string;
  datasetId: string;
  tableId: string;
  serviceAccountEmail: string;
  hasServiceAccountKey: boolean;
}

export interface SplunkAuditDeliveryTargetEntry extends BaseAuditDeliveryTargetEntry {
  type: "splunk";
  hasHecToken: boolean;
  index?: string;
  source?: string;
  sourcetype?: string;
}

export interface DatadogAuditDeliveryTargetEntry extends BaseAuditDeliveryTargetEntry {
  type: "datadog";
  site: string;
  hasApiKey: boolean;
  service?: string;
  source?: string;
  tags: string[];
}

export type AuditDeliveryTargetEntry =
  | WebhookAuditDeliveryTargetEntry
  | S3AuditDeliveryTargetEntry
  | BigQueryAuditDeliveryTargetEntry
  | SplunkAuditDeliveryTargetEntry
  | DatadogAuditDeliveryTargetEntry;

export type AuditDeliveryTargetSaveInput =
  | {
      targetId?: string;
      type?: "webhook";
      displayName: string;
      endpointUrl: string;
      format?: AuditDeliveryTargetFormat;
      status?: "active" | "disabled";
      signingSecret?: string;
      scheduleEnabled?: boolean;
      scheduleIntervalMinutes?: number;
    }
  | {
      targetId?: string;
      type: "s3";
      displayName: string;
      bucket: string;
      region: string;
      prefix?: string;
      endpoint?: string;
      accessKeyId: string;
      secretAccessKey?: string;
      sessionToken?: string;
      forcePathStyle?: boolean;
      format?: AuditDeliveryTargetFormat;
      status?: "active" | "disabled";
      scheduleEnabled?: boolean;
      scheduleIntervalMinutes?: number;
    }
  | {
      targetId?: string;
      type: "bigquery";
      displayName: string;
      projectId: string;
      datasetId: string;
      tableId: string;
      serviceAccountEmail: string;
      serviceAccountPrivateKey?: string;
      format?: "jsonl";
      status?: "active" | "disabled";
      scheduleEnabled?: boolean;
      scheduleIntervalMinutes?: number;
    }
  | {
      targetId?: string;
      type: "splunk";
      displayName: string;
      endpointUrl: string;
      hecToken?: string;
      index?: string;
      source?: string;
      sourcetype?: string;
      format?: AuditDeliveryTargetFormat;
      status?: "active" | "disabled";
      scheduleEnabled?: boolean;
      scheduleIntervalMinutes?: number;
    }
  | {
      targetId?: string;
      type: "datadog";
      displayName: string;
      site?: string;
      endpointUrl?: string;
      apiKey?: string;
      service?: string;
      source?: string;
      tags?: string[];
      format?: "jsonl";
      status?: "active" | "disabled";
      scheduleEnabled?: boolean;
      scheduleIntervalMinutes?: number;
    };

export interface AuditDeliveryResultEntry {
  target: AuditDeliveryTargetEntry;
  responseStatus: number;
  deliveredAt: string;
  count: number;
}

export interface AuditLifecycleEventEntry {
  kind: "delivery";
  source: "manual" | "scheduled";
  status: "success" | "failed";
  deliveredAt: string;
  target: AuditDeliveryTargetEntry;
  responseStatus?: number;
  count?: number;
  error?: string;
}

export interface AuditLogEntry {
  entryId: string;
  timestamp: string;
  agentId: string;
  tenantId: string;
  action: string;
  resource: string;
  decision: "allow" | "deny";
  matchedRuleId: string;
  reason: string;
  traceId: string;
}

export type GovernancePostureStatusEntry = "ready" | "attention" | "locked";
export type GovernanceRetentionSourceEntry = "default" | "environment" | "config";
export type GovernanceDlpMaturityEntry = "off" | "redacting" | "blocking";
export type GovernanceByokPostureEntry = "tenant-managed" | "partial" | "platform-fallback" | "missing" | "unavailable";
export type GovernanceEntitlementSourceEntry = "plan" | "override" | "default";

export interface GovernanceRetentionPolicyEntry {
  auditLogDays: number;
  dlpFindingDays: number;
  source: GovernanceRetentionSourceEntry;
}

export interface GovernanceAuditPostureEntry {
  exportEnabled: boolean;
  exportSource: GovernanceEntitlementSourceEntry;
  recentEntryCount: number;
  denyEntryCount: number;
  deliveryTargetCount: number;
  activeDeliveryTargetCount: number;
  scheduledDeliveryTargetCount: number;
  failingDeliveryTargetCount: number;
  lastEntryAt?: string;
}

export interface GovernanceDlpPostureEntry {
  mode: TenantDlpMode;
  maturity: GovernanceDlpMaturityEntry;
  enabledCategoryCount: number;
  customPatternCount: number;
  findingCount7d: number;
  latestFindingAt?: string;
}

export interface GovernanceByokPostureViewEntry {
  posture: GovernanceByokPostureEntry;
  configuredProviderCount: number;
  tenantManagedProviderCount: number;
  platformProviderCount: number;
  modelCount: number;
}

export interface GovernancePostureSnapshotEntry {
  tenantId: string;
  generatedAt: string;
  status: GovernancePostureStatusEntry;
  retention: GovernanceRetentionPolicyEntry;
  audit: GovernanceAuditPostureEntry;
  dlp: GovernanceDlpPostureEntry;
  byok: GovernanceByokPostureViewEntry;
  attentionItems: string[];
}

export interface BillingLineItemEntry {
  description: string;
  quantity: number;
  unitAmountCents: number;
  subtotalCents: number;
}

export interface BillingCustomerEntry {
  customerId: string;
  tenantId: string;
  provider: "local" | "stripe";
  externalCustomerId?: string;
  email?: string;
  displayName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BillingInvoiceEntry {
  invoiceId: string;
  tenantId: string;
  subscriptionId?: string;
  provider: "local" | "stripe";
  externalInvoiceId?: string;
  status: "draft" | "open" | "paid" | "void";
  currency: string;
  subtotalCents: number;
  totalCents: number;
  lineItems: BillingLineItemEntry[];
  hostedInvoiceUrl?: string;
  periodStart?: string;
  periodEnd?: string;
  issuedAt: string;
  paidAt?: string;
}

export interface BillingOverviewEntry {
  provider: "local" | "stripe";
  paidPlanChangesEnabled: boolean;
  customer: BillingCustomerEntry | null;
  subscription: SubscriptionEntry;
  plan: PlanEntry | null;
  paymentMethod: BillingPaymentMethodEntry | null;
  invoices: BillingInvoiceEntry[];
  usageCharges: BillingUsageChargeEntry[];
}

export interface BillingUsageChargeEntry {
  featureKey: string;
  metricKey: string;
  label: string;
  currentQuantity: number;
  includedQuantity: number;
  overageQuantity: number;
  unitPriceCents: number;
  subtotalCents: number;
  currency: string;
  unit?: string;
  periodStart: string;
  periodEnd?: string;
  invoiceId?: string;
  invoiceStatus?: "draft" | "open" | "paid" | "void";
}

export interface BillingPaymentMethodEntry {
  status: "missing" | "configured";
  type?: string;
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
  source?: "customer" | "subscription";
}

export interface BillingCheckoutSessionEntry {
  provider: "local" | "stripe";
  mode: "direct" | "redirect";
  sessionId: string;
  checkoutUrl?: string;
  message?: string;
}

export interface BillingPortalSessionEntry {
  provider: "local" | "stripe";
  mode: "local" | "redirect";
  url?: string;
  message?: string;
}

export type AutomationTriggerTypeEntry = "webhook" | "cron" | "metric" | "connector_event";
export type AutomationTriggerMethodEntry = "GET" | "POST" | "PUT";
export type AutomationMetricOperatorEntry = "gt" | "lt" | "gte" | "lte" | "eq";

export interface AutomationTriggerBaseInputEntry {
  triggerId?: string;
  name: string;
  targetAgentIds: string[];
  inputTemplate?: Record<string, unknown>;
  enabled?: boolean;
  spaceId?: string;
}

export interface WebhookAutomationTriggerInputEntry extends AutomationTriggerBaseInputEntry {
  type: "webhook";
  webhookPath?: string;
  methods?: AutomationTriggerMethodEntry[];
  secret?: string;
  filter?: string;
}

export interface CronAutomationTriggerInputEntry extends AutomationTriggerBaseInputEntry {
  type: "cron";
  cronExpression: string;
  timezone?: string;
}

export interface MetricAutomationTriggerInputEntry extends AutomationTriggerBaseInputEntry {
  type: "metric";
  metricName: string;
  operator: AutomationMetricOperatorEntry;
  threshold: number;
  windowSeconds?: number;
  cooldownSeconds?: number;
}

export interface ConnectorEventAutomationTriggerInputEntry extends AutomationTriggerBaseInputEntry {
  type: "connector_event";
  connectorId: string;
  eventType: string;
  connectionId?: string;
}

export type AutomationTriggerCreateInputEntry =
  | WebhookAutomationTriggerInputEntry
  | CronAutomationTriggerInputEntry
  | MetricAutomationTriggerInputEntry
  | ConnectorEventAutomationTriggerInputEntry;

export interface AutomationWebhookDetailsEntry {
  webhookPath: string;
  methods: AutomationTriggerMethodEntry[];
  hasSecret: boolean;
  filter?: string;
}

export interface AutomationCronDetailsEntry {
  cronExpression: string;
  timezone: string;
  lastFiredAt?: string;
  nextFireAt?: string;
}

export interface AutomationMetricDetailsEntry {
  metricName: string;
  operator: AutomationMetricOperatorEntry;
  threshold: number;
  windowSeconds: number;
  cooldownSeconds: number;
  lastFiredAt?: string;
}

export interface AutomationConnectorEventDetailsEntry {
  connectorId: string;
  eventType: string;
  connectionId?: string;
  lastFiredAt?: string;
}

export interface AutomationTriggerEntry {
  triggerId: string;
  tenantId: string;
  name: string;
  type: AutomationTriggerTypeEntry;
  targetAgentIds: string[];
  inputTemplate: Record<string, unknown>;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  spaceId?: string;
  webhook?: AutomationWebhookDetailsEntry;
  cron?: AutomationCronDetailsEntry;
  metric?: AutomationMetricDetailsEntry;
  connectorEvent?: AutomationConnectorEventDetailsEntry;
}

export interface AutomationTriggerEventEntry {
  eventId: string;
  triggerId: string;
  tenantId: string;
  triggerType: AutomationTriggerTypeEntry;
  firedAt: string;
  payload: Record<string, unknown>;
  activatedAgentIds: string[];
  replayed?: boolean;
}

export interface AutomationTriggerTestFireInputEntry {
  payload?: Record<string, unknown>;
  method?: AutomationTriggerMethodEntry;
  metricValue?: number;
  connectionId?: string;
  eventId?: string;
}

export interface AutomationTriggerDryRunDispatchPreviewEntry {
  targetAgentId: string;
  taskInput: Record<string, unknown>;
}

export interface AutomationTriggerDryRunPreviewEntry {
  previewId: string;
  tenantId: string;
  triggerId: string;
  triggerType: AutomationTriggerTypeEntry;
  firedAt: string;
  payload: Record<string, unknown>;
  activatedAgentIds: string[];
  dispatches: AutomationTriggerDryRunDispatchPreviewEntry[];
  enabled: boolean;
  wouldDispatch: boolean;
}

export interface AutomationTriggerTaskSummaryEntry {
  taskId: string;
  agentId: string;
  status: string;
  traceId?: string;
  threadId?: string;
  submittedAt: string;
  completedAt: string | null;
  updatedAt: string;
  attemptCount: number;
  maxAttempts: number;
}

export interface AutomationTriggerEvidenceLinksEntry {
  task?: string;
  trace?: string;
  trustDossier?: string;
}

export interface AutomationTriggerEvidenceLinkEntry {
  taskId: string;
  agentId: string;
  traceId?: string;
  links: AutomationTriggerEvidenceLinksEntry;
}

export interface AutomationTriggerDispatchCorrelationEntry {
  dispatchId: string;
  triggerId: string;
  eventId: string;
  targetAgentId: string;
  status: "pending" | "submitted" | "failed" | "dead_letter";
  taskInput: Record<string, unknown>;
  taskId?: string;
  traceId?: string;
  attemptCount: number;
  maxAttempts: number;
  nextAttemptAt?: string;
  lastAttemptAt?: string;
  lastError?: string;
  createdAt: string;
  updatedAt: string;
  task?: AutomationTriggerTaskSummaryEntry;
  evidenceLinks?: AutomationTriggerEvidenceLinksEntry;
}

export interface AutomationTriggerEventCorrelationEntry {
  event: AutomationTriggerEventEntry;
  dispatches: AutomationTriggerDispatchCorrelationEntry[];
  taskIds: string[];
  traceIds: string[];
  evidenceLinks?: AutomationTriggerEvidenceLinkEntry[];
}

export const api = {
  health: () => request<HealthData>("GET", "/health"),

  auth: {
    login: (email: string, password: string) =>
      request<{ user: AuthenticatedUser; token: string }>("POST", "/api/v1/auth/login", { email, password }),
    logout: () => request<{ ok: true }>("POST", "/api/v1/auth/logout"),
    register: (email: string, password: string, name: string, tenantId = "default", tenantName?: string) =>
      request<{ user: AuthenticatedUser; tenant: TenantOverview }>("POST", "/api/v1/auth/register", {
        email,
        password,
        name,
        tenantId,
        ...(tenantName ? { tenantName } : {}),
      }),
    invite: (token: string) =>
      request<InviteLookupEntry>("GET", `/api/v1/invites/${encodeURIComponent(token)}`),
    acceptInvite: (token: string, name: string, password: string) =>
      request<{ user: AuthenticatedUser; token: string; membership: MembershipEntry; invite: InviteEntry }>(
        "POST",
        "/api/v1/auth/accept-invite",
        { token, name, password },
      ),
    requestPasswordReset: (email: string, continueInviteToken?: string) =>
      request<{ accepted: true }>("POST", "/api/v1/auth/request-password-reset", {
        email,
        ...(continueInviteToken ? { continueInviteToken } : {}),
      }),
    passwordReset: (token: string) =>
      request<{ reset: PasswordResetLookupEntry }>("GET", `/api/v1/auth/password-reset/${encodeURIComponent(token)}`),
    resetPassword: (token: string, password: string) =>
      request<{ user: AuthenticatedUser; token: string }>("POST", "/api/v1/auth/reset-password", {
        token,
        password,
      }),
    ssoProviders: (tenantId: string) =>
      request<{ providers: PublicSSOProviderEntry[] }>(
        "GET",
        `/api/v1/auth/sso/providers?tenantId=${encodeURIComponent(tenantId)}`,
      ),
    me: () => request<{ user: AuthenticatedUser | null }>("GET", "/api/v1/auth/me"),
  },

  saas: {
    tenant: () => request<{ tenant: TenantOverview }>("GET", "/api/v1/tenant"),
    onboarding: () => request<{ onboarding: TenantOnboardingStateEntry }>("GET", "/api/v1/tenant/onboarding"),
    recordOnboardingEvent: (input: TenantOnboardingEventInputEntry) =>
      request<{ onboarding: TenantOnboardingStateEntry }>("POST", "/api/v1/tenant/onboarding/events", input),
    completeOnboarding: (input: TenantOnboardingCompleteInputEntry) =>
      request<{ onboarding: TenantOnboardingStateEntry }>("POST", "/api/v1/tenant/onboarding/complete", input),
    activeSupportSessions: () => request<{ sessions: PlatformSupportSessionEntry[] }>("GET", "/api/v1/tenant/support-sessions/active"),
    members: () => request<{ memberships: MembershipEntry[] }>("GET", "/api/v1/tenant/members"),
    invites: () => request<{ invites: InviteEntry[] }>("GET", "/api/v1/tenant/invites"),
    inviteMember: (email: string, role: string) =>
      request<{ membership: MembershipEntry; invite: InviteEntry }>("POST", "/api/v1/tenant/members", { email, role }),
    resendInvite: (inviteId: string) =>
      request<{ invite: InviteEntry }>("POST", `/api/v1/tenant/invites/${encodeURIComponent(inviteId)}/resend`),
    revokeInvite: (inviteId: string) =>
      request<{ invite: InviteEntry }>("POST", `/api/v1/tenant/invites/${encodeURIComponent(inviteId)}/revoke`),
    updateMember: (membershipId: string, updates: { role?: string; status?: MembershipEntry["status"] }) =>
      request<{ membership: MembershipEntry }>("POST", `/api/v1/tenant/members/${encodeURIComponent(membershipId)}`, updates),
    plans: () => request<{ plans: PlanEntry[] }>("GET", "/api/v1/plans"),
    usage: () => request<{ usage: WorkspaceUsageOverviewEntry }>("GET", "/api/v1/usage"),
    subscription: () => request<{
      subscription: SubscriptionEntry;
      plan: PlanEntry | null;
      entitlements: TenantEntitlementSnapshot;
      usage: UsageSummaryEntry[];
    }>("GET", "/api/v1/subscription"),
    changePlan: (planId: string) =>
      request<{
        subscription: SubscriptionEntry;
        plan: PlanEntry | null;
        entitlements: TenantEntitlementSnapshot;
      }>("POST", "/api/v1/subscription", { planId }),
    branding: () => request<{ branding: TenantBrandingEntry }>("GET", "/api/v1/settings/branding"),
    updateBranding: (branding: Partial<TenantBrandingEntry>) =>
      request<{ branding: TenantBrandingEntry }>("POST", "/api/v1/settings/branding", branding),
    mcpPolicy: () => request<{ policy: TenantMcpPolicyConfigEntry }>("GET", "/api/v1/settings/interop/mcp"),
    updateMcpPolicy: (policy: Pick<TenantMcpPolicyConfigEntry, "servicePolicies" | "toolPolicies" | "bindingPolicies">) =>
      request<{ policy: TenantMcpPolicyConfigEntry }>("POST", "/api/v1/settings/interop/mcp", policy),
    modelProviders: () => request<{ providers: ModelProviderStatusEntry[]; models: AvailableModelEntry[] }>("GET", "/api/v1/settings/model-providers"),
    secrets: () => request<{ secrets: TenantSecretSummaryEntry[] }>("GET", "/api/v1/settings/secrets"),
    saveSecret: (secretKey: string, secretValue: string, displayName?: string) =>
      request<{ secret: TenantSecretSummaryEntry }>("POST", "/api/v1/settings/secrets", {
        secretKey,
        secretValue,
        ...(displayName ? { displayName } : {}),
      }),
    deleteSecret: (secretKey: string) =>
      request<void>("DELETE", `/api/v1/settings/secrets/${encodeURIComponent(secretKey)}`),
    dlpPolicy: () => request<{ policy: TenantDlpPolicyEntry }>("GET", "/api/v1/tenant/dlp-policy"),
    updateDlpPolicy: (policy: TenantDlpPolicyInput) =>
      request<{ policy: TenantDlpPolicyEntry }>("PUT", "/api/v1/tenant/dlp-policy", policy),
    dlpFindings: (options: { limit?: number; beforeId?: number; category?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.limit !== undefined) params.set("limit", String(options.limit));
      if (options.beforeId !== undefined) params.set("beforeId", String(options.beforeId));
      if (options.category !== undefined && options.category.trim() !== "") {
        params.set("category", options.category.trim());
      }
      const qs = params.toString();
      return request<{ findings: TenantDlpFindingEntry[] }>(
        "GET",
        `/api/v1/tenant/dlp-findings${qs ? `?${qs}` : ""}`,
      );
    },
    dlpFindingsAggregate: (options: { days?: number; category?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.days !== undefined) params.set("days", String(options.days));
      if (options.category !== undefined && options.category.trim() !== "") {
        params.set("category", options.category.trim());
      }
      const qs = params.toString();
      return request<{
        buckets: TenantDlpFindingsAggregateBucketEntry[];
        categoryTotals: TenantDlpFindingsAggregateCategoryTotalEntry[];
      }>(
        "GET",
        `/api/v1/tenant/dlp-findings/aggregate${qs ? `?${qs}` : ""}`,
      );
    },
  },

  platform: {
    plans: (filters: { activeOnly?: boolean } = {}) =>
      request<{ plans: PlanEntry[] }>("GET", `/api/v1/platform/plans${buildQueryString({ activeOnly: filters.activeOnly })}`),
    plan: (planId: string) =>
      request<{ plan: PlanEntry }>("GET", `/api/v1/platform/plans/${encodeURIComponent(planId)}`),
    createPlan: (input: PlatformPlanCreateInputEntry) =>
      request<{ plan: PlanEntry }>("POST", "/api/v1/platform/plans", input),
    updatePlan: (planId: string, input: PlatformPlanUpdateInputEntry) =>
      request<{ plan: PlanEntry }>("PUT", `/api/v1/platform/plans/${encodeURIComponent(planId)}`, input),
    archivePlan: (planId: string, input: PlatformPlanArchiveInputEntry) =>
      request<{ plan: PlanEntry }>("POST", `/api/v1/platform/plans/${encodeURIComponent(planId)}/archive`, input),
    clonePlan: (planId: string, input: PlatformPlanCloneInputEntry) =>
      request<{ plan: PlanEntry }>("POST", `/api/v1/platform/plans/${encodeURIComponent(planId)}/clone`, input),
    previewPlan: (planId: string, input: PlatformPlanPreviewInputEntry) =>
      request<{ preview: PlatformPlanImpactPreviewEntry }>("POST", `/api/v1/platform/plans/${encodeURIComponent(planId)}/preview`, input),
    fleetOverview: (filters: { query?: string; status?: string; region?: string; limit?: number } = {}) =>
      request<{ fleet: PlatformFleetOverviewEntry }>("GET", `/api/v1/platform/fleet/overview${buildQueryString({
        query: filters.query?.trim() || undefined,
        status: filters.status,
        region: filters.region?.trim() || undefined,
        limit: filters.limit,
      })}`),
    workspaces: (filters: { query?: string; status?: string; region?: string; limit?: number } = {}) =>
      request<{ workspaces: PlatformWorkspaceSummaryEntry[] }>("GET", `/api/v1/platform/workspaces${buildQueryString({
        query: filters.query?.trim() || undefined,
        status: filters.status,
        region: filters.region?.trim() || undefined,
        limit: filters.limit,
      })}`),
    workspace: (tenantId: string) =>
      request<{ workspace: PlatformWorkspaceDetailEntry }>(
        "GET",
        `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}`,
      ),
    workspacePosture: (tenantId: string) =>
      request<{ posture: PlatformWorkspaceOperationalPostureEntry }>(
        "GET",
        `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/posture`,
      ),
    updateWorkspaceControls: (tenantId: string, controls: PlatformWorkspaceControlsUpdateEntry) =>
      request<{ posture: PlatformWorkspaceOperationalPostureEntry }>(
        "PATCH",
        `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/controls`,
        controls,
      ),
    startSupportSession: (tenantId: string, input: {
      reason: string;
      expiresInMinutes?: number;
      ticketId?: string;
      metadata?: Record<string, unknown>;
    }) =>
      request<{ session: PlatformSupportSessionEntry }>(
        "POST",
        `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/support-sessions`,
        input,
      ),
    workspaceSupportSessions: (
      tenantId: string,
      filters: { status?: PlatformSupportSessionStatusEntry; limit?: number } = {},
    ) =>
      request<{ sessions: PlatformSupportSessionEntry[] }>(
        "GET",
        `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/support-sessions${buildQueryString({
          status: filters.status,
          limit: filters.limit,
        })}`,
      ),
    supportSessions: (
      filters: { tenantId?: string; status?: PlatformSupportSessionStatusEntry; limit?: number } = {},
    ) =>
      request<{ sessions: PlatformSupportSessionEntry[] }>(
        "GET",
        `/api/v1/platform/support-sessions${buildQueryString({
          tenantId: filters.tenantId,
          status: filters.status,
          limit: filters.limit,
        })}`,
      ),
    endSupportSession: (sessionId: string, input: { reason?: string } = {}) =>
      request<{ session: PlatformSupportSessionEntry }>(
        "POST",
        `/api/v1/platform/support-sessions/${encodeURIComponent(sessionId)}/end`,
        input,
      ),
    supportAudit: (filters: { tenantId?: string; since?: string; until?: string; limit?: number } = {}) =>
      request<{ entries: PlatformSupportAuditEntry[] }>(
        "GET",
        `/api/v1/platform/audit/support-actions${buildQueryString({
          tenantId: filters.tenantId,
          since: filters.since,
          until: filters.until,
          limit: filters.limit,
        })}`,
      ),
    members: (tenantId: string) =>
      request<{ members: PlatformWorkspaceMemberEntry[] }>(
        "GET",
        `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/members`,
      ),
    usage: (tenantId: string) =>
      request<{ usage: WorkspaceUsageOverviewEntry }>(
        "GET",
        `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/usage`,
      ),
    subscription: (tenantId: string) =>
      request<PlatformWorkspaceSubscriptionResultEntry>(
        "GET",
        `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/subscription`,
      ),
    updateSubscription: (tenantId: string, input: PlatformWorkspaceSubscriptionUpdateEntry) =>
      request<PlatformWorkspaceSubscriptionResultEntry>(
        "PATCH",
        `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/subscription`,
        input,
      ),
    entitlementOverrides: (tenantId: string) =>
      request<{ overrides: TenantEntitlementOverrideEntry[] }>(
        "GET",
        `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/entitlement-overrides`,
      ),
    createEntitlementOverride: (tenantId: string, input: PlatformEntitlementOverrideInputEntry) =>
      request<PlatformEntitlementOverrideResultEntry>(
        "POST",
        `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/entitlement-overrides`,
        input,
      ),
    updateEntitlementOverride: (tenantId: string, overrideId: string, input: PlatformEntitlementOverrideInputEntry) =>
      request<PlatformEntitlementOverrideResultEntry>(
        "PUT",
        `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/entitlement-overrides/${encodeURIComponent(overrideId)}`,
        input,
      ),
    expireEntitlementOverride: (tenantId: string, overrideId: string, reason: string) =>
      request<PlatformEntitlementOverrideResultEntry>(
        "POST",
        `/api/v1/platform/workspaces/${encodeURIComponent(tenantId)}/entitlement-overrides/${encodeURIComponent(overrideId)}/expire`,
        { reason },
      ),
    users: (filters: { query: string; limit?: number }) =>
      request<{ users: PlatformUserSummaryEntry[] }>("GET", `/api/v1/platform/users${buildQueryString({
        query: filters.query.trim(),
        limit: filters.limit,
      })}`),
  },

  operations: {
    posture: () => request<{ posture: PlatformWorkspaceOperationalPostureEntry }>("GET", "/api/v1/operations/posture"),
  },

  controlTower: {
    dashboard: (filters: ControlTowerQueryEntry = {}) =>
      request<ControlTowerDashboardResponseEntry>("GET", `/api/v1/control-tower/dashboard${buildQueryString({
        tenantId: filters.tenantId,
        workspaceId: filters.workspaceId,
        environment: filters.environment,
        region: filters.region,
        timeRange: filters.timeRange,
      })}`),
    section: (section: Exclude<ControlTowerSectionEntry, "dashboard">, filters: ControlTowerQueryEntry = {}) =>
      request<{ generatedAt: string; filters: ControlTowerFiltersEntry; section: ControlTowerSectionEntry } & Record<string, unknown>>(
        "GET",
        `/api/v1/control-tower/${section}${buildQueryString({
          tenantId: filters.tenantId,
          workspaceId: filters.workspaceId,
          environment: filters.environment,
          region: filters.region,
          timeRange: filters.timeRange,
        })}`,
      ),
    report: async (report: ControlTowerReportEntry, filters: ControlTowerQueryEntry = {}) => {
      const path = `/api/v1/control-tower/reports/${encodeURIComponent(report)}${buildQueryString({
        tenantId: filters.tenantId,
        workspaceId: filters.workspaceId,
        environment: filters.environment,
        region: filters.region,
        timeRange: filters.timeRange,
      })}`;
      const headers = buildCredentialHeaders({ method: "GET", path });

      let res: Response;
      try {
        res = await fetchWithTimeout(
          `${BASE}${path}`,
          { method: "GET", headers, credentials: "include" },
        );
      } catch (error) {
        throw normalizeRequestError(error);
      }

      const content = await res.text();
      if (!res.ok) {
        throw buildApiRequestError(res, content);
      }

      return {
        fileName: parseFileName(
          res.headers.get("content-disposition"),
          `control-tower-${report}.${report === "usage" ? "csv" : report === "logs" || report === "audit" ? "jsonl" : "json"}`,
        ),
        contentType: res.headers.get("content-type") ?? "text/plain",
        content,
      };
    },
  },

  enterprise: {
    sso: () => request<{ providers: EnterpriseSSOProviderEntry[] }>("GET", "/api/v1/settings/sso"),
    saveSso: (input: {
      providerId: string;
      displayName?: string;
      type?: "oidc" | "saml";
      allowedDomains?: string[];
      preset?: "google" | "okta" | "azure" | "custom";
      clientId?: string;
      clientSecret?: string;
      issuerUrl?: string;
      authorizationEndpoint?: string;
      tokenEndpoint?: string;
      userinfoEndpoint?: string;
      scopes?: string[];
      samlIssuer?: string;
      samlEntryPoint?: string;
      samlIdpIssuer?: string;
      samlIdpCert?: string;
      samlIdentifierFormat?: string;
      samlPreset?: "generic" | "okta" | "azure";
      samlSigningPrivateKey?: string;
      samlSigningPublicCert?: string;
      samlSignMetadata?: boolean;
      samlSignatureAlgorithm?: "sha1" | "sha256" | "sha512";
      defaultRole?: string;
      status?: "active" | "disabled";
    }) => request<{ provider: EnterpriseSSOProviderEntry }>("POST", "/api/v1/settings/sso", input),
    deleteSso: (providerId: string) =>
      request<void>("DELETE", `/api/v1/settings/sso/${encodeURIComponent(providerId)}`),
    scim: () => request<{ providers: EnterpriseSCIMProviderEntry[] }>("GET", "/api/v1/settings/scim"),
    saveScim: (input: {
      providerId: string;
      displayName?: string;
      defaultRole?: string;
      roleMappingMode?: "default" | "roles" | "groups";
      roleMappings?: Record<string, string>;
      status?: "active" | "disabled";
      rotateToken?: boolean;
    }) => request<{ provider: EnterpriseSCIMProviderEntry; bearerToken?: string }>("POST", "/api/v1/settings/scim", input),
    rotateScimToken: (providerId: string) =>
      request<{ provider: EnterpriseSCIMProviderEntry; bearerToken?: string }>(
        "POST",
        `/api/v1/settings/scim/${encodeURIComponent(providerId)}/rotate-token`,
      ),
    deleteScim: (providerId: string) =>
      request<void>("DELETE", `/api/v1/settings/scim/${encodeURIComponent(providerId)}`),
    audit: (limit = 100) =>
      request<{ entries: AuditLogEntry[] }>(
        "GET",
        `/api/v1/settings/audit?limit=${encodeURIComponent(String(limit))}`,
      ),
    auditTargets: () => request<{ targets: AuditDeliveryTargetEntry[] }>("GET", "/api/v1/settings/audit/targets"),
    governancePosture: () =>
      request<{ posture: GovernancePostureSnapshotEntry }>("GET", "/api/v1/settings/governance-posture"),
    saveAuditTarget: (input: AuditDeliveryTargetSaveInput) =>
      request<{ target: AuditDeliveryTargetEntry }>("POST", "/api/v1/settings/audit/targets", input),
    deleteAuditTarget: (targetId: string) =>
      request<void>("DELETE", `/api/v1/settings/audit/targets/${encodeURIComponent(targetId)}`),
    deliverAuditTarget: (targetId: string) =>
      request<AuditDeliveryResultEntry>("POST", `/api/v1/settings/audit/targets/${encodeURIComponent(targetId)}/deliver`),
    auditExport: async (format: "csv" | "jsonl", limit = 1000) => {
      const path = `/api/v1/settings/audit/export?format=${encodeURIComponent(format)}&limit=${encodeURIComponent(String(limit))}`;
      const headers = buildCredentialHeaders({ method: "GET", path });

      let res: Response;
      try {
        res = await fetchWithTimeout(
          `${BASE}${path}`,
          { method: "GET", headers, credentials: "include" },
        );
      } catch (error) {
        throw normalizeRequestError(error);
      }

      const content = await res.text();
      if (!res.ok) {
        throw buildApiRequestError(res, content);
      }

      return {
        fileName: parseFileName(
          res.headers.get("content-disposition"),
          `audit-export.${format === "csv" ? "csv" : "jsonl"}`,
        ),
        contentType: res.headers.get("content-type") ?? "text/plain",
        content,
      };
    },
  },

  billing: {
    overview: () => request<{ billing: BillingOverviewEntry }>("GET", "/api/v1/billing"),
    listPublicPlans: () => request<{ plans: PlanEntry[] }>("GET", "/api/v1/billing/plans"),
    checkout: (planId: string) =>
      request<{
        session: BillingCheckoutSessionEntry;
        customer: BillingCustomerEntry;
        subscription: SubscriptionEntry;
        plan: PlanEntry | null;
        invoice?: BillingInvoiceEntry;
      }>("POST", "/api/v1/billing/checkout", { planId }),
    portal: (flowType: "default" | "payment_method_update" = "default") =>
      request<{
        session: BillingPortalSessionEntry;
        customer: BillingCustomerEntry;
      }>("POST", "/api/v1/billing/portal", flowType === "default" ? undefined : { flowType }),
  },

  agents: {
    list: () => request<{ agents: AgentSummary[]; totalCount: number }>("GET", "/api/v1/agents"),
    get: (id: string) => request<AgentSummary>("GET", `/api/v1/agents/${id}`),
    manifest: (id: string) => request<{ manifest: AgentManifestEntry }>("GET", `/api/v1/agents/${encodeURIComponent(id)}/manifest`),
    trustDossier: (id: string) => request<{ dossier: AgentTrustDossierEntry }>("GET", `/api/v1/agents/${encodeURIComponent(id)}/trust-dossier`),
    exportTrustDossier: (id: string) => request<AgentTrustDossierExportEntry>("GET", `/api/v1/agents/${encodeURIComponent(id)}/trust-dossier/export`),
    spawn: (manifest: unknown) => request<{ agentId: { id: string } }>("POST", "/api/v1/agents", { manifest }),
    kill: (id: string, reason = "UI action") => request<void>("DELETE", `/api/v1/agents/${id}`, { reason }),
  },

  releases: {
    list: (filters: AgentReleaseFiltersEntry = {}) => request<{ releases: readonly AgentReleaseEntry[] }>("GET", `/api/v1/agent-releases${buildQueryString({
      agentId: filters.agentId,
      environment: filters.environment,
      status: filters.status,
      limit: filters.limit,
    })}`),
    listChecklists: (filters: ReleaseEvidenceChecklistFiltersEntry = {}) =>
      request<{ checklists: readonly ReleaseEvidenceChecklistEntry[] }>("GET", `/api/v1/release-evidence-checklists${buildQueryString({
        agentId: filters.agentId,
        status: filters.status,
        limit: filters.limit,
      })}`),
    get: (releaseId: string) => request<{ release: AgentReleaseEntry }>("GET", `/api/v1/agent-releases/${encodeURIComponent(releaseId)}`),
    create: (input: AgentReleaseCreateInputEntry) => request<{ release: AgentReleaseEntry }>("POST", "/api/v1/agent-releases", input),
    promote: (releaseId: string, input: AgentReleasePromoteInputEntry = {}) => request<{ release: AgentReleaseEntry }>("POST", `/api/v1/agent-releases/${encodeURIComponent(releaseId)}/promote`, input),
    rollback: (releaseId: string, input: AgentReleaseRollbackInputEntry = {}) => request<{ release: AgentReleaseEntry }>("POST", `/api/v1/agent-releases/${encodeURIComponent(releaseId)}/rollback`, input),
    deploy: (releaseId: string, input: AgentReleaseDeployInputEntry = {}) =>
      request<{ release: AgentReleaseEntry; deployment: { agentId: { id: string }; initialState: string } }>(
        "POST",
        `/api/v1/agent-releases/${encodeURIComponent(releaseId)}/deploy`,
        input,
      ),
    getEvidenceChecklist: (releaseId: string) =>
      request<{ checklist: ReleaseEvidenceChecklistEntry }>("GET", `/api/v1/agent-releases/${encodeURIComponent(releaseId)}/evidence-checklist`),
    createEvidenceChecklist: (releaseId: string, input: ReleaseEvidenceChecklistCreateInputEntry = {}) =>
      request<{ checklist: ReleaseEvidenceChecklistEntry }>("POST", `/api/v1/agent-releases/${encodeURIComponent(releaseId)}/evidence-checklist`, input),
    updateEvidenceChecklistItem: (releaseId: string, itemId: string, input: ReleaseEvidenceChecklistItemInputEntry) =>
      request<{ checklist: ReleaseEvidenceChecklistEntry }>(
        "PATCH",
        `/api/v1/agent-releases/${encodeURIComponent(releaseId)}/evidence-checklist/items/${encodeURIComponent(itemId)}`,
        input,
      ),
    approveEvidenceChecklist: (releaseId: string, input: ReleaseEvidenceChecklistApproveInputEntry = {}) =>
      request<{ checklist: ReleaseEvidenceChecklistEntry }>("POST", `/api/v1/agent-releases/${encodeURIComponent(releaseId)}/evidence-checklist/approve`, input),
  },

  workspaceReviews: {
    list: (filters: WorkspaceReviewFiltersEntry = {}) =>
      request<{ reviews: readonly WorkspaceReviewEntry[] }>("GET", `/api/v1/workspace/reviews${buildQueryString({
        targetType: filters.targetType,
        targetId: filters.targetId,
        status: filters.status,
        assignee: filters.assignee,
        limit: filters.limit,
      })}`),
    get: (reviewId: string) =>
      request<{ review: WorkspaceReviewEntry }>("GET", `/api/v1/workspace/reviews/${encodeURIComponent(reviewId)}`),
    create: (input: WorkspaceReviewCreateInputEntry) =>
      request<{ review: WorkspaceReviewEntry }>("POST", "/api/v1/workspace/reviews", input),
    update: (reviewId: string, input: WorkspaceReviewUpdateInputEntry) =>
      request<{ review: WorkspaceReviewEntry }>("PATCH", `/api/v1/workspace/reviews/${encodeURIComponent(reviewId)}`, input),
    decide: (reviewId: string, input: WorkspaceReviewDecisionInputEntry) =>
      request<{ review: WorkspaceReviewEntry }>("POST", `/api/v1/workspace/reviews/${encodeURIComponent(reviewId)}/decision`, input),
  },

  localRunners: {
    list: (filters: LocalRunnerListFiltersEntry = {}) => request<{ runners: readonly LocalRunnerEntry[] }>("GET", `/api/v1/local-runners${buildQueryString({
      status: filters.status,
      limit: filters.limit,
    })}`),
    register: (input: LocalRunnerRegisterInputEntry) =>
      request<{ runner: LocalRunnerEntry; registrationToken: string }>("POST", "/api/v1/local-runners", input),
    rotateToken: (runnerId: string, input: LocalRunnerTokenRotationInputEntry) =>
      request<{ runner: LocalRunnerEntry; registrationToken: string }>("POST", `/api/v1/local-runners/${encodeURIComponent(runnerId)}/rotate-token`, input),
    rotateCertificate: (runnerId: string, input: LocalRunnerCertificateRotationInputEntry) =>
      request<{ runner: LocalRunnerEntry }>("POST", `/api/v1/local-runners/${encodeURIComponent(runnerId)}/rotate-certificate`, input),
    retire: (runnerId: string, input: LocalRunnerLifecycleInputEntry) =>
      request<{ runner: LocalRunnerEntry; retiredAssignments: readonly LocalRunnerAssignmentEntry[] }>("POST", `/api/v1/local-runners/${encodeURIComponent(runnerId)}/retire`, input),
    assignments: {
      list: (runnerId: string, filters: LocalRunnerAssignmentListFiltersEntry = {}) =>
        request<{ assignments: readonly LocalRunnerAssignmentEntry[] }>("GET", `/api/v1/local-runners/${encodeURIComponent(runnerId)}/assignments${buildQueryString({
          status: filters.status,
          targetType: filters.targetType,
          limit: filters.limit,
        })}`),
      assign: (runnerId: string, input: LocalRunnerAssignmentCreateInputEntry) =>
        request<{ assignment: LocalRunnerAssignmentEntry }>("POST", `/api/v1/local-runners/${encodeURIComponent(runnerId)}/assignments`, input),
      drain: (runnerId: string, assignmentId: string, input: LocalRunnerLifecycleInputEntry) =>
        request<{ assignment: LocalRunnerAssignmentEntry }>("POST", `/api/v1/local-runners/${encodeURIComponent(runnerId)}/assignments/${encodeURIComponent(assignmentId)}/drain`, input),
      unassign: (runnerId: string, assignmentId: string, input: LocalRunnerLifecycleInputEntry) =>
        request<{ assignment: LocalRunnerAssignmentEntry }>("DELETE", `/api/v1/local-runners/${encodeURIComponent(runnerId)}/assignments/${encodeURIComponent(assignmentId)}`, input),
    },
    pools: {
      assign: (runnerPoolId: string, input: LocalRunnerAssignmentCreateInputEntry) =>
        request<LocalRunnerPoolAssignmentResultEntry>("POST", `/api/v1/local-runner-pools/${encodeURIComponent(runnerPoolId)}/assignments`, input),
    },
    tasks: {
      submit: (runnerId: string, assignmentId: string, input: LocalRunnerTaskSubmitInputEntry = {}) =>
        request<{ task: LocalRunnerTaskEntry }>("POST", `/api/v1/local-runners/${encodeURIComponent(runnerId)}/assignments/${encodeURIComponent(assignmentId)}/tasks`, input),
    },
  },

  rollouts: {
    get: (agentId: string, environment: AgentReleaseEnvironmentEntry = "production") =>
      request<{ plan: RolloutPlanEntry | null }>(
        "GET",
        `/api/v1/agents/${encodeURIComponent(agentId)}/rollout${buildQueryString({ environment })}`,
      ),
    set: (agentId: string, input: RolloutSetInputEntry) =>
      request<{ plan: RolloutPlanEntry }>(
        "PUT",
        `/api/v1/agents/${encodeURIComponent(agentId)}/rollout`,
        input,
      ),
    rollbackCanary: (agentId: string, environment: AgentReleaseEnvironmentEntry = "production") =>
      request<{ plan: RolloutPlanEntry | null }>(
        "POST",
        `/api/v1/agents/${encodeURIComponent(agentId)}/rollout/rollback-canary${buildQueryString({ environment })}`,
        {},
      ),
  },

  tasks: {
    list: () => request<{ tasks: TaskListEntry[]; totalCount: number }>("GET", "/api/v1/tasks"),
    submit: (agentId: string, input: Record<string, unknown>) =>
      request<TaskHandle>("POST", "/api/v1/tasks", { agentId, input, timeoutSeconds: 300 }),
    get: (id: string) => request<TaskResultEntry>("GET", `/api/v1/tasks/${id}`),
    detail: (id: string) => request<TaskDetailEntry>("GET", `/api/v1/tasks/${id}/detail`),
    cancel: (id: string, reason = "UI cancel") => request<unknown>("DELETE", `/api/v1/tasks/${id}`, { reason }),
  },

  threads: {
    list: (filters: ThreadListFilters = {}) =>
      request<ThreadListPage>("GET", `/api/v1/threads${buildQueryString({
        agentId: filters.agentId,
        status: filters.status,
        cursor: filters.cursor,
        limit: filters.limit,
      })}`),
    get: (threadId: string) => request<ThreadDetailEntry>("GET", `/api/v1/threads/${encodeURIComponent(threadId)}`),
    resume: (threadId: string, input: ThreadResumeInputEntry = {}) =>
      request<ThreadMutationResultEntry>("POST", `/api/v1/threads/${encodeURIComponent(threadId)}/resume`, input),
    resumeFailedNode: (threadId: string, input: ThreadFailedNodeResumeInputEntry = {}) =>
      request<ThreadMutationResultEntry>("POST", `/api/v1/threads/${encodeURIComponent(threadId)}/resume-failed-node`, input),
    replay: (threadId: string, input: ThreadReplayInputEntry = {}) =>
      request<ThreadMutationResultEntry>("POST", `/api/v1/threads/${encodeURIComponent(threadId)}/replay`, input),
    fork: (threadId: string, input: ThreadForkInputEntry = {}) =>
      request<ThreadMutationResultEntry>("POST", `/api/v1/threads/${encodeURIComponent(threadId)}/fork`, input),
    cancel: (threadId: string, input: ThreadCancelInputEntry = {}) =>
      request<ThreadCancelResultEntry>("POST", `/api/v1/threads/${encodeURIComponent(threadId)}/cancel`, input),
    exportCheckpoint: (threadId: string, input: ThreadCheckpointExportInputEntry = {}) =>
      request<ThreadCheckpointExportResultEntry>("POST", `/api/v1/threads/${encodeURIComponent(threadId)}/export`, input),
    importCheckpoint: (input: ThreadCheckpointImportInputEntry) =>
      request<ThreadMutationResultEntry>("POST", "/api/v1/threads/import", input),
  },

  approvals: {
    list: (filters: ApprovalListFilters = {}) =>
      request<ApprovalListPage>("GET", `/api/v1/approvals${buildQueryString({
        status: filters.status,
        threadId: filters.threadId,
        agentId: filters.agentId,
        cursor: filters.cursor,
        limit: filters.limit,
      })}`),
    listPending: (filters: Omit<ApprovalListFilters, "status"> = {}) =>
      request<ApprovalListPage>("GET", `/api/v1/approvals${buildQueryString({
        status: "pending",
        threadId: filters.threadId,
        agentId: filters.agentId,
        cursor: filters.cursor,
        limit: filters.limit,
      })}`),
    respond: (approvalId: string, input: ApprovalRespondInputEntry) =>
      request<ApprovalRespondResultEntry>("POST", `/api/v1/approvals/${encodeURIComponent(approvalId)}/respond`, input),
    bulkRespond: (input: ApprovalBulkRespondInputEntry) =>
      request<ApprovalBulkRespondResultEntry>("POST", "/api/v1/approvals/bulk/respond", input),
    analytics: async () => {
      const response = await request<{ analytics: ApprovalAnalyticsEntry }>("GET", "/api/v1/approvals/analytics");
      return response.analytics;
    },
    exportAnalyticsEvidence: async (input: { exportType?: ApprovalAnalyticsExportTypeEntry; label?: string } = {}) => {
      const params = new URLSearchParams();
      if (input.exportType) params.set("exportType", input.exportType);
      if (input.label) params.set("label", input.label);
      const query = params.toString();
      const response = await request<{ export: ApprovalAnalyticsEvidenceExportEntry }>(
        "GET",
        `/api/v1/approvals/analytics/export${query ? `?${query}` : ""}`,
      );
      return response.export;
    },
    createAnalyticsSnapshot: async (input: ApprovalAnalyticsSnapshotInputEntry = {}) => {
      const response = await request<{ snapshot: ApprovalAnalyticsSnapshotEntry }>(
        "POST",
        "/api/v1/approvals/analytics/snapshots",
        input,
      );
      return response.snapshot;
    },
    listAnalyticsSnapshots: async () => {
      const response = await request<{ snapshots: ApprovalAnalyticsSnapshotEntry[] }>(
        "GET",
        "/api/v1/approvals/analytics/snapshots",
      );
      return response.snapshots;
    },
    getNotifications: async () => {
      const response = await request<{ notifications: ApprovalNotificationSettingsEntry }>(
        "GET",
        "/api/v1/settings/approvals/notifications",
      );
      return response.notifications;
    },
    updateNotifications: async (input: ApprovalNotificationUpdateInputEntry) => {
      const response = await request<{ notifications: ApprovalNotificationSettingsEntry }>(
        "POST",
        "/api/v1/settings/approvals/notifications",
        input,
      );
      return response.notifications;
    },
    listChainTemplates: async () => {
      const response = await request<{ chainTemplates: ApprovalChainTemplateConfigEntry }>(
        "GET",
        "/api/v1/settings/approvals/chains",
      );
      return response.chainTemplates;
    },
    upsertChainTemplate: async (input: ApprovalChainTemplateUpsertInputEntry) => {
      const response = await request<{ template: ApprovalChainTemplateEntry }>(
        "POST",
        "/api/v1/settings/approvals/chains",
        input,
      );
      return response.template;
    },
    deleteChainTemplate: async (templateId: string) => {
      await request<void>("DELETE", `/api/v1/settings/approvals/chains/${encodeURIComponent(templateId)}`);
    },
  },

  retrieval: {
    listCollections: async () => {
      const response = await request<{ collections: RetrievalCollectionEntry[] }>("GET", "/api/v1/retrieval/collections");
      return {
        collections: response.collections.map(normalizeRetrievalCollection),
      };
    },
    observability: () => request<{ observability: RetrievalObservabilitySummaryEntry }>("GET", "/api/v1/retrieval/observability"),
    simulateGovernance: (input: RetrievalGovernanceSimulationInputEntry) => request<{ simulation: RetrievalGovernanceSimulationResultEntry }>(
      "POST",
      "/api/v1/retrieval/governance/simulate",
      {
        ...(input.profileId !== undefined ? { profileId: input.profileId } : {}),
        ...(input.config !== undefined ? { config: input.config } : {}),
        ...(input.callerRoles !== undefined ? { callerRoles: [...input.callerRoles] } : {}),
        ...(input.targetStage !== undefined ? { targetStage: input.targetStage } : {}),
        ...(input.status !== undefined ? { status: input.status } : {}),
        ...(input.evidence !== undefined ? { evidence: input.evidence } : {}),
      },
    ),
    listChunkingProfiles: () => request<{ profiles: RetrievalChunkingProfileEntry[] }>("GET", "/api/v1/retrieval/chunking-profiles"),
    saveChunkingProfile: (input: RetrievalChunkingProfileInputEntry) => request<{ profile: RetrievalChunkingProfileEntry }>("POST", "/api/v1/retrieval/chunking-profiles", {
      name: input.name,
      config: input.config,
      ...(input.profileId !== undefined ? { profileId: input.profileId } : {}),
      ...(input.description !== undefined ? { description: input.description } : {}),
      ...(input.status !== undefined ? { status: input.status } : {}),
      ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
    }),
    deleteChunkingProfile: (profileId: string) => request<void>("DELETE", `/api/v1/retrieval/chunking-profiles/${encodeURIComponent(profileId)}`),
    listProfiles: () => request<{ profiles: RetrievalProfileEntry[] }>("GET", "/api/v1/retrieval/profiles"),
    saveProfile: (input: RetrievalProfileInputEntry) => request<{ profile: RetrievalProfileEntry }>("POST", "/api/v1/retrieval/profiles", {
      name: input.name,
      config: input.config,
      ...(input.profileId !== undefined ? { profileId: input.profileId } : {}),
      ...(input.description !== undefined ? { description: input.description } : {}),
      ...(input.stage !== undefined ? { stage: input.stage } : {}),
      ...(input.status !== undefined ? { status: input.status } : {}),
      ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
    }),
    promoteProfile: (profileId: string, input: RetrievalProfilePromotionInputEntry) => request<{ profile: RetrievalProfileEntry }>("POST", `/api/v1/retrieval/profiles/${encodeURIComponent(profileId)}/promote`, {
      targetStage: input.targetStage,
      ...(input.status !== undefined ? { status: input.status } : {}),
      ...(input.reason !== undefined ? { reason: input.reason } : {}),
      ...(input.evidence !== undefined ? { evidence: input.evidence } : {}),
      ...(input.archiveExistingProduction !== undefined ? { archiveExistingProduction: input.archiveExistingProduction } : {}),
    }),
    deleteProfile: (profileId: string) => request<void>("DELETE", `/api/v1/retrieval/profiles/${encodeURIComponent(profileId)}`),
    runExperiment: (input: RetrievalExperimentInputEntry) => request<{ experiment: RetrievalExperimentResultEntry }>(
      "POST",
      "/api/v1/retrieval/experiments/run",
      {
        query: input.query,
        candidates: input.candidates.map((candidate) => ({
          ...(candidate.candidateId !== undefined ? { candidateId: candidate.candidateId } : {}),
          ...(candidate.profileId !== undefined ? { profileId: candidate.profileId } : {}),
          ...(candidate.label !== undefined ? { label: candidate.label } : {}),
          ...(candidate.config !== undefined ? { config: candidate.config } : {}),
        })),
        ...(input.expected !== undefined ? { expected: input.expected.map((target) => ({ kind: target.kind, id: target.id })) } : {}),
      },
    ),
    createCollection: (input: RetrievalCollectionCreateInput) =>
      request<{ collection: RetrievalCollectionEntry }>("POST", "/api/v1/retrieval/collections", {
        name: input.name,
        dimensions: input.dimensions,
        ...(input.description !== undefined ? { description: input.description } : {}),
        ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
      }),
    deleteCollection: (collectionId: string) =>
      request<void>("DELETE", `/api/v1/retrieval/collections/${encodeURIComponent(collectionId)}`),
    ingest: (input: RetrievalIngestInput) =>
      request<{ results: RetrievalIngestResultEntry[] }>(
        "POST",
        `/api/v1/retrieval/collections/${encodeURIComponent(input.collectionId)}/documents`,
        {
          ...(input.chunkingProfileId !== undefined ? { chunkingProfileId: input.chunkingProfileId } : {}),
          documents: input.documents.map((document) => ({
            sourceId: document.sourceId,
            ...(document.filename !== undefined ? { filename: document.filename } : {}),
            ...(document.documentType !== undefined ? { documentType: document.documentType } : {}),
            ...(document.content !== undefined ? { content: document.content } : {}),
            ...(document.fileId !== undefined ? { fileId: document.fileId } : {}),
            ...(document.metadata !== undefined ? { metadata: document.metadata } : {}),
            ...(document.chunkingProfileId !== undefined ? { chunkingProfileId: document.chunkingProfileId } : {}),
            ...(document.chunking !== undefined ? { chunking: document.chunking } : {}),
          })),
        },
      ),
    search: (input: RetrievalSearchInput) =>
      request<{ results: RetrievalSearchResultEntry[] }>("POST", "/api/v1/retrieval/search", {
        query: input.query,
        ...(input.collectionId !== undefined ? { collectionId: input.collectionId } : {}),
        ...(input.collectionIds !== undefined ? { collectionIds: [...input.collectionIds] } : {}),
        ...(input.mode !== undefined ? { mode: input.mode } : {}),
        ...(input.topK !== undefined ? { topK: input.topK } : {}),
        ...(input.minScore !== undefined ? { minScore: input.minScore } : {}),
        ...(input.filters !== undefined ? { filters: input.filters } : {}),
        ...(input.includeContent !== undefined ? { includeContent: input.includeContent } : {}),
        ...(input.rerank !== undefined ? { rerank: input.rerank } : {}),
      }),
    evaluate: (input: RetrievalEvaluateInputEntry) => request<{ evaluation: RetrievalEvaluationResultEntry }>(
      "POST",
      "/api/v1/retrieval/evaluate",
      {
        query: input.query,
        expected: input.expected.map((target) => ({ kind: target.kind, id: target.id })),
        ...(input.collectionId !== undefined ? { collectionId: input.collectionId } : {}),
        ...(input.collectionIds !== undefined ? { collectionIds: [...input.collectionIds] } : {}),
        ...(input.mode !== undefined ? { mode: input.mode } : {}),
        ...(input.topK !== undefined ? { topK: input.topK } : {}),
        ...(input.minScore !== undefined ? { minScore: input.minScore } : {}),
        ...(input.filters !== undefined ? { filters: input.filters } : {}),
        ...(input.includeContent !== undefined ? { includeContent: input.includeContent } : {}),
        ...(input.rerank !== undefined ? { rerank: input.rerank } : {}),
      },
    ),
  },

  evals: {
    listDatasets: () => request<{ datasets: EvaluationDatasetEntry[] }>("GET", "/api/v1/evals/datasets"),
    getDataset: (datasetId: string) =>
      request<{ dataset: EvaluationDatasetEntry }>("GET", `/api/v1/evals/datasets/${encodeURIComponent(datasetId)}`),
    saveDataset: (input: EvaluationDatasetInputEntry) =>
      request<{ dataset: EvaluationDatasetEntry }>("POST", "/api/v1/evals/datasets", input),
    deleteDataset: (datasetId: string) =>
      request<void>("DELETE", `/api/v1/evals/datasets/${encodeURIComponent(datasetId)}`),
    listRuns: (filters: EvaluationRunFiltersEntry = {}) => request<{ runs: EvaluationRunEntry[] }>(
      "GET",
      `/api/v1/evals/runs${buildQueryString({
        datasetId: filters.datasetId,
        limit: filters.limit,
      })}`,
    ),
    run: (input: EvaluationRunInputEntry) =>
      request<{ run: EvaluationRunEntry }>("POST", "/api/v1/evals/runs", input),
    getRun: (runId: string) =>
      request<{ run: EvaluationRunEntry }>("GET", `/api/v1/evals/runs/${encodeURIComponent(runId)}`),
    listExperiments: (filters: EvaluationExperimentFiltersEntry = {}) => request<{ experiments: EvaluationExperimentEntry[] }>(
      "GET",
      `/api/v1/evals/experiments${buildQueryString({
        status: filters.status,
        datasetId: filters.datasetId,
        limit: filters.limit,
      })}`,
    ),
    getExperiment: (experimentId: string) =>
      request<{ experiment: EvaluationExperimentEntry }>("GET", `/api/v1/evals/experiments/${encodeURIComponent(experimentId)}`),
    saveExperiment: (input: EvaluationExperimentInputEntry) =>
      request<{ experiment: EvaluationExperimentEntry }>("POST", "/api/v1/evals/experiments", input),
    archiveExperiment: (experimentId: string) =>
      request<{ experiment: EvaluationExperimentEntry }>("DELETE", `/api/v1/evals/experiments/${encodeURIComponent(experimentId)}`),
    notifyExperimentReview: (experimentId: string, input: EvaluationExperimentReviewNotifyInputEntry = {}) =>
      request<EvaluationExperimentReviewNotifyResultEntry>(
        "POST",
        `/api/v1/evals/experiments/${encodeURIComponent(experimentId)}/review-notify`,
        input,
      ),
  },

  prompts: {
    list: (filters: { status?: PromptStatusEntry; tag?: string; query?: string; limit?: number } = {}) =>
      request<{ prompts: PromptEntry[] }>("GET", `/api/v1/prompts${buildQueryString(filters)}`),
    get: (promptIdOrSlug: string) =>
      request<PromptDetailEntry>("GET", `/api/v1/prompts/${encodeURIComponent(promptIdOrSlug)}`),
    create: (input: PromptCreateInputEntry) =>
      request<PromptDetailEntry & { version: PromptVersionEntry }>("POST", "/api/v1/prompts", input),
    createVersion: (promptIdOrSlug: string, input: {
      messages: readonly PromptMessageEntry[];
      variables?: readonly string[];
      includes?: readonly string[];
      metadata?: Readonly<Record<string, unknown>>;
      changeSummary?: string;
    }) =>
      request<PromptDetailEntry & { version: PromptVersionEntry }>(
        "POST",
        `/api/v1/prompts/${encodeURIComponent(promptIdOrSlug)}/versions`,
        input,
      ),
    labels: (promptIdOrSlug: string) =>
      request<{ labels: PromptLabelEntry[] }>("GET", `/api/v1/prompts/${encodeURIComponent(promptIdOrSlug)}/labels`),
    updateLabel: (
      promptIdOrSlug: string,
      label: string,
      input: {
        version: number;
        protected?: boolean;
        metadata?: Record<string, unknown>;
        approvalId?: string;
        evidence?: Record<string, unknown>;
      },
    ) =>
      request<{ label: PromptLabelEntry }>(
        "POST",
        `/api/v1/prompts/${encodeURIComponent(promptIdOrSlug)}/labels/${encodeURIComponent(label)}`,
        input,
      ),
    render: (promptIdOrSlug: string, input: {
      version?: number;
      label?: string;
      variables?: Record<string, unknown>;
      additionalContext?: string;
      allowUnresolved?: boolean;
      useCache?: boolean;
    } = {}) =>
      request<PromptRenderResultEntry>("POST", `/api/v1/prompts/${encodeURIComponent(promptIdOrSlug)}/render`, input),
    runPlayground: (promptIdOrSlug: string, input: {
      version?: number;
      label?: string;
      variables?: Record<string, unknown>;
      additionalContext?: string;
      allowUnresolved?: boolean;
      modelSettings?: Record<string, unknown>;
    }) =>
      request<{ run: PromptPlaygroundRunEntry }>(
        "POST",
        `/api/v1/prompts/${encodeURIComponent(promptIdOrSlug)}/playground/runs`,
        input,
      ),
    playgroundRuns: (promptIdOrSlug: string, limit = 20) =>
      request<{ runs: PromptPlaygroundRunEntry[] }>(
        "GET",
        `/api/v1/prompts/${encodeURIComponent(promptIdOrSlug)}/playground/runs${buildQueryString({ limit })}`,
      ),
    experiments: (filters: { promptIdOrSlug?: string; status?: PromptExperimentEntry["status"]; limit?: number } = {}) =>
      request<{ experiments: PromptExperimentEntry[] }>(
        "GET",
        `/api/v1/prompt-experiments${buildQueryString(filters)}`,
      ),
    createExperiment: (input: {
      name: string;
      description?: string;
      promptIdOrSlug: string;
      datasetId?: string;
      evaluationRunId?: string;
      candidates: readonly { candidateId?: string; promptIdOrSlug?: string; version?: number; label?: string; trafficWeight?: number }[];
      metadata?: Record<string, unknown>;
    }) =>
      request<{ experiment: PromptExperimentEntry }>("POST", "/api/v1/prompt-experiments", input),
    runExperiment: (experimentId: string, input: {
      metrics: Record<string, { score: number; sampleCount?: number; latencyMs?: number; inputTokens?: number; outputTokens?: number }>;
      evaluationRunId?: string;
      metadata?: Record<string, unknown>;
    }) =>
      request<{ experiment: PromptExperimentEntry }>(
        "POST",
        `/api/v1/prompt-experiments/${encodeURIComponent(experimentId)}/run`,
        input,
      ),
    notificationSettings: () =>
      request<{ settings: PromptNotificationSettingsEntry }>("GET", "/api/v1/prompts/notifications/settings"),
    updateNotificationSettings: (input: {
      enabled?: boolean;
      events?: readonly string[];
      webhookUrl?: string | null;
      webhookSigningSecret?: string | null;
      slackWebhookUrl?: string | null;
      slackChannel?: string | null;
    }) =>
      request<{ settings: PromptNotificationSettingsEntry }>("POST", "/api/v1/prompts/notifications/settings", input),
    notificationDeliveries: (filters: { eventType?: string; limit?: number } = {}) =>
      request<{ deliveries: PromptNotificationDeliveryEntry[] }>(
        "GET",
        `/api/v1/prompts/notifications/deliveries${buildQueryString(filters)}`,
      ),
    sendTestNotification: () =>
      request<{ deliveries: PromptNotificationDeliveryEntry[] }>("POST", "/api/v1/prompts/notifications/test", {}),
  },

  roi: {
    summary: () => request<{ summary: RoiSummaryEntry }>("GET", "/api/v1/roi/summary"),
    workflows: () => request<{ workflows: WorkflowRoiEntry[] }>("GET", "/api/v1/roi/workflows"),
    updateAssumptions: (workflowId: string, input: RoiWorkflowAssumptionsUpdateEntry) =>
      request<{ workflow: WorkflowRoiEntry }>(
        "PUT",
        `/api/v1/roi/workflows/${encodeURIComponent(workflowId)}/assumptions`,
        {
          ...(input.displayName !== undefined ? { displayName: input.displayName } : {}),
          ...(input.estimatedManualMinutesPerTask !== undefined ? { estimatedManualMinutesPerTask: input.estimatedManualMinutesPerTask } : {}),
          ...(input.loadedHourlyCostUsd !== undefined ? { loadedHourlyCostUsd: input.loadedHourlyCostUsd } : {}),
          ...(input.failureCostUsd !== undefined ? { failureCostUsd: input.failureCostUsd } : {}),
          ...(input.approvalCostUsd !== undefined ? { approvalCostUsd: input.approvalCostUsd } : {}),
          ...(input.successCriteria !== undefined ? { successCriteria: input.successCriteria } : {}),
        },
      ),
    exportEvidence: () => request<{ export: RoiEvidenceExportEntry }>("GET", "/api/v1/roi/export"),
    createSnapshot: (input: RoiEvidenceSnapshotInputEntry = {}) =>
      request<{ snapshot: RoiEvidenceSnapshotEntry }>("POST", "/api/v1/roi/snapshots", input),
    listSnapshots: () => request<{ snapshots: RoiEvidenceSnapshotSummaryEntry[] }>("GET", "/api/v1/roi/snapshots"),
  },

  evidenceSchedules: {
    list: async () => {
      const response = await request<{ schedules: EvidenceScheduleEntry[] }>("GET", "/api/v1/evidence/schedules");
      return response.schedules;
    },
    upsert: async (input: EvidenceScheduleInputEntry) => {
      const response = await request<{ schedule: EvidenceScheduleEntry }>("POST", "/api/v1/evidence/schedules", input);
      return response.schedule;
    },
    delete: (scheduleId: string) => request<void>("DELETE", `/api/v1/evidence/schedules/${encodeURIComponent(scheduleId)}`),
    runs: async (scheduleId: string, input: { limit?: number } = {}) => {
      const response = await request<{ runs: EvidenceScheduleRunHistoryEntry[] }>(
        "GET",
        `/api/v1/evidence/schedules/${encodeURIComponent(scheduleId)}/runs${buildQueryString({ limit: input.limit })}`,
      );
      return response.runs;
    },
    listAuditEvents: async (scheduleId: string, input: { limit?: number } = {}) => {
      const response = await request<{ events: EvidenceScheduleAuditEventEntry[] }>(
        "GET",
        `/api/v1/evidence/schedules/${encodeURIComponent(scheduleId)}/audit-events${buildQueryString({ limit: input.limit })}`,
      );
      return response.events;
    },
    exportPack: async (scheduleId: string, input: { runId?: string } = {}) => {
      const response = await request<{ pack: ProductionEvidenceExportPackEntry }>(
        "GET",
        `/api/v1/evidence/schedules/${encodeURIComponent(scheduleId)}/export${buildQueryString({ runId: input.runId })}`,
      );
      return response.pack;
    },
    run: async (scheduleId: string) => {
      const response = await request<{ run: EvidenceScheduleRunEntry }>(
        "POST",
        `/api/v1/evidence/schedules/${encodeURIComponent(scheduleId)}/run`,
      );
      return response.run;
    },
    runDue: (input: { now?: string; limit?: number } = {}) =>
      request<EvidenceScheduleRunResultEntry>("POST", "/api/v1/evidence/schedules/run-due", input),
    runPendingAlertDeliveries: (input: { scheduleId?: string; now?: string; limit?: number } = {}) =>
      request<ProductionEvidenceAlertDeliveryRetryResultEntry>("POST", "/api/v1/evidence/schedules/run-pending-alert-deliveries", input),
  },

  connectors: {
    listCatalog: (filters: ConnectorCatalogFilters = {}) =>
      request<{ connectors: ConnectorCatalogEntry[] }>("GET", `/api/v1/connectors/catalog${buildQueryString({
        query: filters.query,
        category: filters.category,
      })}`),
    list: () => request<{ connections: ConnectorConnectionEntry[] }>("GET", "/api/v1/connectors"),
    readiness: (connectionId: string) =>
      request<ConnectorConnectionReadinessResultEntry>(
        "GET",
        `/api/v1/connectors/${encodeURIComponent(connectionId)}/readiness`,
      ),
    create: (input: CreateConnectorConnectionInputEntry) =>
      request<CreateConnectorConnectionResultEntry>("POST", "/api/v1/connectors", {
        connectorId: input.connectorId,
        ...(input.displayName ? { displayName: input.displayName } : {}),
        ...(input.credentials ? { credentials: input.credentials } : {}),
        ...(input.redirectUri ? { redirectUri: input.redirectUri } : {}),
        ...(input.metadata ? { metadata: input.metadata } : {}),
      }),
    authorize: (connectionId: string, redirectUri?: string) =>
      request<CreateConnectorConnectionResultEntry>(
        "POST",
        `/api/v1/connectors/${encodeURIComponent(connectionId)}/authorize`,
        redirectUri ? { redirectUri } : {},
      ),
    sync: (connectionId: string) =>
      request<{ sync: ConnectorSyncResultEntry }>("POST", `/api/v1/connectors/${encodeURIComponent(connectionId)}/sync`),
    execute: (
      connectionId: string,
      actionId: string,
      input: Record<string, unknown> = {},
      options: { simulationProfileId?: string } = {},
    ) =>
      request<{ result: ConnectorActionResultEntry }>(
        "POST",
        `/api/v1/connectors/${encodeURIComponent(connectionId)}/actions/${encodeURIComponent(actionId)}`,
        {
          input,
          ...(options.simulationProfileId ? { simulationProfileId: options.simulationProfileId } : {}),
        },
      ),
    delete: (connectionId: string) =>
      request<void>("DELETE", `/api/v1/connectors/${encodeURIComponent(connectionId)}`),
    getPolicy: () =>
      request<{ policy: ConnectorPolicyConfigEntry }>("GET", "/api/v1/settings/connectors/policy"),
    upsertPolicyRule: (input: ConnectorPolicyRuleInputEntry) =>
      request<{ rule: ConnectorPolicyRuleEntry }>("POST", "/api/v1/settings/connectors/policy", {
        ...(input.ruleId ? { ruleId: input.ruleId } : {}),
        ...(input.role ? { role: input.role } : {}),
        effect: input.effect,
        actions: input.actions,
        resources: input.resources,
        ...(input.description ? { description: input.description } : {}),
      }),
    deletePolicyRule: (ruleId: string) =>
      request<void>("DELETE", `/api/v1/settings/connectors/policy/${encodeURIComponent(ruleId)}`),
    plugins: {
      list: (filters: ConnectorPluginListFiltersEntry = {}) =>
        request<{ plugins: ConnectorPluginEntry[] }>("GET", `/api/v1/connectors/plugins${buildQueryString({
          status: filters.status,
          adapter: filters.adapter,
        })}`),
      get: (pluginId: string, version: string) =>
        request<{ plugin: ConnectorPluginEntry }>(
          "GET",
          `/api/v1/connectors/plugins/${encodeURIComponent(pluginId)}/${encodeURIComponent(version)}`,
        ),
      register: (input: ConnectorPluginRegisterInputEntry) =>
        request<{ plugin: ConnectorPluginEntry }>("POST", "/api/v1/connectors/plugins", {
          definition: input.definition,
          ...(input.version ? { version: input.version } : {}),
          ...(input.notes ? { notes: input.notes } : {}),
        }),
      importOpenApi: (input: ConnectorPluginOpenApiImportInputEntry) =>
        request<ConnectorPluginImportResultEntry>("POST", "/api/v1/connectors/plugins/import/openapi", {
          document: input.document,
          ...(input.connectorId ? { connectorId: input.connectorId } : {}),
          ...(input.name ? { name: input.name } : {}),
          ...(input.version ? { version: input.version } : {}),
          ...(input.category ? { category: input.category } : {}),
          ...(input.authMethod ? { authMethod: input.authMethod } : {}),
          ...(input.baseUrl ? { baseUrl: input.baseUrl } : {}),
          ...(input.notes ? { notes: input.notes } : {}),
        }),
      certify: (pluginId: string, version: string) =>
        request<{ certification: ConnectorPluginCertificationReportEntry }>(
          "POST",
          `/api/v1/connectors/plugins/${encodeURIComponent(pluginId)}/${encodeURIComponent(version)}/certify`,
          {},
        ),
      evidencePack: (pluginId: string, version: string) =>
        request<{ evidencePack: ConnectorPluginEvidencePackEntry }>(
          "GET",
          `/api/v1/connectors/plugins/${encodeURIComponent(pluginId)}/${encodeURIComponent(version)}/evidence-pack`,
        ),
      approve: (pluginId: string, version: string) =>
        request<{ plugin: ConnectorPluginEntry }>(
          "POST",
          `/api/v1/connectors/plugins/${encodeURIComponent(pluginId)}/${encodeURIComponent(version)}/approve`,
          {},
        ),
      suspend: (pluginId: string, version: string, input: ConnectorPluginTransitionInputEntry = {}) =>
        request<{ plugin: ConnectorPluginEntry }>(
          "POST",
          `/api/v1/connectors/plugins/${encodeURIComponent(pluginId)}/${encodeURIComponent(version)}/suspend`,
          input.reason ? { reason: input.reason } : {},
        ),
      revoke: (pluginId: string, version: string, input: ConnectorPluginTransitionInputEntry = {}) =>
        request<{ plugin: ConnectorPluginEntry }>(
          "POST",
          `/api/v1/connectors/plugins/${encodeURIComponent(pluginId)}/${encodeURIComponent(version)}/revoke`,
          input.reason ? { reason: input.reason } : {},
        ),
      delete: (pluginId: string, version: string) =>
        request<{ removed: boolean }>(
          "DELETE",
          `/api/v1/connectors/plugins/${encodeURIComponent(pluginId)}/${encodeURIComponent(version)}`,
        ),
    },
  },

  simulations: {
    listProfiles: () => request<{ profiles: SimulationProfileEntry[] }>("GET", "/api/v1/simulations/profiles"),
    getProfile: (profileId: string) =>
      request<{ profile: SimulationProfileEntry }>(
        "GET",
        `/api/v1/simulations/profiles/${encodeURIComponent(profileId)}`,
      ),
    saveProfile: (input: SimulationProfileInputEntry) =>
      request<{ profile: SimulationProfileEntry }>("POST", "/api/v1/simulations/profiles", input),
    deleteProfile: (profileId: string) =>
      request<void>("DELETE", `/api/v1/simulations/profiles/${encodeURIComponent(profileId)}`),
    listSuites: () => request<{ suites: SimulationSuiteEntry[] }>("GET", "/api/v1/simulations/suites"),
    getSuite: (suiteId: string) =>
      request<{ suite: SimulationSuiteEntry }>(
        "GET",
        `/api/v1/simulations/suites/${encodeURIComponent(suiteId)}`,
      ),
    saveSuite: (input: SimulationSuiteInputEntry) =>
      request<{ suite: SimulationSuiteEntry }>("POST", "/api/v1/simulations/suites", input),
    deleteSuite: (suiteId: string) =>
      request<void>("DELETE", `/api/v1/simulations/suites/${encodeURIComponent(suiteId)}`),
    listRuns: (filters: SimulationRunFiltersEntry = {}) => {
      const status = typeof filters.status === "string"
        ? filters.status
        : filters.status !== undefined
          ? filters.status.join(",")
          : undefined;
      return request<{ runs: SimulationRunEntry[] }>("GET", `/api/v1/simulations/runs${buildQueryString({
        agentId: filters.agentId,
        suiteId: filters.suiteId,
        status,
        limit: filters.limit,
      })}`);
    },
    getRun: (runId: string) =>
      request<{ run: SimulationRunEntry }>("GET", `/api/v1/simulations/runs/${encodeURIComponent(runId)}`),
    saveRun: (input: SimulationRunInputEntry) =>
      request<{ run: SimulationRunEntry }>("POST", "/api/v1/simulations/runs", input),
    updateRun: (runId: string, input: SimulationRunUpdateEntry) =>
      request<{ run: SimulationRunEntry }>("PATCH", `/api/v1/simulations/runs/${encodeURIComponent(runId)}`, input),
    preflight: (input: SimulationPreflightInputEntry) =>
      request<SimulationPreflightResultEntry>("POST", "/api/v1/simulations/preflight", input),
    resolveFault: (input: SimulationFaultResolutionInputEntry) =>
      request<{ fault: SimulationFaultResolutionEntry | null }>("POST", "/api/v1/simulations/faults/resolve", {
        simulationProfileId: input.simulationProfileId,
        ...(input.target ? { target: input.target } : {}),
        toolId: input.toolId,
        input: input.input ?? {},
      }),
  },

  packs: {
    list: () => request<{ packs: IndustryPackListEntry[] }>("GET", "/api/v1/packs"),
    get: (packId: string) =>
      request<{ pack: IndustryPackDescriptorEntry }>(
        "GET",
        `/api/v1/packs/${encodeURIComponent(packId)}`,
      ),
    install: (
      packId: string,
      options: {
        grantedScopes?: ReadonlyArray<Record<string, unknown>>;
        packKind?: "industry" | "marketplace";
        version?: string;
      } = {},
    ) =>
      request<{ receipt: PackInstallReceiptEntry }>(
        "POST",
        `/api/v1/tenant/packs/${encodeURIComponent(packId)}/install`,
        {
          ...(options.grantedScopes ? { grantedScopes: options.grantedScopes } : {}),
          ...(options.packKind ? { packKind: options.packKind } : {}),
          ...(options.version ? { version: options.version } : {}),
        },
      ),
    preflight: (packId: string, options: PackConnectorPreflightInputEntry = {}) =>
      request<{ preflight: PackConnectorPreflightResultEntry }>(
        "POST",
        `/api/v1/tenant/packs/${encodeURIComponent(packId)}/preflight`,
        {
          ...(options.grantedScopes ? { grantedScopes: options.grantedScopes } : {}),
          ...(options.connectionBindings ? { connectionBindings: options.connectionBindings } : {}),
          ...(options.packKind ? { packKind: options.packKind } : {}),
          ...(options.version ? { version: options.version } : {}),
        },
      ),
    listInstalls: () =>
      request<{ installs: PackInstallReceiptEntry[] }>("GET", "/api/v1/tenant/packs/installs"),
    getInstall: (installId: string) =>
      request<{ receipt: PackInstallReceiptEntry }>(
        "GET",
        `/api/v1/tenant/packs/installs/${encodeURIComponent(installId)}`,
      ),
    getManifest: (packId: string) =>
      request<PackManifestLookupEntry>(
        "GET",
        `/api/v1/packs/${encodeURIComponent(packId)}/manifest`,
      ),
    authorize: (packId: string, scopes: ReadonlyArray<Record<string, unknown>>) =>
      request<{ decision: PackScopeAuthorizeDecisionEntry }>(
        "POST",
        `/api/v1/tenant/packs/${encodeURIComponent(packId)}/authorize`,
        { scopes },
      ),
    getRevocationList: () =>
      request<{ list: RevocationListEntry }>("GET", "/api/v1/marketplace/revocations"),
    listRevocations: () =>
      request<{ revocations: PackRevocationEntryEntry[] }>(
        "GET",
        "/api/v1/platform/pack-revocations",
      ),
  },

  marketplace: {
    list: (filters: { publisherId?: string; includeRevoked?: boolean } = {}) =>
      request<{ packs: MarketplacePackEntry[] }>(
        "GET",
        `/api/v1/marketplace/packs${buildQueryString({
          publisherId: filters.publisherId,
          includeRevoked: filters.includeRevoked,
        })}`,
      ),
    get: (packId: string, version: string) =>
      request<{ pack: MarketplacePackEntry }>(
        "GET",
        `/api/v1/marketplace/packs/${encodeURIComponent(packId)}/${encodeURIComponent(version)}`,
      ),
    publish: (
      signed: { manifest: Record<string, unknown>; signature: string },
      commercial?: MarketplaceCommercialTermsEntry,
    ) =>
      request<{ pack: MarketplacePackEntry }>(
        "POST",
        "/api/v1/marketplace/packs",
        commercial ? { signed, commercial } : { signed },
      ),
    yank: (packId: string, version: string) =>
      request<{ pack: MarketplacePackEntry }>(
        "POST",
        `/api/v1/marketplace/packs/${encodeURIComponent(packId)}/${encodeURIComponent(version)}/yank`,
      ),
    applyPublisher: (input: RegisterPackPublisherInputEntry) =>
      request<{ publisher: PackPublisherEntry }>(
        "POST",
        "/api/v1/marketplace/publishers/applications",
        input,
      ),
    revocations: () =>
      request<{ list: RevocationListEntry }>("GET", "/api/v1/marketplace/revocations"),
    registry: () =>
      request<{ registry: MarketplaceRegistrySnapshotEntry }>("GET", "/api/v1/marketplace/registry"),
  },

  tenantPolicy: {
    getPackInstallPolicy: () =>
      request<{ policy: TenantPackInstallPolicyEntry }>(
        "GET",
        "/api/v1/tenant/pack-install-policy",
      ),
    setPackInstallPolicy: (policy: TenantPackInstallPolicyInputEntry) =>
      request<{ policy: TenantPackInstallPolicyEntry }>(
        "PUT",
        "/api/v1/tenant/pack-install-policy",
        policy,
      ),
    authorizePack: (packId: string, scopes: readonly Record<string, unknown>[]) =>
      request<{ decision: PackScopeAuthorizeDecisionEntry }>(
        "POST",
        `/api/v1/tenant/packs/${encodeURIComponent(packId)}/authorize`,
        { scopes },
      ),
  },

  marketplaceAdmin: {
    listPackPublishers: (filter?: { status?: "pending" | "verified" | "suspended" }) =>
      request<{ publishers: PackPublisherEntry[] }>(
        "GET",
        `/api/v1/platform/pack-publishers${buildQueryString({ status: filter?.status })}`,
      ),
    getPackPublisher: (publisherId: string) =>
      request<{ publisher: PackPublisherEntry }>(
        "GET",
        `/api/v1/platform/pack-publishers/${encodeURIComponent(publisherId)}`,
      ),
    registerPackPublisher: (input: RegisterPackPublisherInputEntry) =>
      request<{ publisher: PackPublisherEntry }>(
        "POST",
        "/api/v1/platform/pack-publishers",
        input,
      ),
    approvePackPublisher: (publisherId: string) =>
      request<{ publisher: PackPublisherEntry }>(
        "POST",
        `/api/v1/platform/pack-publishers/${encodeURIComponent(publisherId)}/approve`,
      ),
    suspendPackPublisher: (publisherId: string, reason?: string) =>
      request<{ publisher: PackPublisherEntry }>(
        "POST",
        `/api/v1/platform/pack-publishers/${encodeURIComponent(publisherId)}/suspend`,
        reason ? { reason } : {},
      ),
    listRevocations: () =>
      request<{ revocations: PackRevocationEntryEntry[] }>(
        "GET",
        "/api/v1/platform/pack-revocations",
      ),
    revokePack: (packId: string, version: string, reason: string) =>
      request<{ revocation: PackRevocationEntryEntry }>(
        "POST",
        "/api/v1/platform/pack-revocations",
        { packId, version, reason },
      ),
    unrevokePack: (packId: string, version: string) =>
      request<{ removed: boolean }>(
        "DELETE",
        `/api/v1/platform/pack-revocations/${encodeURIComponent(packId)}/${encodeURIComponent(version)}`,
      ),
    reviewPack: (packId: string, version: string, input: MarketplaceReviewInputEntry) =>
      request<{ pack: MarketplacePackEntry }>(
        "POST",
        `/api/v1/platform/marketplace/packs/${encodeURIComponent(packId)}/${encodeURIComponent(version)}/review`,
        input,
      ),
    commercialOps: () =>
      request<{ commerce: MarketplaceCommercialOpsSnapshotEntry }>(
        "GET",
        "/api/v1/platform/marketplace/commercial-ops",
      ),
  },

  interop: {
    listMcpServices: () => request<{ services: McpServiceSummaryEntry[] }>("GET", "/api/v1/interop/mcp/services"),
    listMcpInvocationHistory: (filters: McpInvocationHistoryFilters = {}) =>
      request<{ entries: McpInvocationHistoryEntry[] }>("GET", `/api/v1/interop/mcp/history${buildQueryString({
        serviceId: filters.serviceId,
        status: filters.status,
        limit: filters.limit,
      })}`),
    getMcpInvocationHistorySummary: (filters: McpInvocationHistoryFilters = {}) =>
      request<{ summary: McpInvocationHistorySummaryEntry }>("GET", `/api/v1/interop/mcp/history/summary${buildQueryString({
        serviceId: filters.serviceId,
        status: filters.status,
        limit: filters.limit,
      })}`),
    connectMcpService: (input: ConnectMcpServiceInputEntry) =>
      request<{ service: McpServiceSummaryEntry }>("POST", "/api/v1/interop/mcp/services", {
        ...(input.serviceId ? { serviceId: input.serviceId } : {}),
        displayName: input.displayName,
        transport: input.transport,
        ...(input.endpoint ? { endpoint: input.endpoint } : {}),
        ...(input.command ? { command: input.command } : {}),
        ...(input.args && input.args.length > 0 ? { args: input.args } : {}),
        ...(input.secretBindings && input.secretBindings.length > 0 ? { secretBindings: input.secretBindings } : {}),
        ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
      }),
    setMcpServiceStatus: (serviceId: string, status: McpManagedServiceStatusEntry) =>
      request<{ service: McpServiceSummaryEntry }>("POST", `/api/v1/interop/mcp/services/${encodeURIComponent(serviceId)}/status`, {
        status,
      }),
    deleteMcpService: (serviceId: string) =>
      request<void>("DELETE", `/api/v1/interop/mcp/services/${encodeURIComponent(serviceId)}`),
    invokeMcpTool: (serviceId: string, toolName: string, input: McpToolInvocationInputEntry = {}) =>
      request<McpToolInvocationApprovalRequiredEntry | { result: McpToolInvocationResultEntry }>(
        "POST",
        `/api/v1/interop/mcp/services/${encodeURIComponent(serviceId)}/tools/${encodeURIComponent(toolName)}`,
        {
          ...(input.arguments !== undefined ? { arguments: input.arguments } : {}),
          ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
        },
      ).then((response) => ("result" in response ? response.result : response)),
    getOutboundCapabilities: () =>
      request<McpOutboundCapabilitiesEntry>("GET", "/api/v1/interop/mcp/outbound/capabilities"),
    invokeOutboundTool: (toolName: string, input: Record<string, unknown> = {}) =>
      request<McpOutboundInvocationResultEntry>(
        "POST",
        `/api/v1/interop/mcp/outbound/tools/${encodeURIComponent(toolName)}/invoke`,
        input,
      ),
  },

  sessions: {
    listMemory: (filters: SessionMemoryListFilters = {}) =>
      request<{ entries: SessionMemoryEntryEntry[] }>("GET", `/api/v1/sessions/memory${buildQueryString({
        scope: filters.scope,
        scopeId: filters.scopeId,
        limit: filters.limit,
      })}`),
    getMemoryEntry: (scope: SessionMemoryScopeEntry, scopeId: string, key: string) =>
      request<{ entry: SessionMemoryEntryEntry }>(
        "GET",
        `/api/v1/sessions/memory/${encodeURIComponent(scope)}/${encodeURIComponent(scopeId)}/${encodeURIComponent(key)}`,
      ),
    upsertMemoryEntry: (input: SessionMemoryUpsertInputEntry) =>
      request<{ entry: SessionMemoryEntryEntry }>(
        "PUT",
        `/api/v1/sessions/memory/${encodeURIComponent(input.scope)}/${encodeURIComponent(input.scopeId)}/${encodeURIComponent(input.key)}`,
        {
          value: input.value,
          ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
        },
      ),
    deleteMemoryEntry: (scope: SessionMemoryScopeEntry, scopeId: string, key: string) =>
      request<void>(
        "DELETE",
        `/api/v1/sessions/memory/${encodeURIComponent(scope)}/${encodeURIComponent(scopeId)}/${encodeURIComponent(key)}`,
      ),
    hydrateThread: (threadId: string) =>
      request<SessionMemorySnapshotEntry>("GET", `/api/v1/sessions/threads/${encodeURIComponent(threadId)}/memory`),
    upsertThreadMemoryEntry: (threadId: string, input: SessionMemoryThreadUpsertInputEntry) =>
      request<{ entry: SessionMemoryEntryEntry }>(
        "POST",
        `/api/v1/sessions/threads/${encodeURIComponent(threadId)}/memory`,
        {
          key: input.key,
          value: input.value,
          ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
        },
      ),
  },

  debugger: {
    listSnapshots: (filters: DebuggerSnapshotListFilters = {}) =>
      request<{ snapshots: DebuggerSnapshotEntry[] }>("GET", `/api/v1/debugger/snapshots${buildQueryString({
        threadId: filters.threadId,
        taskId: filters.taskId,
        agentId: filters.agentId,
        kinds: filters.kinds?.join(","),
        limit: filters.limit,
      })}`),
    getSnapshot: (snapshotId: string) =>
      request<{ snapshot: DebuggerSnapshotEntry }>("GET", `/api/v1/debugger/snapshots/${encodeURIComponent(snapshotId)}`),
    captureSnapshot: (input: DebuggerCaptureSnapshotInputEntry) =>
      request<{ snapshot: DebuggerSnapshotEntry }>("POST", "/api/v1/debugger/snapshots", input),
    deleteSnapshot: (snapshotId: string) =>
      request<void>("DELETE", `/api/v1/debugger/snapshots/${encodeURIComponent(snapshotId)}`),
    listBreakpoints: (filters: DebuggerBreakpointListFilters = {}) =>
      request<{ breakpoints: DebuggerBreakpointEntry[] }>("GET", `/api/v1/debugger/breakpoints${buildQueryString({
        agentId: filters.agentId,
        threadId: filters.threadId,
        enabled: filters.enabled,
        limit: filters.limit,
      })}`),
    upsertBreakpoint: (input: DebuggerBreakpointUpsertInputEntry) =>
      request<{ breakpoint: DebuggerBreakpointEntry }>("PUT", "/api/v1/debugger/breakpoints", input),
    deleteBreakpoint: (breakpointId: string) =>
      request<void>("DELETE", `/api/v1/debugger/breakpoints/${encodeURIComponent(breakpointId)}`),
  },

  observability: {
    listTraces: (filters: ObservabilityTraceListFilters = {}) =>
      request<{ traces: ObservabilityTraceSpanEntry[] }>(
        "GET",
        `/api/v1/observability/traces${buildQueryString({
          query: filters.query,
          spanId: filters.spanId,
          taskId: filters.taskId,
          agentId: filters.agentId,
          userId: filters.userId,
          sessionId: filters.sessionId,
          operationName: filters.operationName,
          attributeKey: filters.attributeKey,
          attributeValue: filters.attributeValue,
          status: filters.status,
          since: filters.since,
          until: filters.until,
          minDurationMs: filters.minDurationMs,
          limit: filters.limit,
        })}`,
      ),
    listSavedTraceFilters: () =>
      request<{ filters: ObservabilitySavedTraceFilterEntry[] }>(
        "GET",
        "/api/v1/observability/traces/saved-filters",
      ),
    saveTraceFilter: (input: ObservabilitySavedTraceFilterInputEntry) =>
      request<{ filter: ObservabilitySavedTraceFilterEntry }>(
        "POST",
        "/api/v1/observability/traces/saved-filters",
        input,
      ),
    deleteTraceFilter: (filterId: string) =>
      request<void>("DELETE", `/api/v1/observability/traces/saved-filters/${encodeURIComponent(filterId)}`),
    compareTraces: (leftTraceId: string, rightTraceId: string) =>
      request<{ comparison: ObservabilityTraceDiffEntry }>(
        "GET",
        `/api/v1/observability/traces/compare${buildQueryString({ leftTraceId, rightTraceId })}`,
      ),
    getGovernancePolicy: () =>
      request<{ policy: ObservabilityTraceGovernancePolicyEntry }>("GET", "/api/v1/observability/governance-policy"),
    updateGovernancePolicy: (input: ObservabilityTraceGovernancePolicyInputEntry) =>
      request<{ policy: ObservabilityTraceGovernancePolicyEntry }>(
        "PUT",
        "/api/v1/observability/governance-policy",
        input,
      ),
    getTrace: (taskId: string) =>
      request<{ trace: ObservabilityTraceEnvelopeEntry }>(
        "GET",
        `/api/v1/observability/tasks/${encodeURIComponent(taskId)}/trace`,
      ),
    getTraceById: (traceId: string) =>
      request<{ trace: ObservabilityTraceEnvelopeEntry }>(
        "GET",
        `/api/v1/observability/traces/${encodeURIComponent(traceId)}`,
      ),
    annotateEvalFailure: (traceId: string, spanId: string, input: ObservabilityTraceAnnotationInputEntry) =>
      request<ObservabilityTraceAnnotationResultEntry>(
        "POST",
        `/api/v1/observability/traces/${encodeURIComponent(traceId)}/spans/${encodeURIComponent(spanId)}/annotations`,
        input,
      ),
    createIncidentHandoff: (traceId: string, input: ObservabilityIncidentHandoffInputEntry) =>
      request<{ incident: ObservabilityIncidentHandoffEntry }>(
        "POST",
        `/api/v1/observability/traces/${encodeURIComponent(traceId)}/incidents`,
        input,
      ),
    listExecutionEvents: (taskId: string, filters: ObservabilityExecutionEventFilters = {}) =>
      request<{ events: ObservabilityExecutionEventEntry[] }>(
        "GET",
        `/api/v1/observability/tasks/${encodeURIComponent(taskId)}/events${buildQueryString({
          type: filters.type,
          status: filters.status,
          after: filters.after,
          limit: filters.limit,
        })}`,
      ),
  },

  execution: {
    runSandboxedCode: (input: SandboxedExecutionInputEntry) =>
      request<{ execution: SandboxedExecutionResultEntry }>("POST", "/api/v1/execution/sandbox", {
        language: input.language,
        code: input.code,
        ...(input.timeoutMs !== undefined ? { timeoutMs: input.timeoutMs } : {}),
      }).then((response) => response.execution),
    runCommand: (input: PrivilegedCommandExecutionInputEntry) =>
      request<PrivilegedExecutionSubmissionEntry>("POST", "/api/v1/execution/command", {
        command: input.command,
        ...(input.workingDirectory !== undefined ? { workingDirectory: input.workingDirectory } : {}),
        ...(input.timeoutMs !== undefined ? { timeoutMs: input.timeoutMs } : {}),
        ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
      }),
    runPowerShell: (input: PrivilegedPowerShellExecutionInputEntry) =>
      request<PrivilegedExecutionSubmissionEntry>("POST", "/api/v1/execution/powershell", {
        script: input.script,
        ...(input.workingDirectory !== undefined ? { workingDirectory: input.workingDirectory } : {}),
        ...(input.timeoutMs !== undefined ? { timeoutMs: input.timeoutMs } : {}),
        ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
      }),
    runFilesystem: (input: PrivilegedFilesystemExecutionInputEntry) =>
      request<PrivilegedExecutionSubmissionEntry>("POST", "/api/v1/execution/filesystem", {
        operation: input.operation,
        path: input.path,
        ...(input.encoding !== undefined ? { encoding: input.encoding } : {}),
        ...(input.content !== undefined ? { content: input.content } : {}),
        ...(input.overwrite !== undefined ? { overwrite: input.overwrite } : {}),
        ...(input.recursive !== undefined ? { recursive: input.recursive } : {}),
        ...(input.createParents !== undefined ? { createParents: input.createParents } : {}),
        ...(input.timeoutMs !== undefined ? { timeoutMs: input.timeoutMs } : {}),
        ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
      }),
  },

  files: {
    upload: async (file: File): Promise<{ files: UploadedFileEntry[] }> => {
      const formData = new FormData();
      formData.append("file", file);
      const path = "/api/v1/files";
      const headers = buildCredentialHeaders({ method: "POST", path });

      let res: Response;
      try {
        res = await fetchWithTimeout(`${BASE}${path}`, { method: "POST", headers, body: formData, credentials: "include" });
      } catch (error) {
        throw normalizeRequestError(error);
      }

      const data = await readResponseBody(res);
      if (!res.ok) throw buildApiRequestError(res, data);
      return data as { files: UploadedFileEntry[] };
    },
    list: () => request<{ files: UploadedFileEntry[] }>("GET", "/api/v1/files"),
    delete: (id: string) => request<void>("DELETE", `/api/v1/files/${id}`),
  },

  triggers: {
    create: (input: AutomationTriggerCreateInputEntry) =>
      request<{ trigger: AutomationTriggerEntry }>("POST", "/api/v1/triggers", input),
    replace: (triggerId: string, input: AutomationTriggerCreateInputEntry) =>
      request<{ trigger: AutomationTriggerEntry }>("PUT", `/api/v1/triggers/${encodeURIComponent(triggerId)}`, input),
    get: (triggerId: string) =>
      request<{ trigger: AutomationTriggerEntry }>("GET", `/api/v1/triggers/${encodeURIComponent(triggerId)}`),
    setEnabled: (triggerId: string, enabled: boolean) =>
      request<{ trigger: AutomationTriggerEntry }>(
        "PATCH",
        `/api/v1/triggers/${encodeURIComponent(triggerId)}`,
        { enabled },
      ),
    listEvents: (triggerId: string, input: { limit?: number } = {}) =>
      request<{ events: AutomationTriggerEventEntry[] }>(
        "GET",
        `/api/v1/triggers/${encodeURIComponent(triggerId)}/events${buildQueryString({
          ...(input.limit !== undefined ? { limit: input.limit } : {}),
        })}`,
      ),
    testFire: (triggerId: string, input: AutomationTriggerTestFireInputEntry = {}) =>
      request<{ events: AutomationTriggerEventEntry[] }>(
        "POST",
        `/api/v1/triggers/${encodeURIComponent(triggerId)}/test-fire`,
        {
          ...(input.method !== undefined ? { method: input.method } : {}),
          ...(input.metricValue !== undefined ? { metricValue: input.metricValue } : {}),
          ...(input.connectionId !== undefined ? { connectionId: input.connectionId } : {}),
          ...(input.eventId !== undefined ? { eventId: input.eventId } : {}),
          payload: input.payload ?? {},
        },
      ),
    dryRun: (triggerId: string, input: AutomationTriggerTestFireInputEntry = {}) =>
      request<{ preview: AutomationTriggerDryRunPreviewEntry }>(
        "POST",
        `/api/v1/triggers/${encodeURIComponent(triggerId)}/dry-run`,
        {
          ...(input.method !== undefined ? { method: input.method } : {}),
          ...(input.metricValue !== undefined ? { metricValue: input.metricValue } : {}),
          ...(input.connectionId !== undefined ? { connectionId: input.connectionId } : {}),
          ...(input.eventId !== undefined ? { eventId: input.eventId } : {}),
          payload: input.payload ?? {},
        },
      ),
    getEventCorrelation: (triggerId: string, eventId: string, input: { limit?: number } = {}) =>
      request<AutomationTriggerEventCorrelationEntry>(
        "GET",
        `/api/v1/triggers/${encodeURIComponent(triggerId)}/events/${encodeURIComponent(eventId)}/correlation${buildQueryString({
          ...(input.limit !== undefined ? { limit: input.limit } : {}),
        })}`,
      ),
  },

  builder: {
    listTemplates: () =>
      request<{ templates: BuilderTemplateEntry[] }>("GET", "/api/v1/builder/templates"),
    listWorkflows: () =>
      request<{ workflows: BuilderWorkflowEntry[] }>("GET", "/api/v1/builder/workflows"),
    getWorkflow: (id: string) =>
      request<{ workflow: BuilderWorkflowEntry }>("GET", `/api/v1/builder/workflows/${encodeURIComponent(id)}`),
    saveWorkflow: (input: SaveBuilderWorkflowInputEntry) =>
      request<{ workflow: BuilderWorkflowEntry }>("POST", "/api/v1/builder/workflows", input),
    deleteWorkflow: (id: string) =>
      request<void>("DELETE", `/api/v1/builder/workflows/${encodeURIComponent(id)}`),
    listVersions: (workflowId: string) =>
      request<{ versions: BuilderWorkflowVersionEntry[] }>(
        "GET",
        `/api/v1/builder/workflows/${encodeURIComponent(workflowId)}/versions`,
      ),
    appendVersion: (workflowId: string, input: AppendBuilderVersionInputEntry) =>
      request<{ version: BuilderWorkflowVersionEntry }>(
        "POST",
        `/api/v1/builder/workflows/${encodeURIComponent(workflowId)}/versions`,
        input,
      ),
    listComments: (workflowId: string) =>
      request<{ comments: BuilderCommentEntry[] }>(
        "GET",
        `/api/v1/builder/workflows/${encodeURIComponent(workflowId)}/comments`,
      ),
    addComment: (workflowId: string, input: AddBuilderCommentInputEntry) =>
      request<{ comment: BuilderCommentEntry }>(
        "POST",
        `/api/v1/builder/workflows/${encodeURIComponent(workflowId)}/comments`,
        input,
      ),
    deleteComment: (workflowId: string, commentId: string) =>
      request<void>(
        "DELETE",
        `/api/v1/builder/workflows/${encodeURIComponent(workflowId)}/comments/${encodeURIComponent(commentId)}`,
      ),
    aiGenerate: (prompt: string) =>
      request<BuilderAiGenerateEntry>(
        "POST",
        "/api/v1/builder/ai/generate",
        { prompt },
      ),
    aiSuggest: (kinds: string[]) =>
      request<{ suggestions: BuilderAiSuggestionEntry[] }>(
        "POST",
        "/api/v1/builder/ai/suggest",
        { kinds },
      ),
    aiExplain: (kinds: string[]) =>
      request<{ explanation: string }>(
        "POST",
        "/api/v1/builder/ai/explain",
        { kinds },
      ),
    aiFix: (kinds: string[], issues?: string[]) =>
      request<{ fixes: BuilderAiFixEntry[] }>(
        "POST",
        "/api/v1/builder/ai/fix",
        issues ? { kinds, issues } : { kinds },
      ),
    assets: {
      list: () =>
        request<{ assets: BuilderAssetEntry[] }>("GET", "/api/v1/builder/assets"),
      upsert: (input: UpsertBuilderAssetInputEntry) =>
        request<{ asset: BuilderAssetEntry }>("POST", "/api/v1/builder/assets", input),
      delete: (id: string) =>
        request<void>("DELETE", `/api/v1/builder/assets/${encodeURIComponent(id)}`),
    },
    subflows: {
      list: () =>
        request<{ subflows: BuilderSubflowEntry[] }>("GET", "/api/v1/builder/subflows"),
      upsert: (input: UpsertBuilderSubflowInputEntry) =>
        request<{ subflow: BuilderSubflowEntry }>("POST", "/api/v1/builder/subflows", input),
      delete: (id: string) =>
        request<void>("DELETE", `/api/v1/builder/subflows/${encodeURIComponent(id)}`),
    },
  },
};
